/**
 * English copy, the source catalogue: `fr.ts` must match its shape key for key (checked by
 * `tsc` and by `catalogues.test.ts`). i18next syntax: `{{name}}` interpolation, `_one`/`_other`
 * plural keys resolved by `Intl.PluralRules` for `count`.
 */
export const en = {
  sourceViewer: {
    description: "Shown in ESI from the stored file. Download keeps the original.",
    loading: "Opening the file…",
    content: "File content",
    tooLargeTitle: "Too large to show here",
    tooLarge: "This file is over 2 MB. Download it to read it.",
    notOpenableTitle: "This file can't be shown here",
    notOpenable: "Download it to read it.",
    refused: "You can no longer open this file.",
    failed: "The file could not be opened: {{reason}}",
    download: "Download",
    close: "Close",
  },
  attachments: {
    readOnly: "You can read the files shared with you. Only the creator can manage them.",
    addToMessage: "Add to message",
    filesEmpty: "No files in this conversation yet.",
    emptyConversation: "Ask a question to begin.",
    actionFailed: "Could not complete this action. Refresh the files and try again.",
    floorProject: "People who can read this conversation may read this file.",
    floorEntity: "Conversation readers must also belong to the uploader’s entity or a sub-entity.",
    floorNamed: "Only the uploader and the people they name may read this file.",
    attach: "Attach files",
    title: "Conversation files",
    scope: "Available in this conversation only. Sharing still follows each file’s classification.",
    choose: "Choose files",
    confirm: "Attach",
    cancel: "Cancel",
    remove: "Remove from message: {{name}}",
    cancelUpload: "Cancel upload: {{name}}",
    delete: "Delete attachment",
    deleteTitle: "Delete attachment?",
    deleteBody:
      "The agent loses access to this file. Old citations will show Source deleted. Earlier message labels remain.",
    notSent: "Not sent",
    sent: "Available in this conversation",
    creationPending:
      "Files are kept here until the conversation opens. Retry, or remove them to send a question without files.",
    checking: "Checking",
    selected: "Selected",
    toReview: "To review",
    reviewHint: "Choose who can see these files before they upload.",
    review: "Review",
    reviewLead_one: "Review {{count}} new file before it uploads",
    reviewLead_other: "Review {{count}} new files before they upload",
    opening: "Opening the conversation…",
    removeToSend: "Remove the files to send your question alone.",
    reasonUnavailable: "Attachments are unavailable here.",
    listFailed: "Could not load the files.",
    uploading: "Uploading",
    processing: "Processing",
    ready: "Ready",
    paused: "Paused by usage limit",
    failed: "Could not upload the file. Check the connection or choose it again.",
    processFailed: "Could not process",
    refused: "This file could not be attached. Refresh the files and try again.",
    unavailable: "Attachment unavailable",
    metadata:
      "Attachments are unavailable. Refresh or update the app; remove selected files to send text only.",
    question: "Add a question to send these files.",
    blocked: "Wait until every selected file is ready, or remove it from the message.",
    limit: "Select at most 10 files for one message.",
    retry: "Retry",
    retryCheck: "Retry check",
    reselect: "Remove and choose this file again; this upload cannot resume.",
    open: "Open {{name}}",
    download: "Download {{name}}",
    marker: "Attachment",
    path: "This conversation",
    checkingSend: "Checking whether your message was saved. Your draft is kept.",
    checkFailed: "Could not check whether the message was saved. Retry the check before sending.",
    checkFailedBefore:
      "Could not confirm that the selected files can still be sent, so nothing was sent. Retry the check before sending.",
    archived: "This conversation is archived. Files are read-only.",
    project_frozen: "This project is frozen. Files are read-only.",
    attachments_disabled: "New attachments are turned off for this conversation.",
    standalone_disabled: "Standalone conversations are turned off. Files are read-only.",
    private_level_unavailable:
      "No private classification level exists for your organisation. Ask an admin. Files are read-only.",
    done: "Done",
    preparing: "Preparing",
    fromOutside: "From outside the company",
    originLocked: "Set once the file was used in the conversation.",
    empty: "Choose files, or select an uploaded file that has not been sent.",
    count_one: "{{count}} file",
    count_other: "{{count}} files",
    overLimit: "{{count}} of {{max}}",
    more: "+{{count}}",
    moreLabel_one: "Show {{count}} more file",
    moreLabel_other: "Show {{count}} more files",
    showAll: "Show all",
    collapse: "Collapse",
    manage: "Manage",
    details: "Details",
    detailsFor: "Details: {{name}}",
    showing_one: "Showing {{count}} file",
    showing_other: "Showing {{count}} files",
    size: "Size",
    type: "Type",
    status: "Status",
    kind: {
      document: "Document",
      spreadsheet: "Spreadsheet",
      presentation: "Presentation",
      image: "Image",
      text: "Text",
      file: "File",
    },
    max: "Up to {{mb}} MB per file; images up to {{imageMb}} MB.",
  },
  taskProposal: {
    createTitle: "Create a task",
    updateTitle: "Change a task: {{title}}",
    fields: {
      title: "Title",
      assignee: "Assignee",
      due: "Due",
      description: "Description",
      status: "Status",
      comment: "Comment",
    },
    cleared: "Cleared",
    fromSources_one: "Drawn from {{count}} source of this answer",
    fromSources_other: "Drawn from {{count}} sources of this answer",
    create: "Create",
    apply: "Apply",
    applying: "Saving…",
    edit: "Edit",
    cancel: "Cancel",
    dismiss: "Dismiss",
    created: "Task created.",
    applied: "Task changed.",
    dismissed: "Dismissed.",
    expired: "This proposal expired.",
    open: "Open task",
    refused: "You can no longer do this in this project.",
    gone: "This task is no longer available to you.",
    decided: "This proposal was already decided.",
    invalidAssignee: "This person cannot be assigned tasks in this project.",
  },
  tasks: {
    applyStatus: "Apply status",
    rowStatus: "Status of {{title}}",
    rowStatusRestricted: "Status of a restricted task",
    rowLeft: "“{{title}}” moved to {{status}}.",
    rowLeftRestricted: "A restricted task moved to {{status}}.",
    rowBusy: "That task is still saving. Try Undo again in a moment.",
    rowUndo: "Undo",
    rowUncertain:
      "The status may not have been saved. The list was refreshed; check the task before trying again.",
    selectPersonPrompt: "Choose a person to apply this filter. The previous filter remains active.",
    levelPublic: "Public",
    levelInternal: "Internal",
    levelConfidential: "Confidential",
    myTitle: "My tasks",
    title: "Tasks",
    assigned: "Assigned to me",
    created: "Created by me",
    status: "Status",
    open: "Open",
    all: "All",
    todo: "To do",
    in_progress: "In progress",
    blocked: "Blocked",
    done: "Done",
    assignee: "Assignee",
    allPeople: "All assignees",
    unassigned: "Unassigned",
    person: "Choose a person",
    unavailablePerson: "Person unavailable",
    searchPeople: "Search by name or email",
    peopleHint: "Enter at least two characters.",
    peopleNone: "No eligible people found.",
    lookupUnavailable: "Person search is temporarily unavailable. The saved assignment is kept.",
    search: "Search task titles",
    clearFilters: "Clear filters",
    newTask: "New task",
    edit: "Edit task",
    taskTitle: "Title",
    description: "Description",
    due: "Due date",
    noDue: "No due date",
    clearDate: "Clear date",
    overdue: "Overdue",
    restricted: "Restricted task",
    unavailable: "Task unavailable",
    unavailableBody: "This task is unavailable. Return to the project tasks.",
    empty: "No open tasks",
    emptyBody: "Your project follow-ups will appear here.",
    noMatches: "No matching tasks",
    noMatchesBody: "Try changing or clearing the filters.",
    projects: "Explore projects",
    more: "Load more",
    restart: "Restart list",
    cursorError: "This page could not be loaded. Restart the list to get current results.",
    unsupportedOpen: "The Open filter is temporarily unavailable. Retry or choose All explicitly.",
    stale: "The refresh failed. Previously loaded information may be out of date.",
    metadata: "Actions are temporarily unavailable. Refresh to check your permissions.",
    refresh: "Refresh",
    disabled: "Tasks are disabled for this project. Existing tasks remain readable.",
    frozen: "This project is frozen. Tasks are read-only.",
    sources: "Sources",
    addSource: "Add reference",
    removeSource: "Remove reference",
    noSources: "No references yet.",
    unavailableSource: "Reference unavailable",
    document: "File",
    conversation: "Conversation",
    message: "Message",
    artefact: "Artefact",
    files: "Files",
    conversations: "Conversations",
    chooseConversation: "Choose a conversation to see its messages and artefacts.",
    backSources: "Back to conversations",
    linkLimit: "A task can have at most 20 references. Remove one before adding another.",
    comments: "Comments",
    comment: "Add comment",
    commentText: "Comment",
    noComments: "No comments yet.",
    deleteComment: "Delete comment",
    confirmComment: "Delete this comment permanently?",
    delete: "Delete task",
    deleteBody:
      "This permanently removes the task, its comments and its references. Source files, conversations, messages and artefacts remain.",
    creator: "Created by {{name}}",
    createdAt: "Created {{time}}",
    updatedAt: "Updated {{time}}",
    completedAt: "Completed {{time}}",
    classification: "Classification: {{label}}",
    titleInvalid: "Enter 1–200 characters without control characters.",
    descriptionInvalid: "Use at most 4,000 characters without control characters.",
    commentInvalid: "Enter 1–2,000 characters without control characters.",
    dateInvalid: "Choose a valid calendar date.",
    unchanged: "No changes to save.",
    uncertain:
      "Saving could not be confirmed. The latest data has been refreshed; check it before trying again.",
    discard: "Discard changes?",
    discardBody: "Your unsaved changes will be lost.",
    discardAction: "Discard",
    nothing: "No readable items found.",
    removeCommentBody: "This comment will be permanently removed.",
    lookupResults: "Eligible people",
    backTasks: "Back to tasks",
    startConversation: "Work on this with ESI",
    starting: "Starting…",
    startUncertain:
      "The conversation could not be confirmed. Check the project's conversations before trying again.",
  },
  rootError: {
    title: "Something went wrong",
    body: "ESI hit an unexpected error and couldn't show this page. Reloading usually fixes it.",
    reload: "Reload",
    home: "Back to home",
  },
  app: {
    name: "ESI",
    unreachable: "Can't connect right now",
    loading: "Loading…",
    skipToContent: "Skip to main content",
    retry: "Try again",
    cancel: "Cancel",
    close: "Close",
    done: "Done",
    save: "Save",
    saving: "Saving…",
    remove: "Remove",
    newTab: "(opens in a new tab)",
    you: "You",
    more: "More",
  },
  notifications: {
    bell: "Notifications",
    bellUnread_one: "Notifications, {{count}} unread",
    bellUnread_other: "Notifications, {{count}} unread",
    title: "Notifications",
    description: "What happened in your projects’ wikis and tasks, and to your feedback.",
    empty: "You’re up to date",
    markAll: "Mark all read",
    unread: "Unread",
    someone: "Someone",
    changeSet: "a change set",
    page: "a page",
    task: "a task",
    taskStart: "A task",
    quoted: "“{{title}}”",
    feedbackFrom: "ESI team",
    feedback: {
      bug: {
        triaged: "Your bug report was reviewed",
        planned: "Your bug report is planned",
        fixed: "Your bug report is fixed",
        fixedIn: "Your bug report is fixed in version {{version}}",
        not_planned: "Your bug report is not planned",
        duplicate: "Your bug report was merged with a similar report",
      },
      idea: {
        triaged: "Your idea was reviewed",
        planned: "Your idea is planned",
        fixed: "Your idea is available",
        fixedIn: "Your idea is available in version {{version}}",
        not_planned: "Your idea is not planned",
        duplicate: "Your idea was merged with a similar one",
      },
    },
    kind: {
      change_set_submitted: "{{actor}} submitted {{subject}} for review",
      change_request: "{{actor}} requested changes on {{subject}}",
      question: "{{actor}} asked a question on {{subject}}",
      reply: "{{actor}} replied in a discussion on {{subject}}",
      resolved: "{{actor}} resolved a discussion on {{subject}}",
      revision_done: "The agent revised {{subject}} as you asked",
      agent_replies_sent: "The agent answered your comments on {{subject}}",
      page_thread: "{{actor}} started a discussion on {{subject}}",
      task_assigned: "{{actor}} assigned you {{subject}}",
      task_due_today: "{{subject}} is due today",
      task_due_tomorrow: "{{subject}} is due tomorrow",
      task_due_on: "{{subject}} is due {{date}}",
      task_due_soon: "{{subject}} is due soon",
      task_was_due: "{{subject}} was due {{date}}",
      feedback_status: "Your feedback was updated",
    },
    // `agent_replies_sent` with its counts (wiki-resolve-with-agent D11).
    agentReplies_one: "The agent answered {{count}} of your comments on {{subject}}",
    agentReplies_other: "The agent answered {{count}} of your comments on {{subject}}",
    agentQuestions_one: "{{count}} question for you",
    agentQuestions_other: "{{count}} questions for you",
  },
  nav: {
    label: "Main",
    home: "Home",
    projects: "Projects",
    conversations: "Conversations",
    tasks: "Tasks",
    knowledge: "Knowledge",
    backoffice: "Backoffice",
    /** The small ESI mark beside the tenant logo of a branded shell: its name and tooltip. */
    onEsi: "on ESI",
    toggleSidebar: "Toggle sidebar",
    sidebar: "Navigation",
    sidebarHint: "Main navigation and account menu.",
    openMenu: "Open navigation",
    recentProjects: "Recent projects",
    newProject: "New project",
    newChat: "New chat",
    search: "Search",
    recents: "Recents",
    showMore: "Show more",
    showAll: "Show all",
    allProjects: "All projects",
    noConversations: "No conversations yet",
    expandProject: "Show conversations of {{name}}",
    collapseProject: "Hide conversations of {{name}}",
    newConversationIn: "Start a conversation in {{name}}",
    sharedEntry: "{{title}}, shared by {{name}}",
    more: "More",
  },
  feedback: {
    title: "Send feedback",
    lead: "Tell the ESI team about something that isn't working, or an idea. Below is exactly what will be sent.",
    type: "What is it about?",
    kinds: { bug: "Something isn't working", idea: "An idea or a suggestion" },
    types: { bug: "Bug", idea: "Idea" },
    describeBug: "What happened, and what did you expect?",
    describeIdea: "What would you like ESI to do?",
    hint: "The ESI team reads your words as written. Don't paste confidential content.",
    remaining_one: "{{count}} character left.",
    remaining_other: "{{count}} characters left.",
    empty: "Write a few words before sending.",
    whatIsSent: "What will be sent",
    whatIsSentLead:
      "With your description, as it will be stored. Untick what you'd rather not send. Nothing from your projects goes with it, unless you show it in a screenshot.",
    groups: {
      app: "App version and time",
      route: "Page",
      device: "Browser and device",
      recentErrors: "Recent errors in this tab",
      assistant: "Assistant",
    },
    viewport: { s: "small screen", m: "medium screen", l: "large screen" },
    noErrors: "No errors in this tab.",
    send: "Send",
    sending: "Sending…",
    sent: "Thank you. The ESI team has your feedback; you can follow it in My feedback.",
    seeMine: "See my feedback",
    throttled: "You've sent a lot of feedback in the last hour. You can send more from {{time}}.",
    off: "Your organisation has turned feedback off.",
  },
  feedbackDraft: {
    suggested: "Report this to the ESI team?",
    requested: "Feedback for the ESI team",
    drafted: "Drafted by the assistant · nothing from this conversation is attached",
    send: "Send",
    sending: "Sending…",
    edit: "Edit",
    dismiss: "Dismiss",
    dismissed: "Dismissed. I won't suggest this problem again in this conversation.",
    dismissedRequested: "Dismissed.",
    stopSuggesting: "Stop suggesting feedback",
    stopped:
      "The assistant won't suggest feedback any more. You can change this in your account settings.",
    sent: "Sent to the ESI team.",
    seeMine: "See my feedback",
    unavailable: "Couldn't send. Try again in a few minutes.",
    gone: "This draft is no longer available.",
    dialogLead:
      "Drafted by the assistant from this conversation. Change anything you like. Below is exactly what will be sent.",
    nothingAttached: "No conversation text, title or identifier.",
    reportProblem: "Report a problem",
    reportFailed: "Couldn't open the report. Try again.",
  },
  feedbackScreenshot: {
    label: "Screenshot",
    lead: "Optional. Everything from your projects is hidden behind grey blocks; you choose what to show.",
    add: "Add a screenshot",
    taking: "Taking the screenshot…",
    alt: "Screenshot of this page as it will be sent, with hidden areas in grey",
    remove: "Remove",
    show: "Show for this send only",
    shown: "Shown: {{regions}}",
    allHidden: "All content is hidden.",
    failed: "The screenshot couldn't be taken. You can still send your feedback without it.",
    notAttached: "Your feedback was sent, but the screenshot couldn't be attached.",
    regions: {
      page: "Page content",
      messages: "Messages",
      composer: "Message being written",
      conversations: "Conversation names",
      projects: "Project names and details",
      files: "Files",
      documents: "Document contents",
      wiki: "Wiki pages",
      people: "People's names",
      tasks: "Tasks",
      memory: "Memories",
    },
    view: "Show screenshot",
    hide: "Hide screenshot",
    loading: "Loading the screenshot…",
    unavailable: "The screenshot is no longer available.",
    console: {
      masked: "Screenshot, content hidden",
      unmasked: "Screenshot, with content the reporter chose to show",
      open: "Open screenshot",
      opening: "Opening…",
      expired: "The screenshot has expired or could not be opened.",
      failed: "The screenshot couldn't be opened. Try again.",
      blocked: "Your browser blocked the new tab. Allow pop-ups for the console, then try again.",
    },
  },
  myFeedback: {
    title: "My feedback",
    lead: "What you sent to the ESI team, and what became of it.",
    none: "No feedback yet",
    noneBody: "Use Send feedback in the account menu to report a bug or suggest an idea.",
    fixedIn: "Fixed in version {{version}}",
    statuses: {
      new: "New",
      triaged: "Reviewed",
      planned: "Planned",
      fixed: "Fixed",
      not_planned: "Not planned",
      duplicate: "Merged with a similar report",
    },
    reason: "Why: {{reason}}",
  },
  account: {
    menu: "Account menu",
    signedInAs: "Signed in as",
    theme: "Theme",
    themes: { system: "System", light: "Light", dark: "Dark" },
    language: "Language",
    languages: { en: "English", fr: "Français" },
    signOut: "Sign out",
    signingOut: "Signing out…",
    settings: "Account settings",
    sendFeedback: "Send feedback",
    about: "About ESI",
    roles: {
      tenant_admin: "Organisation admin",
      entity_admin: "Entity admin",
      member: "Member",
    },
  },
  about: {
    title: "About ESI",
    lead: "The version running in this browser and what changed in recent releases.",
    sendFeedback: "Send feedback",
    version: "Version",
    build: "Build",
    copyBuild: "Copy build ID",
    copied: "Build ID copied",
    whatsNew: "What's new",
    features: "New",
    fixes: "Fixed",
    noNotes: "No release notes yet.",
    noUserFacing: "Behind-the-scenes improvements only.",
    more_one: "and {{count}} more",
    more_other: "and {{count}} more",
  },
  projectRoles: {
    owner: {
      name: "Owner",
      description: "Accountable for the project; can delete it and transfer ownership.",
    },
    manager: {
      name: "Project manager",
      description: "Manages members, settings and the wiki; can declassify.",
    },
    contributor: { name: "Contributor", description: "Adds files and publishes wiki pages." },
    reader: { name: "Reader", description: "Asks questions and reads files and wiki pages." },
    guest: { name: "Guest", description: "Sees only what is shared with them." },
  },
  home: {
    title: "Ask across your knowledge",
    lead: "Answers cite the passages they rely on, and say plainly what they could not find.",
    ask: "Ask a question…",
    send: "Ask",
    sendHint: "Enter to send, Shift+Enter for a new line",
    project: "Project",
    projectHint: "Answers come from one project for now.",
    noProjectTitle: "Answers come from a project",
    noProject:
      "Create a project and add files to it: ESI answers from what the project holds, with citations.",
    createProject: "Create a project",
    loadingProjects: "Loading your projects…",
    noProjectOption: "No project",
    standaloneHint: "Without a project, answers use no project knowledge.",
    standaloneDisabled:
      "Conversations outside a project are turned off for your organisation. Pick a project to ask in.",
  },
  placeholder: {
    notYet: "Not available yet",
    conversations: {
      title: "Conversations",
      body: "Your conversations across every project will be listed here. For now, open a project to see its conversations.",
    },
    knowledge: {
      title: "Knowledge",
      body: "Every source you can read, across projects, will be browsable here. For now, each project's knowledge panel lists its files.",
    },
    backoffice: {
      title: "Backoffice",
      body: "Administration of your organisation.",
      refused:
        "The backoffice is not available to your role. Ask an organisation admin if you need a change.",
    },
    notFound: {
      title: "Page not found",
      body: "This address does not lead anywhere in ESI.",
      home: "Back to home",
    },
    tasksSlot: "No background tasks",
  },
  backoffice: {
    signin: "Sign-in",
    signinHint: "Identity providers and the domains that route sign-ins to them.",
    memory: "Memory",
    memoryHint:
      "Retention, memory policies, usage figures and memory events. Never what people saved.",
    title: "Backoffice",
    lead: "Administration of your organisation: people, entities, settings, directory and data residency.",
    directory: "Directory",
    directoryHint: "Identity provider, SCIM and HR sources; unmapped org codes.",
    residency: "Data residency",
    residencyHint: "Where models may process prompts and sources, per level.",
    models: "Models",
    modelsHint: "What each model implies, which ones your organisation allows, and consent.",
    back: "Backoffice",
  },
  users: {
    // two-domain-sign-in-modes (#438, design T7): a "SSO or ESI password" domain whose IdP has
    // no directory source.
    leaversKeepPassword:
      "People removed from {{name}} keep their ESI password until you remove them here, or connect a directory source to do it automatically.",
    // sso-and-esi-credentials (#419, D4): the person's ESI sign-in beside their SSO.
    esiCredential: {
      title: "ESI sign-in",
      held: "Has an ESI password or passkey beside their organisation's sign-in.",
      heldNotAllowed:
        "Has an ESI password or passkey, not allowed by the domain now: kept but refused.",
      none: "No ESI password or passkey yet. Their domain allows one.",
      send: "Send ESI sign-in link",
      sending: "Sending…",
      sent: "A link to choose a password went to {{email}}. It works for 72 hours.",
      removeEllipsis: "Remove ESI sign-in…",
      removeEffect:
        "{{email}} loses their ESI password and passkeys, and the sessions that used them end. They keep their organisation's sign-in.",
      remove: "Remove ESI sign-in",
      removed: "ESI sign-in is removed.",
      pending: "An ESI sign-in link was sent and not used yet.",
      revokeEllipsis: "Revoke link…",
      revokeEffect:
        "The link sent to {{email}} stops working and nothing is added to their account. You can send a new one afterwards.",
      revoke: "Revoke link",
      revoked: "The link is revoked.",
      refused: {
        throttled: "Too many sign-in links for this person. Try again later.",
        notAllowed: "Their domain's sign-in mode does not allow ESI sign-in.",
        onlyWayIn: "This is their only way to sign in: it cannot be removed here.",
        conflict:
          "Not possible now: they have not signed in through their identity provider yet, a link is still live, a password is already set, or another sign-in account uses this e-mail.",
      },
    },
    memory: {
      title: "Memories",
      lead: "Erase this person's personal memories and pending memory suggestions now that their account is disabled. You never see what they saved.",
      erase: "Erase memories",
      counting: "Counting memories…",
      count_one: "{{count}} personal memory",
      count_other: "{{count}} personal memories",
      none: "No personal memory left. Their pending suggestions and project memory authorship can still be erased.",
      effects: {
        deleted: "Their personal memories and pending suggestions are deleted for good.",
        projects: "Project memories they suggested stay with their projects, without their name.",
        backups: "Backups keep deleted data for up to 7 days.",
      },
      confirmLabel: "Type {{email}} to confirm",
      confirm_one: "Erase {{count}} memory",
      confirm_other: "Erase {{count}} memories",
      done_one: "{{count}} memory erased.",
      done_other: "{{count}} memories erased.",
      kept_one: "{{count}} project memory stays, without an author.",
      kept_other: "{{count}} project memories stay, without an author.",
      changed:
        "Their memories changed since the count was shown. Check the new count, then confirm again.",
      active: "This person is active again: they manage their own memories.",
    },
    title: "Users",
    hint: "People in your scope: status, entities and roles, last sign-in.",
    lead: "People in the entities you administer. Disabling someone signs them out at their next request and keeps their work.",
    invite: "Invite",
    adminsOnly: "Only admins can see users.",
    search: "Search users",
    searchPlaceholder: "Search by name or email",
    noMatch: "No user matches this search.",
    none: "No users yet.",
    more: "Show more",
    caption: "Users in your scope",
    user: "User",
    statusLabel: "Status",
    status: { active: "Active", disabled: "Disabled" },
    memberships: "Entities and roles",
    managedBy: "Managed by",
    managedByEsi: "ESI",
    lastSignIn: "Last sign-in",
    neverSignedIn: "Never",
    actions: "Actions",
    manage: "Manage",
    manageOf: "Manage {{name}}",
    outsideScope_one: "Also in {{count}} entity outside your scope",
    outsideScope_other: "Also in {{count}} entities outside your scope",
    access: "Access",
    syncWarning:
      "Managed by {{sources}}: the next sync may re-activate this user. Disable them in your identity provider too.",
    notManageable:
      "This user also belongs to entities you don't administer, so only an organisation admin can disable them.",
    selfNote: "You can't disable your own account.",
    disable: "Disable user",
    disableTitle: "Disable {{name}}?",
    disableEffects: {
      signedOut: "They are signed out at their next request, on every device and app.",
      kept: "Their projects, files and conversations stay where they are.",
      reversible: "You can re-enable them at any time.",
    },
    disableConfirm: "Disable",
    enable: "Re-enable user",
    roleIn: "Role in {{entity}}",
    removeFrom: "Remove from {{entity}}",
    addTo: "Add to an entity",
    pickEntity: "Choose an entity",
    role: "Role",
    add: "Add",
    inviteTitle: "Invite someone",
    inviteBody:
      "They join the entity you pick. People outside your identity provider's domains get an email to set up their account.",
    email: "Email",
    displayName: "Name (optional)",
    entity: "Entity",
    inviteConfirm: "Invite",
    inviting: "Inviting…",
    refused: {
      self: "You can't disable your own account.",
      lastAdmin: "Your organisation needs at least one active organisation admin.",
    },
    mfa: {
      title: "Two-step verification",
      reset: "Reset two-step verification",
      effect:
        "Removes the user's authenticator app and passkeys: they set up a new one at their next sign-in where it is required. Their sessions stay open.",
      reason: "Reason (kept in the audit log)",
      confirm: "Reset",
      done: "Two-step verification reset.",
      self: "Reset your own two-step verification from your account settings.",
      federated:
        "This user signs in through your organisation's identity provider: reset it there.",
    },
    invitation: {
      label: "Invitation",
      pending: "Pending, expires {{date}}",
      expired: "Expired {{date}}",
      pendingShort: "Invitation pending",
      expiredShort: "Invitation expired",
      title: "Invitation",
      lead: "{{email}} has not set a password yet.",
      copy: "Copy invite link",
      confirmTitle: "Create a new invitation link?",
      replaces:
        "The new link replaces the one sent before: the earlier link stops working, even in the email.",
      once: "It works once and expires after 72 hours. Share it only with {{email}}.",
      create: "Create link",
      creating: "Creating…",
      issuedTitle: "New invitation link for {{email}}",
      issuedBody:
        "The link sent before no longer works. No email was sent: share this link with them yourself.",
      field: "Invitation link",
      copyLink: "Copy",
      copied: "Copied",
      expires: "Expires {{date}}",
      done: "Done",
      unsentTitle: "The invitation email was not sent",
      unsentBody:
        "{{email}} now has an account, but the email could not be delivered. Copy an invite link and share it with them yourself.",
      unsentAskAdmin:
        "{{email}} now has an account, but the email could not be delivered. Ask an organisation admin to copy an invite link for them.",
      close: "Close",
      refused: {
        notPending: "{{email}} already set a password: there is no invitation left to share.",
        disabled: "This user is disabled. Enable them before sharing an invitation link.",
        federated:
          "This user signs in through your organisation's identity provider: they need no invitation link.",
        throttled:
          "Too many links for this user in the last hour: the earlier links no longer work. Try again later.",
        missing: "This user's sign-in account is missing. Contact support.",
        forbidden: "Only organisation admins can create invitation links.",
        notFound: "This user no longer exists.",
      },
    },
  },
  entities: {
    title: "Entities",
    hint: "Your organisation's tree: rename, move and remove entities.",
    lead: "Settings, projects and access follow this tree. Entity admins edit the entities below their own.",
    adminsOnly: "Only admins can see entities.",
    root: "Organisation",
    actionsOf: "Actions for {{name}}",
    create: "Add a sub-entity",
    rename: "Rename",
    move: "Move",
    delete: "Delete",
    createTitle: "Add a sub-entity to {{name}}",
    renameTitle: "Rename {{name}}",
    moveTitle: "Move {{name}}",
    moveBody:
      "Its sub-entities move with it. Settings, governance and residency then follow the new parent.",
    deleteTitle: "Delete {{name}}?",
    deleteBody:
      "Only an empty entity can be deleted: no sub-entities, members, projects, groups, org codes or pending promotion requests. Its own settings are removed.",
    name: "Name",
    newParent: "New parent",
    currentParent: "current",
    createConfirm: "Add",
    renameConfirm: "Rename",
    moveConfirm: "Move",
    deleteConfirm: "Delete entity",
    refused: { notEmpty: "This entity isn't empty yet:" },
    usage: {
      children_one: "{{count}} sub-entity",
      children_other: "{{count}} sub-entities",
      members_one: "{{count}} member",
      members_other: "{{count}} members",
      projects_one: "{{count}} project",
      projects_other: "{{count}} projects",
      groups_one: "{{count}} group",
      groups_other: "{{count}} groups",
      orgCodes_one: "{{count}} org code",
      orgCodes_other: "{{count}} org codes",
      promotions_one: "{{count}} pending promotion request",
      promotions_other: "{{count}} pending promotion requests",
    },
  },
  adminSettings: {
    title: "Settings",
    hint: "What the assistant and projects may do, per level, with inheritance and locks.",
    lead: "Each level inherits from the one above unless it sets its own value. A lock stops every level below from changing it.",
    adminsOnly: "Only admins can change settings.",
    level: "Level",
    organisation: "Whole organisation",
    areas: {
      auth: "Sign-in",
      directory: "Directory",
      answers: "Answers",
      conversations: "Conversations",
      models: "Models",
      tools: "Tools",
      projects: "Projects",
      knowledge: "Knowledge",
      wiki: "Wiki",
      memory: "Memory",
      mcp: "MCP clients",
    },
    mcpCeiling: {
      upTo: "{{levels}} may be returned",
      none: "Nothing may be returned",
    },
    nativeClients: {
      any: "Any application",
      allowlist: "Only the applications listed",
      summary_one: "Only {{count}} listed application",
      summary_other: "Only {{count}} listed applications",
      summaryNone: "Only listed applications (none yet)",
      empty: "No application listed yet: only ESI's command-line tool can connect.",
      addLabel: "Add an application by its client ID (its metadata document URL)",
      add: "Add",
      checking: "Checking…",
      remove: "Remove {{client}}",
      invalidId: "Enter an https URL, as the application publishes it.",
      unreadable: "This application's metadata could not be read. Check the client ID.",
      alwaysAllowed: "ESI's command-line tool is always allowed.",
      firstParty: "ESI's command-line tool is always allowed.",
    },
    mfa: { optional: "Optional", required: "Required" },
    modelRoles: { chat: "Balanced", reasoning: "Deep", fast: "Fast" },
    hours_one: "{{count}} hour",
    hours_other: "{{count}} hours",
    megabytes_one: "{{count}} MB",
    megabytes_other: "{{count}} MB",
    usd: "{{count}} USD",
    percent: "{{count}} %",
    operatorBudget: "Operator budget",
    instanceResidency: "Fixed for this deployment: global models aren't available on it",
    lockedHere: "Locked for the levels below",
    // How outbound tools treat a level's files (its egress floor), in readers' terms: admins
    // never see the floor ids.
    egress: {
      title: "Outbound tools",
      readers: "Like its readers (default)",
      project: "Like files any conversation reader may read",
      entity: "Like files limited to the uploader’s entity",
      named: "Like files only named people may read",
      confirmLevel:
        "Web search and web fetch will accept content from {{level}} files as if its readers were {{audience}}, and argument inspection stops blocking verbatim copies of it.",
      confirmRisk: "People who cannot read these files may see what is sent.",
      confirm: "Loosen and save",
      audience: { project: "the project", entity: "the entity" },
    },
    levels: {
      howItWorks:
        "Levels go from least to most sensitive. Content built from several levels takes the most sensitive one.",
      listLabel: "Classification levels, least to most sensitive",
      leastSensitive: "Least sensitive",
      mostSensitive: "Most sensitive",
      files_one: "{{count}} file",
      files_other: "{{count}} files",
      moveUp: "Move {{level}} up",
      moveDown: "Move {{level}} down",
      details: "{{level}} details",
      unnamed: "Unnamed level",
      moved: "{{level}} moved to position {{position}} of {{total}}",
      add: "Add level",
      newName: "New level",
      added: "{{level}} added at position {{position}} of {{total}}",
      remove: "Remove level",
      removed: "{{level}} removed.",
      undo: "Undo",
      restored: "{{level}} restored at position {{position}}",
      usedBy: "Used by {{uses}}.",
      usesFiles_one: "{{count}} file",
      usesFiles_other: "{{count}} files",
      usesDefaults_one: "{{count}} default classification",
      usesDefaults_other: "{{count}} default classifications",
      nextStep: {
        files: "Reclassify its files before removing it.",
        defaults: "Change the default classification before removing it.",
        both: "Reclassify the files and change the default before removing it.",
      },
      fields: {
        name: "Name",
        readers: "Who can read",
        download: "Downloads",
        downloadLabel: "People who can read may download",
      },
      readersOption: {
        project: "Anyone who can read the conversation or project",
        entity: "Named entities, groups or people, never the whole project",
        named: "Only the people or groups named on the file",
      },
      inPractice: "In practice",
      whatTheyMean: "What these levels mean",
      projectLevels: "Classification levels of this project's files",
      notInScheme: "No longer in the classification levels.",
      problem_one: "{{count}} problem",
      problem_other: "{{count}} problems",
      fixToSave_one: "Fix {{count}} problem to save",
      fixToSave_other: "Fix {{count}} problems to save",
      problems: {
        nameEmpty: "Give this level a name.",
        nameTooLong: "Keep the name to 60 characters or fewer.",
        nameDuplicate: "Another level already has this name.",
        egressStricter:
          "Stricter than its readers: outbound tools can only treat a level more openly.",
        empty: "Keep at least one level.",
      },
      reviewAndSave: "Review and save",
      reviewTitle: "Before you save",
      consequence: {
        name: "Shown on files and in answers.",
        readers: {
          project: "Anyone who can read the project or conversation can read these files.",
          entity:
            "Never shared with the whole project: only the entities, groups or people named on each file can read it (in conversations, the uploader’s entity).",
          named: "Only the people and groups named on a file can read it.",
        },
        download: {
          on: "Readers can download these files.",
          off: "Readers can open these files in the app but not download them.",
        },
        outbound: {
          readers: {
            allow:
              "After a conversation reads these files, web search and web fetch run without asking.",
            confirm: "After a conversation reads these files, web search and web fetch ask first.",
            block: "After a conversation reads these files, web search and web fetch are blocked.",
          },
          treated: {
            project: {
              allow:
                "Web search and web fetch treat these files like project files: with your policy, they run without asking.",
              confirm:
                "Web search and web fetch treat these files like project files: with your policy, they ask first.",
              block:
                "Web search and web fetch treat these files like project files: with your policy, they are blocked.",
            },
            entity: {
              allow:
                "Web search and web fetch treat these files like files never shared with the whole project: with your policy, they run without asking.",
              confirm:
                "Web search and web fetch treat these files like files never shared with the whole project: with your policy, they ask first.",
              block:
                "Web search and web fetch treat these files like files never shared with the whole project: with your policy, they are blocked.",
            },
            named: {
              allow:
                "Web search and web fetch treat these files like files only named people may read: with your policy, they run without asking.",
              confirm:
                "Web search and web fetch treat these files like files only named people may read: with your policy, they ask first.",
              block:
                "Web search and web fetch treat these files like files only named people may read: with your policy, they are blocked.",
            },
          },
          unknown:
            "Web search and web fetch treat these files like their readers, under your organisation’s policy.",
          treatedUnknown: {
            project: "Web search and web fetch treat these files like project files.",
            entity:
              "Web search and web fetch treat these files like files never shared with the whole project.",
            named:
              "Web search and web fetch treat these files like files only named people may read.",
          },
        },
      },
      impact: {
        declassification: {
          on: "Owners and managers who can read these files may still publish content derived from them more widely.",
          off: "Content derived from these files can never be published more widely than its readers.",
        },
        default: "New files get this level unless the uploader picks another.",
        conversationFiles: "Files added to conversations outside projects get this level.",
      },
      summary: {
        readers: {
          project: "Project readers",
          entity: "Named entities",
          named: "Named readers",
        },
        download: "Download",
        noDownload: "No download",
        outbound: {
          readers: "Outbound like its readers",
          project: "Outbound like project files",
          entity: "Outbound like entity files",
          named: "Outbound like named files",
        },
        default: "Default for new files",
        conversationFiles: "Conversation files",
      },
      review: {
        added: "{{level}} is added at position {{position}} of {{total}}.",
        renamed: "{{from}} is renamed {{to}}.",
        renamedFiles_one: "Its file shows the new name.",
        renamedFiles_other: "Its {{count}} files show the new name.",
        moved: "{{level}} moves above {{below}}.",
        movedFiles_one:
          "Their file keeps its level; content built from these levels will now be labelled {{level}}, and lowering a file from {{level}} to {{lower}} becomes a widening only managers may do.",
        movedFiles_other:
          "Their {{count}} files keep their level; content built from these levels will now be labelled {{level}}, and lowering a file from {{level}} to {{lower}} becomes a widening only managers may do.",
        readersTightened: {
          project: "{{level}} now allows the project’s readers.",
          entity:
            "{{level}} is no longer shared with whole projects: only with the entities, groups or people named on each file.",
          named: "{{level}} now allows only the people and groups named on each file.",
        },
        keepReaders_one:
          "Its file keeps its current readers until someone changes them; files added to conversations follow the new rule at once.",
        keepReaders_other:
          "Its {{count}} files keep their current readers until someone changes them; files added to conversations follow the new rule at once.",
        readersLoosened: {
          project:
            "{{level}} files can now be read by anyone who can read the project or conversation.",
          entity:
            "{{level}} files can now be read by the entities, groups or people named on them, never the whole project.",
          named: "{{level}} files can now be read only by the people and groups named on them.",
        },
        download: {
          on: "People who can read {{level}} files can now download them.",
          off: "People who can read {{level}} files can no longer download them.",
        },
        egress: {
          project: "Web search and web fetch now treat {{level}} files like project files.",
          entity:
            "Web search and web fetch now treat {{level}} files like files never shared with the whole project.",
          named:
            "Web search and web fetch now treat {{level}} files like files only named people may read.",
        },
        removed: "{{level}} is removed.",
      },
    },
    unavailable: "No effect: not available on this deployment.",
    providers: {
      agentcore: "Amazon Bedrock AgentCore",
      lambda: "ESI page reader, in this Region",
    },
    navLabel: "Settings pages",
    groups: {
      general: {
        label: "General",
        purpose:
          "How answers use sources, conversations outside projects, and who may create and join projects.",
      },
      security: {
        label: "Sign-in & security",
        purpose:
          "Who gets an account at first sign-in, second factor, the applications people may connect, device sign-in and identity provider groups.",
      },
      models: {
        label: "Models & residency",
        purpose: "Where models run, the efforts people may use and how they pick a model.",
      },
      knowledge: {
        label: "Knowledge & memory",
        purpose:
          "Classification of files, how scans and images are read, wiki drafting and what the assistant remembers.",
      },
      limits: {
        label: "Usage & limits",
        purpose: "The largest file a project accepts and how many pages scans may read.",
      },
      integrations: {
        label: "Integrations",
        purpose: "The outside services the assistant may use, and their limits.",
      },
      clients: {
        label: "External clients",
        purpose:
          "Whether MCP clients such as Claude Code reach your knowledge, what they may save and receive, and which tools they may use.",
      },
    },
    sections: {
      answers: "Answers and conversations",
      projects: "Projects",
      signIn: "Sign-in",
      mcpAccess: "Access",
      mcpToolGroups: "Tool groups",
      directory: "Directory",
      esiTeam: "Sharing with the ESI team",
      models: "Models",
      classification: "Classification",
      files: "Scanned files and images",
      wiki: "Wiki",
      memory: "Memory",
      uploads: "Files and scans",
      webSearch: "Web search",
      webFetch: "Reading web pages",
      codeExecution: "Code execution",
      egress: "Outbound tool calls",
      other: "Other",
    },
    setHereCount_one: "{{count}} set at this level",
    setHereCount_other: "{{count}} set at this level",
    related: "Also on:",
    summary: {
      setHere: "Set at this level:",
      lockedAbove: "Locked above:",
      allDefault_one: "Nothing is set for the organisation: this setting uses the defaults.",
      allDefault_other:
        "Nothing is set for the organisation: all {{count}} settings use the defaults.",
      allInherited_one: "Nothing is set at this level: this setting is inherited.",
      allInherited_other: "Nothing is set at this level: all {{count}} settings are inherited.",
    },
    marks: { overridden: "Overridden at this level" },
    search: {
      label: "Search settings",
      placeholder: "Name or topic, e.g. memory",
      results: "Matching settings",
      none: "No setting matches.",
      found_one: "{{count}} setting found. Use the arrow keys, then Enter.",
      found_other: "{{count}} settings found. Use the arrow keys, then Enter.",
    },
  },
  errors: {
    legacy: {
      uploadMismatch: "The uploaded file does not match the declared size or type",
      readersRequired: "Readers must be named for this classification",
    },
    codes: {
      upload_size_limit: "Files are limited to {{maxMb}} MB in this project",
      unknown_models: "Unknown models: {{models}}",
      unknown_setting: 'Unknown setting "{{key}}"',
      invalid_setting: 'Invalid value for "{{key}}". Check this setting\'s requirements.',
      rule_document_usage: "This rule is a reader of {{count}} document(s)",
      rule_project_usage: "This rule is granted a role in {{count}} project(s)",
      attribute_rule_usage: "{{count}} rule(s) use {{key}}",
      attribute_value_rule_usage: "{{count}} rule(s) use {{key}} = {{value}}",
      document_reader_required: "You must be among the document's readers",
      search_length: "A search needs {{min}} to {{max}} characters",
      operator: "Instance operators cannot authorize native clients",
      inactive: "The account or its organisation is not active",
      mfa_required: "Two-step verification is required for this organisation",
      device_grant_disabled: "Your organisation does not allow signing in with a device code",
      client_outdated: "Reload ESI to continue with the latest version.",
      cannot_read_project: "This person cannot read the project.",
      cannot_read_sources: "This person cannot read the sources.",
      self: "You already have access to your own conversation.",
      cannot_receive: "This person cannot receive the conversation.",
      quota: "Your usage limit is reached. Try again later.",
      auth_unavailable: "Sign-in is temporarily unavailable. Try again later.",
      client_not_allowed: "This application is not allowed to connect.",
      database_unavailable: "The service is briefly unavailable. Try again in a moment.",
      flow_expired: "Sign-in expired. Start again.",
      forbidden_origin: "This request's origin is not allowed.",
      internal_error: "The server ran into a problem. Try again later.",
      invalid_code: "This code is invalid. Check it and try again.",
      invalid_credentials: "Check your sign-in details and try again.",
      invalid_link: "This link is invalid or expired.",
      invalid_report: "This report could not be accepted.",
      method_not_allowed: "This action is not supported here.",
      model_unavailable: "The assistant is temporarily unavailable. Try again later.",
      payload_too_large: "This request is too large. Reduce it and try again.",
      schema_not_ready: "The server is updating. Try again shortly.",
      unavailable: "This service is temporarily unavailable.",
      unsupported_media_type: "This file format is not supported.",
      weak_password: "Choose a stronger password.",
      password_already_set: "This account already has a password. Sign in, or reset your password.",
      snapshot_too_large: "The directory snapshot is too large.",
      pull_failed: "The directory could not be reached. Try syncing again.",
      invalid_records: "The directory returned invalid records.",
      conflict: "This changed meanwhile. Reload and try again.",
      project_frozen: "This project is read-only.",
      personal_project: "Only the owner can manage this personal project.",
      turn_in_progress: "A reply is still being written. Try again when it finishes.",
      conversation_changed: "The conversation changed. Reload and try again.",
      upload_already_confirmed: "This upload is already confirmed.",
      upload_missing: "Send the file before confirming the upload.",
      change_set_closed: "This change set is no longer open.",
      change_set_changed: "This change set changed. Reload it and review the changes.",
      item_stale: "The page changed. Update this proposal against the current version.",
      too_many_open_change_sets:
        "This project has too many open change sets. Publish or discard one first.",
      page_already_in_change_set: "This page already has a proposal in this change set.",
      proposal_decided: "This proposal was already decided.",
      proposal_expired: "This proposal expired.",
      version_current: "This is already the current version of the page.",
      cannot_review_hidden_items:
        "Some items are not accessible to you. Ask someone who can read every item to publish this change set.",
      self_publish_mcp_draft:
        "Your MCP client proposed this change, so someone else with publish rights must publish it.",
      draft_stale: "The page changed. Create a new draft.",
      draft_not_open: "This draft is no longer open.",
      draft_not_stale: "This draft is already up to date.",
      draft_changed: "The draft changed. Reload and try again.",
      wiki_title_taken: "Another page already uses this title.",
      already_withdrawn: "This declassification was already withdrawn.",
      export_not_ready: "This export is not ready. Try again later.",
      export_stale: "Access changed. Create a new export.",
      reauth_required: "Sign in again before continuing.",
      last_factor: "Your account must keep a second factor.",
      totp_required: "Set up your authenticator app first.",
      entity_not_empty: "Move this entity's contents before deleting it.",
      self_disable: "You cannot disable your own account.",
      last_tenant_admin: "Keep at least one active organisation administrator.",
      domain_not_bound:
        "Only a verified domain bound to an active identity provider has a sign-in mode.",
      last_sign_in_method:
        "This would leave no active organisation administrator a way to sign in.",
      idp_inactive: "This sign-in provider has been removed from your organisation.",
      confirmation_mismatch: "The confirmation does not match. Type it exactly as shown.",
      approval_expired: "This approval expired. Request it again.",
      approval_decided: "This approval was already decided.",
      approval_mismatch: "The action changed. Request approval again.",
      seat_limit: "Your organisation has reached its user limit.",
      storage_limit: "Your organisation has reached its storage limit.",
      plan_in_use: "Move the organisations using this plan before deleting it.",
      self_mfa_reset: "Reset your own second factors in account settings.",
      invitation_not_pending:
        "This user already set a password: there is no invitation left to share.",
      user_disabled: "This user is disabled.",
      account_missing: "This user's sign-in account is missing. Contact support.",
      admin_email_in_use:
        "This e-mail already has an account in another organisation. Use another e-mail.",
      account_unavailable:
        "We can't create an account with this e-mail. Contact your administrator.",
      invitation_pending:
        "This admin has not signed in yet: there is nothing to reset. Resend their invitation instead.",
      tenant_not_active: "Only the admins of an active organisation can be invited.",
      resolution_running: "The agent is already resolving this change set's comments.",
      item_changed_since: "This page changed after the agent's revision: edit it by hand instead.",
      not_standalone: "Only a conversation outside a project can be moved.",
      move_changed: "The move's effects changed. Review them again.",
      residency_narrower: "Choose a project that allows this conversation's model residency.",
      access_request_pending: "An access request is already pending.",
      access_request_decided: "This access request was already decided or expired.",
      access_request_stale: "Access changed. Review the request again.",
      access_held: "This change requires the project owner's approval.",
      memory_kind: "This memory cannot be changed here.",
      memory_queue_full: "The memory queue is full. Try again later.",
      memory_duplicate: "This memory already exists.",
      memory_user_active: "Disable the user before erasing their memories.",
      memory_count_changed: "The memory count changed. Review it again.",
      implications_changed: "The model terms changed. Review them again.",
      name_taken: "This name is already in use.",
      folder_limit: "This section has reached its folder limit.",
      tasks_disabled: "Tasks are disabled for this project.",
      link_limit: "This task has reached its link limit.",
      attachments_disabled: "Attachments are disabled here.",
      attachment_limit: "This conversation has reached its attachment limit.",
      attachment_not_ready: "This attachment is not ready yet.",
      attachment_sent:
        "This file was already used in the conversation: its origin can no longer change.",
      origin_fixed: "This file's origin was set when it was uploaded.",
      private_level_unavailable:
        "No private classification level exists for your organisation. Ask an admin.",
      classification_not_private:
        "Files in a conversation outside a project are private; this classification is not.",
      egress_floor_stricter_than_readers:
        "Outbound tools can't treat a level more strictly than its readers. Pick a wider choice.",
      egress_floor_not_editable: "This server can't set how outbound tools treat a level yet.",
      logo_invalid: "This image can't be used as a logo. Use a still PNG or WebP image.",
      not_openable: "This file can't be opened here. Download it instead.",
      too_large_to_view: "This file is too large to show here. Download it instead.",
      is_file: "This artefact is a file: download it to open it.",
      wiki_item_invalid: "This page can't be saved as written: check its title and first lines.",
      anchor_not_found: "This passage is no longer in the page: select it again.",
      federated_account:
        "This user signs in through your organisation's identity provider: they need no invitation link.",
      archived: "Unarchive the conversation before continuing.",
      not_in_project: "Move the conversation into a project before sharing it.",
      already_added: "This artefact is already in the project's knowledge.",
      not_newer: "There is no newer version to add.",
      version_mismatch: "This changed since you opened it. Reload, then try again.",
      folder_not_empty: "This folder is not empty.",
      change_requested: "An open change request blocks this. Resolve it first.",
      change_set_working: "Its author has not submitted this change set for review yet.",
      folder_missing:
        "A folder in this change set no longer exists. Reject that item to publish the rest.",
      standalone_disabled: "Conversations outside projects are disabled.",
      feedback_disabled: "Your organisation has turned feedback off.",
      fingerprint_linked: "This fingerprint is linked to another group. Unlink it there first.",
      assist_no_reports: "Every report of this group keeps its organisation out of the assistant.",
      suggestion_decided: "This suggestion was already decided.",
      issue_exists: "This group already has an issue.",
      preview_changed: "The issue changed since you previewed it. Preview it again.",
      model_connection_probe_required: "Probe the connection successfully before enabling it.",
      model_connection_logging_confirm:
        "This account logs model invocations: confirm to enable the connection.",
      model_connection_same_account:
        "A connection points at another AWS account: this deployment's own account is always available.",
      model_account_same_account:
        "This deployment's own account is always available and is never added to the allowlist.",
      model_account_capacity: "The account allowlist is full for this deployment.",
      model_provider_disabled:
        "Bedrock is turned off for the instance's connections: turn it on under Providers first.",
      model_account_same_binding: "The account already has this binding.",
      model_account_tenant_bound:
        "This account is bound to a tenant: only that tenant's connections may use it.",
      model_account_tenant_unavailable:
        "This tenant is being deleted: it cannot hold a model account.",
      issue_text_flagged: "The issue text looks copied from a report. Edit the title or summary.",
      reason_text_flagged:
        "The reason holds a reporter's words, an address, an identifier or a tenant's name. Edit it.",
      tracker_not_configured: "GitLab is not configured on this instance yet.",
      memory_disabled: "Memory is disabled for your account.",
      project_memory_disabled: "Memory is disabled for this project.",
      wiki_trust_level:
        "This project's trust level does not let you approve or publish these changes.",
      wiki_suggestions_disabled: "Wiki suggestions are turned off for this project.",
      nothing_to_declassify: "There is nothing to declassify.",
      empty_reason: "Enter a reason before continuing.",
      readers_below_floor: "The selected readers are wider than the classification allows.",
      untested_lab_unacknowledged: "Not tested by ESI: confirm the warning before adding this lab.",
      model_pin_disallowed: "This version is not allowed here: pick one this scope may use.",
      model_roles_uncovered: "Allow a usable model for every required role.",
      too_deep: "This folder would be nested too deeply.",
      cycle: "A group or folder cannot contain itself.",
      wrong_section: "Choose a folder in the same section.",
      not_a_member: "Choose a member of this project.",
      origin_derived: "This derived content's origin cannot be changed.",
      model_budget: "The organisation's model budget is reached. Try again later.",
      user_budget: "Your usage budget is reached. Try again later.",
      triage_budget_reached: "Daily triage budget reached. Try again tomorrow.",
      throttled: "Too many requests. Wait a moment and try again.",
      invalid: "Check the details and try again.",
      unprocessable: "This change is not allowed. Check the details.",
      forbidden: "You don't have permission to do this.",
      not_found: "This doesn't exist, or you don't have access to it.",
      unauthorized: "Your session has ended. Sign in again.",
      unsupported: "This is not available on this server yet.",
      unknown_user: "This user is unknown to the directory source.",
      unknown_group: "This group is unknown to the directory source.",
      unknown_entity: "This entity is unknown.",
      depth_exceeded: "The group nesting limit was reached.",
      invalid_attribute: "An attribute has an invalid value.",
      no_adapter: "This directory source is not connected yet.",
      credential_missing: "The source has no usable key. Replace its key.",
      credential_refused:
        "The source's key could not be read: it is missing, or it belongs to another organisation. Replace it.",
      token_refused:
        "Google refused the service account's key. Replace the key or check the account.",
      missing_privilege:
        "The service account lacks a read privilege. Test the source to see which.",
      scope_not_found: "The provisioning group or org unit was not found in Google Workspace.",
    },
    network: "The server could not be reached. Check your connection and try again.",
    unexpected: "Something went wrong. Try again.",
    sessionEnded: "Your session has ended. Sign in again.",
    forbidden: "You don't have permission to do this.",
    notFound: "This doesn't exist, or you don't have access to it.",
    notImplemented: "This isn't available on this server yet.",
    server: "The server ran into a problem. Try again in a moment.",
    refused: "The request was refused. Check the details and try again.",
    signInAgain: "Sign in again",
  },
  buildRefresh: {
    available: "A new version of ESI is available",
    required: "Some actions may fail until you reload.",
    reload: "Reload",
    later: "Later",
  },
  session: {
    ended: {
      expired: "Your session has expired.",
      signed_out: "You signed out of ESI in another tab or window.",
      revoked: "This session was signed out from your account settings or by your organisation.",
    },
    unsaved:
      "Sign in again to continue. Copy anything you haven't sent first: signing in reloads the page.",
    signInAgain: "Sign in again",
    copyUnsaved: "Copy unsaved text",
    copiedOk: "Copied. Paste it somewhere safe before you sign in again.",
    copyFailed: "Couldn't copy. Select your text and copy it yourself.",
    dismiss: "Dismiss",
    idpRefresh: {
      lead: "Refresh your sign-in to keep the access your company's groups give you.",
      unsaved: "Finish or copy what you're working on first: refreshing reloads the page.",
      action: "Refresh sign-in",
      later: "Later",
    },
  },
  signIn: {
    title: "Sign in",
    lead: "Use your work email. If your organisation has its own sign-in, you'll continue there.",
    errors: {
      expired: "Sign-in took too long and expired. Start again.",
      failed: "Sign-in failed. Check your email address and try again.",
      no_account: "No ESI account matches this sign-in. Ask your administrator for access.",
      seat_limit:
        "Your organisation has no seats left in ESI. Ask your administrator to free one or add seats.",
      invalid: "The email, password or code is wrong. Check them and try again.",
      throttled_one: "Too many attempts. Try again in {{count}} minute.",
      throttled_other: "Too many attempts. Try again in {{count}} minutes.",
      throttledLater: "Too many attempts. Try again later.",
      flowExpired: "Sign-in took too long and expired. Enter your email again.",
      invalidCode: "This code isn't valid. Check your authenticator app and try again.",
      passkeyCancelled: "The passkey wasn't used. Try again, or sign in with your password.",
      unknown:
        "Sign-in did not complete. Start again, or ask your administrator if it keeps happening.",
    },
    notice: {
      expired: "Your session has expired. Sign in again to continue where you were.",
      signed_out: "You signed out. Sign in again to continue.",
      revoked: "This session was signed out. Sign in again to continue.",
    },
    email: "Work email",
    submit: "Continue",
    password: "Password",
    forgot: "Forgot password?",
    signIn: "Sign in",
    usePasskey: "Use a passkey",
    signingInAs: "Signing in as",
    changeEmail: "Change",
    totpLead: "Enter the 6-digit code from your authenticator app.",
    verify: "Verify",
    newPasswordLead: "Choose a password for your account before you continue.",
    passkeyWaiting: "Follow your browser's prompt to use your passkey.",
    or: "or",
    totpHelp: "No access to your app? Ask your administrator to reset two-step verification.",
    choice: {
      lead: "Your organisation also allows an ESI password or passkey on this domain.",
      label: "Ways to sign in as {{email}}",
      continueWith: "Continue with {{name}}",
      credentials: "Use password or passkey",
    },
    steps: {
      password: "Enter your password",
      passkey: "Use your passkey",
      newPassword: "Choose a password",
      redirect: "Continuing to your organisation's sign-in",
      redirectLead: "Your organisation signs you in itself. You continue there through:",
      choice: "Choose how to sign in",
    },
    language: "Language",
    /** Under a tenant's logo on a standalone page: ESI stays named (co-branding). */
    onEsi: "Sign in to ESI",
    enrol: {
      title: "Set up two-step verification",
      lead: "Your organisation requires a second step at sign-in. Set it up now to continue.",
      totp: "Use an authenticator app",
      passkey: "Use a passkey",
      totpHint:
        "A 6-digit code from an app on your phone, such as Microsoft or Google Authenticator.",
      passkeyHint: "Your device's fingerprint, face or screen lock. Nothing to type.",
      other: "Choose another method",
    },
  },
  console: {
    badge: "Console",
    signIn: {
      title: "Operator sign-in",
      lead: "For the operators of this ESI instance. Sign in with your operator account.",
      email: "Email",
      totpHelp:
        "No access to your authenticator app? An AWS administrator of this instance can reset it.",
      enrolLead:
        "Operators always confirm their sign-in with a second step. Set up an authenticator app to continue.",
      unavailable: "Operator sign-in isn't available yet. Try again in a few minutes.",
    },
    nav: {
      label: "Console",
      tenants: "Tenants",
      plans: "Plans",
      modelConnections: "Model connections",
      policies: "Policies",
      support: "Support access",
      reports: "Usage",
      feedback: "Feedback",
      operators: "Operators",
      profile: "Profile",
      groups: { governance: "Governance", access: "Access" },
    },
    operator: {
      menu: "Operator menu",
      role: "Instance operator",
      profile: "Profile",
    },
    profile: {
      title: "Profile",
      lead: "Who you are on this console and how you sign in.",
      identity: "Identity",
      name: "Name",
      nameHint: "Shown to other operators in sessions and activity.",
      nameInvalid: "Use letters or digits, up to 80 characters, on one line.",
      saved: "Name saved.",
      email: "Email",
      totpLead:
        "Every operator signs in with a code from an authenticator app. Lost your device? Ask an instance administrator to reset it: passkeys added before the reset stop working.",
      passkeysLead:
        "Sign in to the console with a passkey instead of your password and authenticator code.",
      stepUp: "Confirm it's you to change your passkeys.",
      stepUpAdd:
        "Enter your password and authenticator code to add a passkey. A passkey can't add another one.",
      unavailable: "Passkeys aren't available on this console.",
    },
    operators: {
      title: "Operators",
      lead: "The people who run this instance, their signed-in browsers and what they changed.",
      tabs: { sessions: "Sessions", members: "Operators", audit: "Activity" },
      tabsLabel: "Operators pages",
      membersTitle: "Operator management is coming",
      membersBody:
        "Adding and removing operators will happen here, each change confirmed with your second step and recorded for every operator to see. Until then, operators are managed by the instance's administrators.",
      auditTitle: "Operator activity is coming",
      auditBody:
        "Every operator change (tenants, plans, policies, limits, support requests) will be listed here, newest first.",
      notYet: "Not available yet",
    },
    tenantStatus: {
      active: "Active",
      suspended: "Suspended",
      deletion_scheduled: "Deletion scheduled",
      deleted: "Deleted",
    },
    spendAlert: { warning: "80 % of spend limit", reached: "Spend limit reached" },
    syntheticSpendDetail:
      "Includes {{amount}} of synthetic demo spend: it counts toward the 80 % warning, but only real spend can reach the limit.",
    spendNotice: {
      warning: {
        hard: "This tenant has used 80 % of its monthly AI spend limit. At the limit, its turns and background work stop until next month or a raised limit.",
        soft: "This tenant has used 80 % of its monthly AI spend limit (soft: it only warns).",
      },
      reached: {
        hard: "This tenant reached its hard monthly AI spend limit: turns are refused and background work is paused until next month or a raised limit.",
        soft: "This tenant passed its soft monthly AI spend limit: nothing is stopped.",
      },
    },
    tenants: {
      title: "Tenants",
      lead: "Every organisation on this instance, with its plan and use. Their content is not visible here.",
      caption: "Tenants of the instance",
      create: "New tenant",
      createTitle: "New tenant",
      createBody: "Creates the organisation and invites its first tenant admin by e-mail.",
      createConfirm: "Create tenant",
      rename: "Edit name",
      name: "Name",
      status: "Status",
      plan: "Plan",
      users: "Active users",
      entities: "Entities",
      ocr: "OCR pages this month",
      spend: "AI spend this month",
      created: "Created",
      adminEmail: "First admin's e-mail",
      emailInUse:
        "This e-mail already has an account in another organisation. Use another e-mail: moving an account between organisations comes later.",
      adminName: "First admin's name",
      defaultPlan: "The default plan",
      none: "No tenants yet",
      noneHint: "Create the first organisation to invite its tenant admin.",
      purgeOn: "Purged on {{date}}",
      back: "All tenants",
      admins: "Tenant admins",
      adminsNote: "The organisation's contract counterparts: the only people you see by name.",
      noAdmins: "This tenant has no active tenant admin: nobody can approve support access.",
      idps: "Identity providers",
      noIdps: "Native accounts only.",
      idpStatus: { active: "active", disabled: "disabled" },
      domains: "Verified domains",
      noDomains: "No verified domain.",
      createdOn: "Created {{date}}",
      adminCount_one: "{{count}} tenant admin",
      adminCount_other: "{{count}} tenant admins",
    },
    tenantTabs: {
      label: "Tenant pages",
      overview: "Overview",
      admins: "Admins",
      "sign-in": "Sign-in",
      limits: "Plan and limits",
      lifecycle: "Lifecycle",
    },
    tenantList: {
      search: "Search tenants",
      filters: "Show",
      all: "All",
      active: "Active",
      suspended: "Suspended",
      deletion: "Deletion scheduled",
      synthetic: "Synthetic data",
      noMatch: "No tenant matches",
      noMatchHint: "Try another name or show every tenant.",
      showAll: "Show every tenant",
      syntheticMark: "Synthetic data",
      usersCount_one: "{{count}} active user",
      usersCount_other: "{{count}} active users",
      used: "{{percent}} % used",
    },
    limits: {
      title: "Plan and limits",
      seats: "Seats",
      storageBytes: "Storage (GB)",
      ocrMonthlyPages: "OCR pages per month",
      modelMonthlyUsd: "AI spend per month (USD)",
      fromPlan: "Empty follows the plan: {{value}}",
      note: "Changes apply from the next request and are recorded in the tenant's audit log. Lowering a limit disables nobody.",
      saved: "Limits saved.",
    },
    lifecycle: {
      title: "Lifecycle",
      suspend: "Suspend",
      reactivate: "Reactivate",
      delete: "Schedule deletion",
      cancel: "Cancel deletion",
      expedite: "Delete now…",
      suspendTitle: "Suspend {{name}}?",
      suspendBody:
        "Every session and token of its users stops working at their next request. Nothing is deleted.",
      suspendConfirm: "Suspend",
      reactivateTitle: "Reactivate {{name}}?",
      reactivateBody: "Its users can sign in again.",
      reactivateConfirm: "Reactivate",
      deleteTitle: "Schedule the deletion of {{name}}?",
      deleteBody:
        "Its users are signed out now. After 30 days its content, users and directory are purged; only its audit log and aggregate reports remain. You can cancel it until then.",
      deleteConfirm: "Schedule deletion",
      cancelTitle: "Cancel the deletion of {{name}}?",
      cancelBody:
        "The tenant returns to the status it had before its deletion was scheduled, with all its data (suspended if that is unknown).",
      cancelConfirm: "Cancel deletion",
      expediteTitle: "Delete {{name}} now?",
      expediteBody:
        "The purge starts now instead of after {{date}}. Its content, users and directory are deleted for good; only its audit log and aggregate reports remain. This cannot be undone.",
      expediteConfirm: "Delete now",
      expediteStepUp: "Confirm it's you before deleting this tenant now.",
      typeId: "Type the first 8 characters of its ID, {{prefix}}",
      idPrefix: "Tenant ID",
      purging: "Deletion under way: the purge has started and cannot be cancelled.",
      typeName: "Type {{name}} to confirm",
      scheduledNotice: "Deletion scheduled: the tenant will be purged after {{date}}.",
    },
    mfa: {
      reset: "Reset two-step verification",
      resetOf: "Reset two-step verification of {{name}}",
      title: "Reset two-step verification of {{email}}",
      body: "Removes this admin's authenticator app and passkeys; they set up a new one at their next sign-in. Recorded in the tenant's audit log.",
      reason: "Reason",
      confirm: "Reset",
      done: "Two-step verification reset.",
      invitationPending:
        "{{email}} has not signed in yet, so there is no two-step verification to reset. Resend their invitation instead.",
    },
    adminInvitation: {
      failed: "The invitation to {{email}} could not be sent: {{reason}}",
      expired: "The invitation to {{email}} expired on {{date}} before it was accepted.",
      failedShort: "Invitation not sent: {{reason}}",
      expiredShort: "Invitation expired {{date}}",
      sentShort: "Invitation sent {{date}}",
      pendingShort: "Invitation pending",
      resend: "Resend invitation",
      resendOf: "Resend the invitation of {{name}}",
      resent: "Invitation sent to {{email}}.",
      notResent: "The invitation to {{email}} could not be sent: {{reason}}",
      reason: {
        sandbox_unverified:
          "this deployment only sends to verified addresses (e-mail service sandbox)",
        not_authorized: "this deployment is not allowed to send e-mail",
        sending_paused: "e-mail sending is paused for this deployment",
        unknown: "the e-mail service refused it",
      },
    },
    models: {
      title: "Model connections",
      lead: "Which AWS accounts serve the models. Changes apply within 30 seconds, without a deploy.",
      perimeter: {
        title: "The network perimeter does not match the allowlist",
        error: {
          capacity_exceeded:
            "The last update wrote nothing: the allowlist exceeds what the perimeter's policies can hold.",
          write_failed: "The last update failed: an AWS write did not complete.",
        },
        removals:
          "Removed, still open at the network level: {{accounts}}. No connection routes to them.",
        adds: "Added, not admitted yet: {{accounts}}. Their connections do not route.",
        retry:
          "The update is retried every 15 minutes; the stage's alarm fires when a retry still finds it diverged. If it persists, follow the runbook.",
      },
      providers: {
        title: "Providers",
        lead: "Which providers the instance's connections may use, and which ones tenants may connect with their own accounts. A provider without an ESI adapter cannot be turned on yet.",
        kind: "Provider",
        instance: "Instance",
        tenant: "Tenants",
        instanceOf: "{{name}} for the instance's connections",
        tenantOf: "{{name}} for tenants' own connections",
        notYet: "Not yet available",
        tenantLater:
          "Tenants connect their own Bedrock in a later release; the switch is kept until then.",
        bedrockOff:
          "Bedrock is off for the instance: the connections below do not route. This deployment's own account keeps serving.",
        confirmTitle: "Turn this provider on?",
        confirm: {
          instance: "The instance's connections may use {{name}} again, from the next request.",
          tenant: "Tenant admins may connect {{name}} in AWS accounts bound to their tenant.",
        },
        turnOn: "Turn on",
      },
      labs: {
        title: "Labs",
        lead: "The labs whose models Bedrock serves on this stage. Every tenant starts on the default lab; tenant admins may add any lab not blocked here, after a warning when ESI has not tested it. Blocking a lab removes it from every tenant at once.",
        state: {
          blocked: "Blocked",
          untested: "Available · Not tested by ESI",
          tested: "Tested by ESI · {{date}}, catalogue {{version}}",
        },
        geographies: "Versions in {{list}}",
        geo: { eu: "the EU", global: "global" },
        notInEu: "Global only: not available in the EU",
        default: "Every tenant's default",
        tenants_one: "{{count}} tenant uses it",
        tenants_other: "{{count}} tenants use it",
        accessDenied:
          "A check of its models was denied: enable model access for this lab in the Bedrock console of the account (runbook: Bedrock model access).",
        block: "Block",
        blockOf: "Block {{name}}",
        unblock: "Unblock",
        confirmTitle: "Unblock this lab?",
        confirm:
          "Tenant admins may add {{name}} again, and tenants that had added it get it back from the next request.",
      },
      health: {
        title: "Model health checks",
        help: "ESI checks that each model answers on each connection, with a one-word test call. Versions that stop answering are set aside until they answer again.",
        hourly: "Every hour",
        daily: "Every day",
        unknown: {
          assume_denied:
            "Health unknown: ESI can't check this connection, its role doesn't trust the health check role. Its models stay in use, but ESI won't move to a newer version through it. Add the health check role to the role's trust policy (Show the role setup).",
          assume_failed:
            "Health unknown: ESI couldn't reach this connection's role to check it. Its models stay in use, but ESI won't move to a newer version through it until a check succeeds.",
        },
        listDenied:
          "Can't list models: add the two List actions to the role (bedrock:ListInferenceProfiles, bedrock:ListFoundationModels)",
      },
      switch: {
        title: "Connections to other AWS accounts",
        on: "Calls may use the connections below, in this order.",
        off: "Every call uses this deployment's own account only. The connections below stay as they are.",
        onWord: "On",
        offWord: "Off",
      },
      connections: {
        title: "Connections",
        lead: "Bedrock in another AWS account, through a role ESI assumes. Calls try them from the top, then this deployment's own account.",
        add: "Register connection",
        needAccount: "Add an account to the allowlist first",
        order: "Connections in route order",
      },
      default: {
        title: "This deployment's account",
        body: "Account {{account}}, always last. It keeps serving when every connection above is off or failing.",
        thisAccount: "of this deployment",
      },
      status: {
        routing: "Routing",
        ready: "Probe passed",
        stale: "Probe out of date",
        unprobed: "Not probed",
        notApplied: "Waiting for the perimeter",
        assumeDenied: "Role cannot be assumed",
        modelDenied: "Model access denied",
        error: "Probe failed",
        tenantBound: "Account bound to a tenant",
        providerOff: "Not routing: Bedrock is off for the instance",
      },
      logging: {
        on: "Account logs invocations",
        off: "Logging off",
        unknown: "Logging unknown (not readable)",
        confirmUnknown:
          "ESI could not read whether this account logs model invocations, prompts included. Enable it only if you know its logging setting.",
        confirm:
          "This account logs model invocations, prompts included. Enable it only if that logging is expected.",
        enableAnyway: "Enable anyway",
        keepOff: "Keep off",
      },
      retention: { default: "Default retention", aws_review: "AWS review retention" },
      actionsFor: "Actions for {{name}}",
      probe: "Probe now",
      probing: "Probing: one minimal call, no tenant content…",
      showSetup: "Show the role setup",
      moveUp: "Move up",
      moveDown: "Move down",
      delete: "Delete connection",
      enableNeedsProbe: "Probe it successfully to enable it.",
      enableNeedsPerimeter: "Its account is not admitted by the perimeter yet.",
      create: {
        title: "Register a connection",
        body: "ESI generates the external ID. Next, the account's administrator creates the role from the setup shown.",
        label: "Name",
        id: "Connection ID",
        idHelp: "Lowercase letters, digits and hyphens, for example claude5-account.",
        account: "Account",
        retention: "Declared retention",
        retentionHelp:
          "The Bedrock data retention mode set in that account. You attest it; ESI cannot read it.",
        submit: "Register",
      },
      setup: {
        title: "Role setup for {{id}}",
        body: "Give this to the model account's administrator. The role trusts this deployment only, with this external ID.",
        role: "Role name",
        externalId: "External ID",
        sourceIdentity: "Source identity",
        trust: "Trust policy",
        permissions: "Permissions policy",
        copy: "Copy",
        copied: "Copied",
        next: "Once the role exists, probe the connection, then enable it.",
      },
      accounts: {
        title: "Account allowlist",
        lead: "Connections may only point at these accounts. Adding one widens the network perimeter: it needs your authenticator code and every operator is told.",
        add: "Add account",
        empty: "No account yet: only this deployment's account serves models.",
        applied: "Admitted",
        pending: "Waiting for the perimeter",
        remove: "Remove",
        removeOf: "Remove account {{account}}",
        capacity: "{{used}} of {{max}} accounts",
        stepUp:
          "Adding an account widens the perimeter. Confirm with your password and authenticator code.",
        bindStepUp:
          "Binding an account changes who may call models in it. Confirm with your password and authenticator code.",
        boundInstance: "Instance",
        boundTenant: "Tenant: {{name}}",
        unbound: "Unbound: tenant {{name}} was deleted",
        bindToTenant: "Bind to tenant",
        bindToTenantOf: "Bind account {{account}} to a tenant",
        changeBinding: "Change binding",
        changeBindingOf: "Change the binding of account {{account}}",
      },
      binding: {
        title: "Bind account {{account}}",
        body: "An account serves the instance's connections, or the connections of one tenant only. Every operator receives an email.",
        for: "Serves",
        help: "A tenant's account is never used by the instance or by another tenant.",
        instance: "The instance",
        tenants: "One tenant",
        disables_one:
          "The connection registered in this account is disabled, kept, and does not carry over.",
        disables_other:
          "The {{count}} connections registered in this account are disabled, kept, and do not carry over.",
        submit: "Bind account",
      },
      addAccount: {
        title: "Add an AWS account",
        body: "Its esi-model-access role becomes reachable from this deployment.",
        account: "AWS account ID",
        label: "Name",
        widens:
          "Every operator receives an email. Removing the account later stops its connections at once.",
        submit: "Add account",
      },
      refused: {
        probe: "Probe the connection successfully before enabling it.",
        logging: "This account logs invocations: confirm to enable it.",
        sameAccount: "This is the deployment's own account, which is always available.",
        capacity: "The allowlist is full for this deployment.",
        providerOff:
          "Bedrock is off for the instance's connections: turn it on under Providers first.",
        tenantBound: "This account is bound to a tenant: the instance cannot use it.",
        sameBinding: "The account already serves that.",
        tenantUnavailable: "This tenant is being deleted: it cannot hold an account.",
      },
    },
    plans: {
      title: "Plans",
      lead: "Limits grouped by plan. Each tenant has one plan; operators can override a limit for one tenant.",
      create: "New plan",
      createTitle: "New plan",
      editTitle: "Edit {{name}}",
      dialogBody: "Editing a plan changes the limits of every tenant on it without an override.",
      name: "Name",
      mode: "AI spend limit",
      modes: { soft: "Warn only", hard: "Refuse new questions" },
      tenants: "Tenants",
      default: "Default",
      isDefault: "New tenants get this plan.",
      makeDefault: "Give this plan to new tenants",
      emptyIsUnlimited: "Leave a limit empty for unlimited.",
      edit: "Edit",
      editOf: "Edit {{name}}",
      delete: "Delete plan",
      inUse_one: "{{count}} tenant uses this plan: move it to another plan first.",
      inUse_other: "{{count}} tenants use this plan: move them to another plan first.",
    },
    policies: {
      title: "Instance policies",
      lead: "Values every tenant inherits. A locked value cannot be changed by any tenant or entity; the deployment may fix some values.",
      reset: "Back to default",
      lockedEverywhere: "Locked for every tenant",
      changedAt: "Changed {{when}}",
      navLabel: "Policy pages",
      setCount_one: "{{count}} set for this instance",
      setCount_other: "{{count}} set for this instance",
      groups: {
        general:
          "How answers use sources, conversations outside projects and their artefacts, and who may create and join projects.",
        security: "The second factor native accounts need to sign in.",
        models: "Where models run, the efforts people may use and how they pick a model.",
        knowledge:
          "Declassification, how scans and images are read, wiki drafting and what the assistant remembers.",
        limits: "The largest file a project accepts and how many pages one scan may read.",
        integrations:
          "Web search and its limits, and what happens to outbound tool calls after reading classified content.",
        clients:
          "Access from MCP clients, what they may save and receive, and the tool groups they may use.",
      },
      summary: {
        setHere: "Set for this instance:",
        locked: "Locked for every tenant:",
        fixed: "Fixed by the deployment:",
        allDefault_one: "Nothing is set for this instance: this policy uses the ESI default.",
        allDefault_other:
          "Nothing is set for this instance: all {{count}} policies use the ESI defaults.",
      },
      marks: { setHere: "Set for this instance", fixed: "Fixed for this deployment" },
      search: { label: "Search policies", placeholder: "Name or topic, e.g. memory" },
      keys: {
        triageAlert: {
          label: "Triage assistant spend alert",
          help: "Daily spend of the feedback triage assistant, in USD, that alerts the operators once a day. Passes continue past it.",
        },
        triageCap: {
          label: "Triage assistant daily cap",
          help: "Optional. Once the day's triage spend reaches it, the assistant stops until the next UTC day. Empty: no cap.",
        },
        artefacts: {
          label: "Artefacts",
          help: "The assistant may create and revise documents, code, diagrams and tables kept beside the conversation.",
        },
      },
    },
    support: {
      title: "Support access",
      lead: "Ask a tenant for time-limited, read-only access to its administration. Its admins decide; every request you make is in its audit log.",
      caption: "Your support requests",
      request: "Request support access",
      requestTitle: "Request support access",
      requestBody:
        "The tenant's admins see the reason and the duration, and may approve for less time or refuse.",
      tenant: "Tenant",
      pickTenant: "Choose a tenant",
      reasonHint: "A ticket number and what you need to look at.",
      duration: "Duration (hours)",
      durationHint: "72 hours at most.",
      send: "Send request",
      none: "No support requests",
      noneHint: "Request access from a tenant's page or here.",
      withContent: "Projects opened by the tenant",
      open: "Open {{tenant}}",
      banner: "Support access to {{tenant}} until {{until}} · read-only, audited",
      bannerEnded: "Support access to {{tenant}} has ended",
      bannerLabel: "Support access",
      leave: "Leave",
      ended:
        "This grant no longer gives access: it ended, was revoked, or the tenant is not active.",
      backofficeTitle: "Tenant administration",
      backofficeLead:
        "Read-only, as the tenant's admins see it. Every page you open is recorded in its audit log.",
    },
    feedback: {
      title: "Feedback",
      lead: "Bugs and ideas users sent from the app. Opening a report is recorded in the instance audit log.",
      filters: "Filter feedback",
      status: "Status",
      type: "Type",
      tenant: "Tenant",
      build: "Build",
      any: "Any",
      anyTenant: "Any tenant",
      unknownTenant: "A tenant not in the list",
      groupFilter: "Group",
      unsorted: "Unsorted",
      unknownGroup: "A group not in the list",
      groupFilterRefused: "Not available yet: refresh in a few minutes.",
      selectAll_one: "Select the {{count}} report shown",
      selectAll_other: "Select the {{count}} reports shown",
      selectFirst_one: "Select the first report",
      selectFirst_other: "Select the first {{count}} reports",
      selected_one: "{{count}} report selected",
      selected_other: "{{count}} reports selected",
      selectRow: "Select: {{title}}",
      addSelected: "Add the selection to a group",
      steps: "Other reports in this list",
      previous: "Previous",
      next: "Next",
      previousKey: "Previous report ([)",
      nextKey: "Next report (])",
      none: "No feedback yet",
      noneHint: "Reports users send from the app appear here.",
      noMatch: "No feedback matches",
      noMatchHint: "Change or clear the filters.",
      clear: "Clear filters",
      older: "Show older",
      rowTitle: "{{type}} from {{tenant}}",
      origin: {
        menu: "From the menu",
        agent_requested: "Asked the assistant",
        agent_suggested: "Suggested by the assistant",
        answer_action: "From an answer",
      },
      assistant: "Assistant",
      assistantNone: "None: sent without the assistant's context",
      noContext: "No context sent",
      description: "Description",
      context: "Context",
      notSent: "Not sent",
      tabs: "Feedback views",
      tabReports: "Reports",
      tabGroups: "Groups",
      assist: {
        title: "Assistant suggestion",
        thinking: "The assistant is reading this report…",
        again: "Suggest again",
        group: "Group",
        groupMeta_one: "{{type}} · {{count}} report",
        groupMeta_other: "{{type}} · {{count}} reports",
        newGroup: "new group",
        noGroup: "No group fits: choose one below",
        duplicates: "Duplicates",
        duplicate: "{{type}} · {{tenant}} · {{date}}",
        noDuplicates: "None found",
        type: "Type",
        severity: "Severity",
        area: "Area",
        why: "Why",
        severities: { low: "Low", medium: "Medium", high: "High" },
        accept: "Accept",
        change: "Change…",
        changeTitle: "Change the suggestion",
        reject: "Reject",
        acceptChanges: "Accept with changes",
        cancel: "Cancel",
        newGroupOption: "New group",
        newGroupTitle: "New group title",
        footnote:
          "Runs on ESI's own models in the EU, on the instance's spend. Nothing changes until you accept.",
        optedOut: "Triaged by hand: this organisation keeps its reports out of the assistant.",
        rejected: "No suggestion for this report. Triage it by hand below.",
        decidedElsewhere:
          "This suggestion was already decided or suggested again. Reload the report.",
        budgetReached: "Daily triage budget reached. The assistant is back tomorrow (UTC).",
        suggested: "Suggested: {{title}}",
        suggestedNew: "Suggested: {{title}} (new group)",
        newGroupChip: "New group",
        batch: "Suggest for Unsorted",
        batchHint:
          "The assistant can suggest a group, severity and area for up to 50 Unsorted reports of this filter.",
        batchRunning: "Suggesting… {{done}} reports done",
        batchDone_one: "{{count}} new suggestion over {{total}} Unsorted reports.",
        batchDone_other: "{{count}} new suggestions over {{total}} Unsorted reports.",
        batchNone: "Every Unsorted report of this filter already has a suggestion.",
        spend: "Triage assistant today: {{usd}} · alerts at {{alert}}",
        spendCap: "stops at {{cap}}",
        draft: "Draft with the assistant",
        drafting: "Drafting…",
        draftUsed_one: "Drafted from {{count}} report.",
        draftUsed_other: "Drafted from {{count}} reports.",
        draftLeftOut_one:
          "{{count}} report was left out (its organisation keeps reports out of the assistant).",
        draftLeftOut_other:
          "{{count}} reports were left out (their organisations keep reports out of the assistant).",
        draftEdit:
          "Edit before saving. The issue preview still refuses any text copied from a report.",
        draftNoReports:
          "Every report of this group comes from an organisation that keeps its reports out of the assistant.",
        discardDraft: "Discard draft",
        notSet: "Not set",
        severityHint: "How serious it is; the issue carries it as a severity:: label.",
        severityHelp: {
          low: "Cosmetic, or a workaround exists. The issue gets severity::low.",
          medium: "A feature misbehaves for some users. The issue gets severity::medium.",
          high: "Data, security, or a core flow broken. The issue gets severity::high.",
        },
        areaHint: "The part of the product, from the backlog's area labels.",
      },
      triage: {
        newGroup: "New group",
        guide:
          "A group collects reports about the same bug or idea. It becomes one GitLab issue, and every reporter in it sees the group's status.",
        newGroupHint:
          "A group collects reports about the same bug or idea and becomes one GitLab issue. You can add reports afterwards.",
        groupsNone: "No groups yet",
        groupsNoneHint:
          "Open a report and add it to a group from its Group section, or create a group here.",
        counts_one: "{{count}} report",
        counts_other: "{{count}} reports",
        tenants_one: "{{count}} tenant",
        tenants_other: "{{count}} tenants",
        issue: "Issue #{{number}}",
        titleLabel: "Title",
        titleHint: "Becomes the GitLab issue's title. Your own words, never a reporter's.",
        summaryLabel: "Summary",
        summaryHint:
          "Written by you for the GitLab issue. Never paste a reporter's words, names or addresses.",
        typeLabel: "Type",
        typeHint: "Bug or idea. It sets the issue's kind:: label.",
        statusLabel: "Status",
        statusNotified:
          "Reporters are told in their notifications when you save a new status, once per status.",
        reasonLabel: "Reason for reporters",
        reasonHint:
          "Optional. Shown to this group's reporters with “Not planned”, in every tenant. Your own words: never a reporter's text, a name, an address or an identifier.",
        reasonFindingsTitle: "Edit the reason before saving",
        reasonFindings: {
          email: "An e-mail address",
          uuid: "An identifier",
          quote: "A quote over 80 characters",
          copied: "Words copied from a report",
          mention: "A mention",
          tenant: "The name of a tenant with a report in this group",
        },
        statusHelp: {
          new: "Every reporter in this group reads “New”: nobody has looked at it yet.",
          triaged: "Every reporter in this group reads “Reviewed”: the ESI team has looked at it.",
          planned: "Every reporter in this group reads “Planned”: a change is scheduled.",
          fixed:
            "Every reporter in this group reads “Fixed”, then “Fixed in version …” on its own once a release closes the GitLab issue. Closes the group.",
          not_planned: "Every reporter in this group reads “Not planned”. Closes the group.",
          duplicate:
            "Every reporter in this group reads “Merged with a similar report”: it is tracked elsewhere. Closes the group.",
        },
        save: "Save",
        saved: "Saved",
        create: "Create group",
        cancel: "Cancel",
        details: "Details",
        detailsHint:
          "The title, summary and type go into this group's GitLab issue. The status is what its reporters see.",
        reports: "Reports",
        reportsNone:
          "No reports yet. Open a report and add it to this group from its Group section.",
        goToReports: "Go to reports",
        removeReport: "Remove from group",
        suggested: "Suggested reports",
        suggestedHint: "Reports outside any group whose recent errors carry a linked fingerprint.",
        add: "Add to group",
        fingerprints: "Error fingerprints",
        fingerprintsHint:
          "A fingerprint is a 16-character code naming one kind of error in the app: the fingerprint of a client.error log line. Link one, and later reports carrying that error are suggested here. A fingerprint belongs to one group.",
        fingerprintsNone: "No fingerprint linked.",
        fingerprintLabel: "Fingerprint",
        link: "Link",
        unlink: "Unlink {{fingerprint}}",
        gitlab: "GitLab issue",
        gitlabHint:
          "The issue holds your title and summary, the type, builds, routes, fingerprints and counts. Never a report's text, a tenant or a person.",
        notConfigured: "GitLab is not configured",
        notConfiguredHint:
          "No GitLab token is stored for this instance yet. Issues can be created once it is (infra runbook).",
        unavailable: "GitLab's token cannot be read right now. Try again later.",
        previewChecks:
          "Preview issue shows exactly what will be sent, and refuses an e-mail address, an identifier, a quote over 80 characters, words copied from a report or an @mention in your title or summary.",
        preview: "Preview issue",
        previewTitle: "Create a GitLab issue",
        previewLead: "This is exactly what will be sent to GitLab.",
        previewIssueTitle: "Title",
        labels: "Labels",
        body: "Body",
        findingsTitle: "Edit the title or summary before creating the issue",
        findings: {
          email: "An e-mail address: {{excerpt}}",
          uuid: "An identifier: {{excerpt}}",
          quote: "A quote over 80 characters: {{excerpt}}",
          copied: "Words copied from a report: {{excerpt}}",
          mention: "A mention, which would notify someone: {{excerpt}}",
          tenant: "A tenant's name: {{excerpt}}",
        },
        fields: { title: "Title", summary: "Summary", reason: "Reason" },
        confirm: "Create issue",
        trackerFailed:
          "GitLab did not create the issue{{status}}. Nothing was saved. Create it again when GitLab answers.",
        openIssue: "Open issue #{{number}} in GitLab",
        group: "Group",
        notGrouped: "Not in a group",
        notGroupedHint:
          "Add this report to the group of the same bug or idea, so it gets that group's status and GitLab issue.",
        inGroup: "In group",
        suggestion: "Same error as {{title}}",
        reportFingerprints: "Error fingerprints",
        linkedHere: "Linked to this group",
        linkedTo: "Linked to",
        notLinked: "Not linked to a group",
        linkHere: "Link to this group",
        linkHereLabel: "Link {{fingerprint}} to this group",
        addTo: "Add to a group",
        moveToOther: "Move to another group",
        chooseGroup: "Choose an open group",
        noOpenGroup: "No open group yet",
        noOtherOpenGroup: "No other open group",
        addShort: "Add",
        move: "Move",
        ungroup: "Remove from group",
        removeHint: "Removing it sets the reporter's status back to New.",
      },
    },
    reports: {
      title: "Usage",
      lead: "Each tenant's use and cost over the last 30 days. Never an entity, project or person.",
      tenants: "Tenants",
      tenant: "Tenant",
      questions: "Questions",
      conversations: "Conversations",
      cost: "Estimated AI cost",
      mcpCost: "MCP cost",
      monthSpend: "Spend this month",
      storage: "Storage",
      unsupported: "Unsupported citations",
      failures: "Ingestion failures",
      costByTenant: "Estimated AI cost by tenant",
      costSummary: "{{total}} across the instance over the period.",
      syntheticTenant: "{{name}} (includes synthetic demo data)",
      perTenant: "By tenant",
      instanceCosts: "Instance costs",
      instanceLine: "Cost",
      today: "Today",
      thisMonth: "This month",
      last30Days: "Last 30 days",
      feedbackTriage: "Feedback triage (ESI)",
    },
    sessions: {
      lead: "Signed-in operator browsers. Ending one signs it out at its next request.",
      current: "This browser",
      unknownDevice: "Unknown device",
      unknownOperator: "unknown",
      meta: "Operator {{operator}} · last seen {{lastSeen}}",
      revoke: "Sign out",
    },
    refused: {
      seatLimit: "The tenant has no seats left.",
    },
  },
  plan: {
    title: "Plan and usage",
    hint: "Your plan's limits and this month's use.",
    lead: "Your organisation is on the {{name}} plan. ESI's operators set its limits.",
    adminsOnly: "Only tenant and entity admins see the plan.",
    thisMonth: "Use against limits ({{month}})",
    seats: "Active users",
    storage: "Stored documents",
    ocrPages: "OCR pages this month",
    modelSpend: "Estimated AI spend this month",
    unlimited: "Unlimited",
    warningTitle: "Close to a limit",
    warningBody: "80 % or more used: {{limits}}.",
    reachedTitle: "Limit reached",
    reachedBody: "New activity of this kind is refused until use goes down or the limit is raised.",
    hardSpend:
      "New questions are refused and new files wait (shown as paused) until next month or until the limit is raised. Paused work then resumes on its own.",
    modeSoft: "The AI spend limit only warns: questions keep working past it.",
    modeHard:
      "Past the AI spend limit, new questions are refused and file processing pauses until next month.",
    unknownCost_one: "{{count}} call of unknown cost this month is counted apart.",
    unknownCost_other: "{{count}} calls of unknown cost this month are counted apart.",
    synthetic: "Includes synthetic demo data",
    syntheticSpend:
      "{{amount}} of this month's AI spend is synthetic demo data. It is shown here, but only real spend can refuse questions or pause work.",
  },
  supportAccess: {
    title: "Support access",
    hint: "Approve or refuse ESI support's requests; see their use.",
    lead: "ESI's support can read your administration only when a tenant admin approves, for a limited time. Projects stay closed unless you open them. Every request they make is in your audit log.",
    adminsOnly: "Only tenant admins decide support access.",
    pending: "Pending requests",
    nonePending: "No pending request.",
    active: "Active access",
    noneActive: "No active access.",
    history: "History",
    noHistory: "No past access.",
    operator: "Requested by",
    operatorRef: "ESI support ({{ref}})",
    reason: "Reason",
    requested: "Requested",
    requestedFor_one: "{{when}}, for {{count}} hour",
    requestedFor_other: "{{when}}, for {{count}} hours",
    metadataOnly:
      "Approving gives read-only access to your administration (users, entities, settings, directory, audit), not to projects' content.",
    approveFor: "Approve for",
    hours_one: "{{count}} hour",
    hours_other: "{{count}} hours",
    approve: "Approve",
    refuse: "Refuse",
    until: "Until",
    content: "Projects opened",
    noContent: "None: administration only",
    uses: "Use",
    usesNone: "Not used yet",
    usesLast_one: "{{count}} request, last {{when}}",
    usesLast_other: "{{count}} requests, last {{when}}",
    editContent: "Choose projects",
    revoke: "Revoke now",
    contentTitle: "Open projects to support",
    contentBody:
      "Support reads the chosen projects as a reader, until the access ends. Documents restricted to named readers stay hidden.",
    contentNote: "Clear every project to close content again.",
    noProjects: "No entity project to open.",
    statusLabel: "Status",
    status: {
      requested: "Pending",
      active: "Active",
      refused: "Refused",
      revoked: "Revoked",
      expired: "Expired",
    },
  },
  totp: {
    title: "Two-step verification",
    lead: "Ask for a code from an authenticator app after your password.",
    required: "Your organisation requires two-step verification for your account.",
    on: "On",
    off: "Off",
    setUp: "Set up",
    remove: "Turn off",
    step1: "Scan the QR code with an authenticator app, or enter the setup key by hand.",
    step2: "Enter the 6-digit code the app shows to confirm.",
    qrLabel: "QR code for your authenticator app",
    key: "Setup key",
    showKey: "Show the setup key",
    copy: "Copy the key",
    copied: "Copied",
    code: "6-digit code",
    confirm: "Confirm",
    confirming: "Confirming…",
    enabled: "Two-step verification is on.",
    removed: "Two-step verification is off.",
    lastFactor: "Your organisation requires a second step: add a passkey before turning this off.",
  },
  password: {
    title: "Password",
    current: "Current password",
    new: "New password",
    rules: {
      length: "At least {{min}} characters",
      upper: "An uppercase letter",
      lower: "A lowercase letter",
      digit: "A digit",
      symbol: "A symbol",
    },
    tooShort: "The password is too short.",
    weak: "This password doesn't meet the requirements. Use upper and lower case, a digit and a symbol.",
    wrongCurrent: "The current password or code is wrong.",
    set: "Set password",
    change: "Change password",
    changed_one: "Password changed. {{count}} other session was signed out.",
    changed_other: "Password changed. {{count}} other sessions were signed out.",
  },
  connect: {
    documentTitle: "Allow access",
    loading: "Loading…",
    title: "Allow {{client}} to use ESI?",
    lead: "An application asks to act on your behalf. Check every line before you allow it.",
    client: "Application",
    firstParty: "ESI command-line tool",
    hostHint: "Published by {{host}}",
    redirect: "Sends you back to",
    resource: "Access",
    resources: {
      api: "ESI API",
      mcp: "ESI MCP server",
    },
    // What `/api` means, under its name (#174): the MCP controls do not apply there.
    resourceHints: {
      api: "Not limited by your organisation's MCP policy",
    },
    tenant: "Organisation",
    user: "Signed in as",
    permissions: {
      label: "It may",
      read: "Read your projects' knowledge, as you",
      everything: "Everything you can do in ESI, as you",
      write:
        "Create and edit in your projects as you; never delete, share, publish or change members",
      writeHint:
        "Notes, files, folders and wiki proposals. Saved files are marked as external content; wiki changes stay drafts until someone reviews and publishes them in ESI.",
    },
    warning: "Only allow this if you started the sign-in yourself, from this application.",
    allow: "Allow",
    allowing: "Allowing…",
    deny: "Deny",
    errors: {
      title: "This request cannot continue",
      expired:
        "This sign-in request has expired or was already used. Start again from the application.",
      otherSession:
        "This sign-in request belongs to another session. Start again from the application.",
    },
  },
  mcpServer: {
    lead: "Use your project knowledge from Claude Code, VS Code and other MCP clients: search it, read it, or ask a project's agent for a cited answer. They act as you, with your permissions, and only receive content your organisation allows to leave ESI.",
    url: "Server URL",
    copy: "Copy",
    copied: "Copied.",
    claudeCode: "Claude Code",
    vscode: "VS Code",
    vscodeHint: "Add an HTTP MCP server with this URL (MCP: Add Server).",
    signIn: "The client opens ESI's sign-in the first time. It then appears under Connected apps.",
    disabled: "Your organisation has turned off access from MCP clients.",
  },
  connectedApps: {
    title: "Connected apps",
    lead: "Desktop, command-line and MCP applications you allowed to use ESI on your behalf. Revoking one signs it out at once.",
    empty: "No application connected.",
    emptyOf: "{{name}} has no connected application.",
    connected: "Connected {{date}}",
    lastUsed: "Last used {{date}}",
    canWrite: "Can save notes and propose wiki drafts",
    revoke: "Revoke",
    revokeNamed: "Revoke {{client}}",
    revoked: "Access revoked.",
  },
  device: {
    documentTitle: "Connect a device",
    title: "Connect a device",
    lead: "Enter the code shown by the application you are signing in to.",
    codeLabel: "Code",
    continue: "Continue",
    checking: "Checking…",
    confirmLead:
      "An application on another device asks to act on your behalf. Check every line before you approve it.",
    code: "Code",
    origin: "Requested from",
    unknownCountry: "Unknown location",
    startedAt: "Started {{time}}",
    warning: "Only continue if you started this sign-in yourself, just now.",
    approve: "Approve",
    approvedTitle: "Device connected",
    approved: "{{client}} can now use ESI on your behalf.",
    deniedTitle: "Request denied",
    denied: "{{client}} did not get access.",
    closeHint: "You can close this page and go back to the application.",
    errors: {
      format: "Enter the 8 letters of the code, as shown by the application.",
      invalid:
        "This code is not valid or has expired. Check it, or start again from the application.",
      throttled: "Too many attempts. Wait a few minutes before you try again.",
      otherSession: "This code is being used in another session. Start again from the application.",
      deviceGrantDisabled: "Your organisation does not allow signing in with a device code.",
      clientNotAllowed: "Your organisation does not allow this application.",
      signIn: "Sign in again to continue.",
    },
  },
  link: {
    invalid: "This link is no longer valid.",
    passwordSet: "This account already has a password. Sign in, or reset your password.",
    expiredTitle: "This link is no longer valid",
    expiredLead:
      "Links work once and for a limited time. Enter your email and we'll send a new one if an account exists.",
    send: "Send a new link",
    sent: "If an account exists for this email, we've sent a link. Check your inbox.",
    sentTitle: "Check your email",
    sendAgain: "Send another link",
    backToSignIn: "Back to sign-in",
    invitation: {
      title: "Welcome to ESI",
      lead: "Choose a password to finish setting up your account.",
      joinTitle: "Join {{tenant}}",
      invitedBy: "{{name}} invited you. Choose a password to finish setting up your account.",
      requestTitle: "Get a new invitation link",
      requestLead:
        "Enter your work email. If an invitation is waiting for it, we'll send a new link.",
    },
    reset: {
      title: "Choose a new password",
      lead: "Your new password signs you out everywhere else.",
      requestTitle: "Reset your password",
      requestLead: "Enter your work email. If an account exists, we'll send a link to reset it.",
    },
  },
  // sso-and-esi-credentials (#419, D4): an ESI password or passkey next to the person's SSO.
  esiCredential: {
    title: "ESI sign-in",
    on: "On",
    off: "Off",
    refusedBadge: "Not allowed",
    addLead:
      "You sign in with your organisation's sign-in. You can also add an ESI password or passkey, to get in when it is unavailable.",
    heldLead:
      "You can sign in with your ESI password or passkey as well as your organisation's sign-in.",
    notAllowed:
      "Your domain's sign-in mode no longer allows ESI sign-in. Your password and passkeys are kept but refused until an admin allows it again.",
    add: "Add a password or passkey",
    sending: "Sending…",
    sent: "We sent a link to {{email}}. Open it within 72 hours to choose your password; you can add a passkey after.",
    removeEllipsis: "Remove ESI sign-in…",
    removeTitle: "Remove your ESI password and passkeys?",
    removeBody: "You keep signing in with your organisation's sign-in.",
    removeSignsOut: "This session used them, so it ends too.",
    remove: "Remove ESI sign-in",
    removing: "Removing…",
    removed: "ESI sign-in is removed.",
    refused: {
      throttled: "Too many sign-in links were sent. Try again later.",
      notAllowed: "Your domain's sign-in mode does not allow ESI sign-in.",
      conflict:
        "A link is already on its way, or another sign-in account uses this e-mail. Check your inbox, or ask an admin.",
    },
  },
  stepUp: {
    title: "Confirm it's you",
    lead: "For your security, enter your password again before this change.",
    orPassword: "or your password and authenticator code",
    confirm: "Confirm",
    federated:
      "Your organisation's sign-in checks who you are. Sign in again with it, then come back to this change.",
    signInAgain: "Sign in again",
  },
  memory: {
    proposal: {
      saveForMe: "Save for me",
      suggestProject: "Suggest for the project",
      suggesting: "Sending…",
      suggestedProject: "Sent to {{project}} for review.",
      addedProject: "Added to {{project}}'s memory.",
      viewProject: "View project memory",
      region: "Memory suggestion",
      suggested: "Remember this?",
      requested: "Save to memory?",
      preference: "Preference, applies in all your conversations",
      fact: "Fact, used only in {{project}}",
      factStandalone: "Fact, used in your conversations",
      thisProject: "this project",
      sources_one: "Uses {{count}} project source",
      sources_other: "Uses {{count}} project sources",
      unverified: "Unverified: from outside results",
      textLabel: "Memory text",
      save: "Save",
      saving: "Saving…",
      edit: "Edit",
      dismiss: "Dismiss",
      cancel: "Cancel",
      saved: "Saved to memory.",
      manage: "Manage memory",
      dismissed: "Not saved. This one won't be suggested again.",
      gone: "This suggestion is no longer available.",
      invalid: "Use one plain sentence of at most 300 characters, without links or formatting.",
      remaining: "{{count}} characters left",
    },
    project: {
      notAllowed: "Your role in this project does not include its memory.",
      lead: "Facts this project's team agreed on. The assistant uses them in every member's conversations here, as information, never as instructions.",
      leadManager:
        "Facts this project's team agreed on. The assistant uses them in every member's conversations here. Suggestions from contributors wait for you or another manager.",
      off: "Project memory is turned off for this project. Stored facts are not used; you can still review and delete them.",
      filter: "Show",
      agreed: "Agreed",
      queue: "Waiting for review",
      mine: "My suggestions",
      search: "Search project memory",
      empty: "No agreed facts yet. Members suggest them from answers in this project.",
      emptyQueue: "Nothing waiting for review.",
      emptyMine: "You have no suggestion waiting for review.",
      noMatch: "No project memory matches this search.",
      hidden: "Hidden: relies on sources you can't read",
      someone: "a former member",
      addedBy: "Added by {{author}}",
      suggestedApproved: "Suggested by {{author}}, approved by {{decider}}",
      suggestedBy: "Suggested by {{author}}",
      yourSuggestion: "Your suggestion",
      expires: "Expires {{date}}",
      restricted_one: "{{count}} listed member can't read its sources",
      restricted_other: "{{count}} listed members can't read its sources",
      approve: "Approve",
      editApprove: "Edit and approve",
      reject: "Reject",
      rejectHidden: "Reject hidden suggestion",
      withdraw: "Withdraw",
      approved: "Approved. Members get it from their next message.",
      rejected: "Suggestion rejected.",
      withdrawn: "Suggestion withdrawn.",
      deleted: "Deleted from project memory.",
      deleteHidden: "Delete hidden project memory",
      deleteTitle: "Delete this fact for everyone?",
      deleteEffects: {
        everyone: "The assistant stops using it in every member's next message in this project.",
        audited: "The deletion is recorded in the project activity, without its text.",
      },
      confirmDelete: "Delete for everyone",
      gone: "This memory is no longer available.",
      tabPending_one: "{{count}} waiting for review",
      tabPending_other: "{{count}} waiting for review",
    },
    panel: {
      export: "Export",
      exporting: "Preparing your file…",
      exported: "Downloaded {{file}}.",
      exportFailed: "Your memories could not be exported. Try again.",
      suggest: "Suggest for the project",
      suggestedProject: "Sent to the project for review.",
      queueFull:
        "This project already has too many suggestions waiting for review. Try again later.",
      duplicate: "The project already has this memory, agreed or waiting for review.",
      lead: "ESI only remembers what you save. Preferences apply in all your conversations; facts stay in the project they came from and follow its sources.",
      locked:
        "Memory is turned off by your organisation. You can still review and delete what you saved.",
      filter: "Show",
      preferences: "Preferences",
      facts: "Facts",
      search: "Search memories",
      add: "Add",
      addTitle: "Add a memory",
      addDescription:
        "Write one plain sentence about you or your work. ESI shows it to the assistant as information, never as instructions.",
      kind: "Kind",
      text: "Text",
      preferenceHint: "About you: role, language, tone, answer format. Used everywhere.",
      factHint: "About your work. Added here, it is used in all your conversations.",
      emptyPreferences:
        "No preferences yet. Write “remember …” in a conversation, or add one here.",
      emptyFacts: "No facts yet. ESI suggests them under answers; nothing is saved without you.",
      noMatch: "No memory matches this search.",
      noProject: "No project",
      unknownProject: "A project you can't open",
      lastUsed: "Used {{date}}",
      neverUsed: "Not used yet",
      expiring: "Deleted {{date}} unless used",
      unverified: "Unverified",
      hidden: "Hidden: relies on sources you can't read",
      actions: "Actions for this memory",
      edit: "Edit",
      delete: "Delete",
      deleteHidden: "Delete hidden memory",
      deleteAll: "Delete all memories",
      deleteAllTitle: "Delete all your memories?",
      deleteAllEffects: {
        everything: "Every preference and fact you saved is deleted, hidden ones included.",
        nextTurn: "The assistant stops using them from your next message.",
        kept: "Your conversations and files are not affected.",
      },
      confirmDeleteAll: "Delete all",
      deleting: "Deleting…",
      saved: "Memory saved.",
      deleted: "Memory deleted.",
      allDeleted_one: "{{count}} memory deleted.",
      allDeleted_other: "{{count}} memories deleted.",
      kindLocked: "This fact comes from a project or its sources, so it can't become a preference.",
    },
  },
  accountSettings: {
    title: "Account settings",
    lead: "Your profile, preferences, memory, sign-in security and active sessions.",
    myFeedback: "My feedback",
    suggestFeedback: "Suggest feedback",
    suggestFeedbackHelp:
      "After a system failure in a conversation, the assistant may offer a drafted report. Nothing is sent unless you click Send. Asking it to report a problem always works.",
    sections: {
      profile: "Profile",
      preferences: "Preferences",
      security: "Sign-in and security",
      sessions: "Active sessions",
      memory: "Memory",
      connectedApps: "Connected apps",
      mcp: "MCP server",
    },
    federatedSecurity:
      "Your organisation's sign-in manages your password and two-step verification.",
    federatedSecurityBeside:
      "Your organisation's sign-in manages its own password and two-step verification. ESI sign-in, below, is separate.",
    profile: {
      name: "Display name",
      email: "Email",
      fromIdp: "From your organisation's sign-in",
    },
    saved: "Saved.",
    preferencesNotSaved: "Applied here, but not saved to your account. Try again later.",
  },
  passkeys: {
    title: "Passkeys",
    lead: "Sign in with your device's fingerprint, face or screen lock instead of a password.",
    unsupported: "This browser doesn't support passkeys.",
    reveal: "Confirm it's you to see your passkeys",
    none: "No passkeys yet.",
    unnamed: "Passkey",
    created: "Added {{date}}",
    removeNamed: "Remove {{name}}",
    add: "Add a passkey",
    added: "Passkey added.",
    removed: "{{name}} removed.",
    lastFactor:
      "Your organisation requires a second step: set up an authenticator app before removing your last passkey.",
    cancelled: "The passkey wasn't added. Try again.",
    removeTitle: "Remove {{name}}?",
    removeLead:
      "You won't be able to sign in with this passkey any more. Your other ways to sign in keep working.",
    removeConfirm: "Remove passkey",
    addConfirmed: "Add the passkey",
    addReady: "You're confirmed. Add the passkey to finish.",
  },
  sessions: {
    lead: "Browsers where you're signed in. Signing out a session doesn't affect desktop or MCP clients.",
    unknownDevice: "Unknown device",
    current: "This session",
    network: "Network {{network}}",
    signedIn: "Signed in {{date}}",
    lastSeen: "Last active {{date}}",
    signOut: "Sign out",
    signOutNamed: "Sign out {{device}}",
    signOutOthers: "Sign out other sessions",
    revoked_one: "{{count}} session signed out.",
    revoked_other: "{{count}} sessions signed out.",
  },
  projects: {
    title: "Projects",
    lead: "Each project holds its own knowledge, members and conversations.",
    empty: "You aren't in any project yet. Create one to start adding files.",
    create: "New project",
    createLead: "A project gathers files, a wiki and the people who work with them.",
    name: "Name",
    owner: "Owner",
    ownerLabel: { personal: "Personal", entity: "Your entity" },
    ownerHint: {
      personal: "Only the members you add can see it.",
      entity: "Owned by your entity; its admins manage it with you.",
    },
    visibilityLabel: "Who can see it",
    createSubmit: "Create project",
    creating: "Creating…",
    list: "Your projects",
  },
  project: {
    breadcrumb: "Breadcrumb",
    backToProjects: "Back to projects",
    personal: "Personal project",
    entityOwned: "Owned by {{entity}}",
    visibility: {
      private: "Members only",
      entity: "Members and the owning entity",
      entity_tree: "Members, the owning entity and its sub-entities",
    },
    yourRole: "Your role: {{role}}",
    frozen: "This project is read-only. Sources and members can be viewed but not changed.",
    frozenShort: "Read-only",
    conversation: "Conversation",
    knowledge: "Knowledge",
    openKnowledge: "Knowledge",
    members: "Members",
    tabs: { files: "Sources", wiki: "Wiki" },
    residencySource: {
      instance: "Set for this instance.",
      tenant: "Set by your organisation.",
      entity: "Set by the owning entity.",
      project: "Set for this project.",
    },
  },
  settings: {
    accessRequests: {
      lead: "Admins who govern this project asked for changes that would widen who reads it. Nothing changes until you approve.",
      requestedBy: "Asked by {{name}} · {{date}}",
      expires: "Expires {{date}}",
      group: "{{approved}} of {{size}} projects approved",
      reason: "Reason: {{reason}}",
      approve: "Approve",
      reject: "Reject",
      approveLabel: "Approve: {{change}}, asked by {{name}}",
      rejectLabel: "Reject: {{change}}, asked by {{name}}",
      own: "Your own request: another owner or manager decides.",
      approved: "Approved. The change applies once every project concerned approves.",
      applied: "Approved and applied.",
      rejected: "Rejected. Nothing changed.",
    },
    entry: "Settings",
    title: "Settings",
    lead: "How this project is set up and who can use it.",
    pages: {
      navLabel: "Settings pages",
      search: {
        label: "Search settings",
        placeholder: "Search, e.g. web search or OCR",
      },
      pendingCount_one: "{{count}} pending request",
      pendingCount_other: "{{count}} pending requests",
      setHereCount_one: "{{count}} set for this project",
      setHereCount_other: "{{count}} set for this project",
      summary: {
        setHere: "Set for this project:",
        lockedAbove: "Locked above:",
        allInherited: "Every setting here is inherited",
      },
      empty: "Nothing on this page can be set on this deployment.",
      general: {
        label: "General",
        purpose: "The project's name, description and who can open it.",
      },
      members: {
        label: "Members & access",
        purpose:
          "Who works in this project and with which role, and the access changes waiting for its approval.",
      },
      assistant: {
        label: "Assistant",
        purpose:
          "How the assistant answers here, which models it uses and what it remembers. Other settings are set for your organisation.",
      },
      knowledge: {
        label: "Knowledge",
        purpose:
          "How the project's files are read and classified, how wiki drafts are written, and what MCP clients may use.",
      },
      web: {
        label: "Web",
        purpose: "What the assistant may look up on the web for this project.",
      },
      danger: {
        label: "Danger zone",
        purpose: "Delete this project and everything in it.",
      },
    },
    sections: {
      general: "General",
      members: "Members and groups",
      behaviour: "Behaviour",
      requests: "Access requests",
    },
    readOnly: "Only the owner and project managers can change this.",
    save: "Save",
    saving: "Saving…",
    saved: "Saved.",
    cancel: "Cancel",
    general: {
      name: "Name",
      description: "Description",
      noDescription: "No description.",
      visibility: "Who can open it",
      personalOnly: "Personal projects are always private.",
      visibilityHint: {
        private: "Only people added to the project, or through a group, can open it.",
        entity: "Members of {{entity}} can also open it, as readers.",
        entity_tree: "Members of {{entity}} and its sub-entities can also open it, as readers.",
      },
      ownerFallback: "the owning entity",
      widenTitle: "Open the project to more people?",
      widen: {
        entity: "Members of {{entity}} will be able to open the project as readers.",
        entity_tree:
          "Members of {{entity}} and its sub-entities will be able to open the project as readers.",
      },
      widenConfirm: "Change who can open it",
    },
    members: {
      add: "Add",
      addTitle: "Add to the project",
      addLead: "Give a person, a group or an attribute rule a role in this project.",
      addKind: "What to add",
      filter: "Show",
      kinds: { all: "All", person: "People", group: "Groups", rule: "Rules" },
      email: "Email",
      emailHint: "They need an active account in your organisation.",
      person: {
        label: "Person",
        placeholder: "Name or email address",
        hint: "Type at least 2 letters of their name or email address.",
        searching: "Searching…",
        results: "People",
        found_one: "{{count}} person found. Use the arrow keys to choose.",
        found_other: "{{count}} people found. Use the arrow keys to choose.",
        member: "Already a member",
        none: "No one matches. Check the spelling, or type their full email address.",
        noneEmail: "No one matches. Add uses this email address as typed.",
        throttled: "Too many searches. Wait a moment, or type their full email address.",
        failed: "People could not be searched. You can still type their full email address.",
        clear: "Choose someone else",
      },
      search: { group: "Search groups", rule: "Search rules" },
      pick: { group: "Groups", rule: "Rules" },
      noMatch: { group: "No group matches.", rule: "No rule matches." },
      noneLeft: {
        group: "Every group already has a role here.",
        rule: "Every rule already has a role here.",
      },
      role: "Role",
      addSubmit: "Add",
      adding: "Adding…",
      memberSince: "Member since {{date}}",
      breakGlass: "Emergency access",
      rule: "Directory attribute rule",
      empty: {
        all: "No one has a role in this project yet.",
        person: "No person has a role in this project.",
        group: "No group has a role in this project.",
        rule: "No attribute rule has a role in this project.",
      },
      roleOf: "Role of {{name}}",
      removeTitle: "Remove {{name}} from the project?",
      removeBody: {
        person:
          "They lose access to the project, unless its visibility or one of its groups lets them in.",
        group:
          "The group's members lose the role it gives them, unless another access lets them in.",
        rule: "People matching the rule lose the role it gives them, unless another access lets them in.",
      },
      removeConfirm: { person: "Remove member", group: "Remove group", rule: "Remove rule" },
      actionsOf: "Actions for {{name}}",
      remove: "Remove from project",
      ownerOnly: "Only the owner can transfer ownership.",
      guestLifted:
        "If they belong to the owning entity, this project's visibility lets them open it as {{role}}.",
      transfer: "Transfer ownership",
      transferTitle: "Transfer ownership to {{name}}?",
      transferBody: "{{name}} becomes owner and you become project manager.",
      transferPoints: {
        owner: "Only the owner can delete the project or transfer ownership again.",
        you: "You keep managing members, settings and the wiki.",
      },
      transferConfirm: "Transfer ownership",
      transferring: "Transferring…",
      refused: {
        ownerSelf: "Transfer ownership to another member first.",
        ownerProtected: "Only the owner can change their own role, by transferring ownership.",
        ceiling: "Only a transfer of ownership makes someone the owner.",
        personalTransfer:
          "A personal project stays with the person who owns it. Move it to an entity to transfer it.",
        notDirect: "Ownership can only go to someone added to the project directly.",
        breakGlassOwner: "Ownership cannot go to emergency access.",
        outsideEntity:
          "Your organisation's settings only allow members of the owning entity in this project.",
        breakGlass: "Emergency access is always read-only (Reader).",
        notFound: "No active person with this email in your organisation.",
        already: "This person is already a member.",
        disabled: "This person's account is disabled.",
        frozen: "This project is read-only now.",
      },
    },
    behaviour: {
      lead: "Settings this project may adjust. Others are set for your organisation in the backoffice.",
      /** Under a tool group's switch: the disclosure listing its tools by name. */
      mcpGroupTools: "Tools in this group",
      modelsLink: "Labs, families, pins and model health for this project's entity",
      groups: {
        answers: "Answers",
        models: "Models",
        knowledge: "Files and classification",
        wiki: "Wiki",
        mcp: "External clients (MCP)",
        web: "Web access",
      },
      source: {
        project: "Set for this project",
        tenant: "Inherited from your organisation",
        entity: "Inherited from {{entity}}",
        entityUnknown: "Inherited from a parent entity",
        instance: "Default",
      },
      locked: {
        tenant: "Locked by your organisation",
        entity: "Locked by {{entity}}",
        entityUnknown: "Locked by a parent entity",
        instance: "Fixed for this deployment",
      },
      reset: "Reset to inherited",
      change: "Change",
      on: "On",
      off: "Off",
      narrowOnly: "Can only be made stricter than the inherited value.",
      models: { locked: "{{effort}} only (effort locked)" },
      keys: {
        idpGroupsMaxAge: {
          label: "Identity provider groups stay valid for",
          help: "Hours, 1 to 168. Access from groups your identity provider sends lapses this long after the person last signed in through it. Groups from SCIM or other directory sources never lapse.",
        },
        nativeClients: {
          label: "Connected applications",
          help: "Which desktop, command-line and MCP applications people may connect to their account. Every connection is audited and can be revoked.",
        },
        mcpEnabled: {
          label: "Access from MCP clients",
          help: "Lets people reach project knowledge from MCP clients such as Claude Code or VS Code, as themselves and with their own permissions. An entity or a project can only turn it off.",
        },
        mcpWriteTools: {
          label: "Saving from MCP clients",
          help: "On by default. Lets MCP clients that a person allowed to write save notes, upload and update files, organise folders (deleting only empty ones) and propose wiki drafts in projects, as that person and within their role. What they save is marked as external content; wiki changes stay drafts until someone publishes them in ESI. It takes effect only when on for the whole organisation; an entity or a project can then turn it off.",
        },
        mcpSelfPublish: {
          label: "Publishing your own MCP wiki changes",
          help: "Off by default: a wiki change proposed through someone's MCP client is published by another person, who reads it first. Turn it on for a project with a single contributor; each such publication is recorded in the activity log.",
        },
        mcpToolsKnowledge: {
          label: "Knowledge tools for MCP clients",
          help: "Lets MCP clients search knowledge, read documents, and work with notes, files and folders.",
        },
        mcpToolsWiki: {
          label: "Wiki tools for MCP clients",
          help: "Lets MCP clients read wiki pages and propose drafts.",
        },
        mcpToolsConversations: {
          label: "Conversation tools for MCP clients",
          help: "Lets MCP clients ask a project's assistant.",
        },
        mcpToolsTasks: {
          label: "Task tools for MCP clients",
          help: "Will let MCP clients work with tasks, once task tools exist.",
        },
        mcpToolsProjects: {
          label: "Project tools for MCP clients",
          help: "Lets MCP clients read a project's summary: its counts at a glance.",
        },
        mcpCeiling: {
          label: "Most sensitive content MCP clients may receive",
          help: "Content above this classification is never searched or returned to an MCP client, even when the person can read it in ESI. An entity or a project can only lower it.",
        },
        assistedTriage: {
          label: "Triage assistant for your feedback",
          help: "The ESI team's triage assistant may read your organisation's feedback reports to suggest how to group them, on ESI's own models in the EU. Off: your reports are triaged by hand only.",
        },
        feedbackEnabled: {
          label: "Feedback to the ESI team",
          help: "People can report a bug or suggest an idea to the ESI team from the app. They see exactly what is sent; nothing from their projects goes with it. Each send shows in the audit log, without its text.",
        },
        feedbackAgentSuggestions: {
          label: "Feedback suggestions from the assistant",
          help: "After a system failure in a conversation (a tool failing, a capability ESI lacks), the assistant may offer a drafted report. Nothing is sent unless the person clicks Send, and nothing from the conversation goes with it. Off: the assistant still drafts a report when someone asks for one.",
        },
        clientTelemetry: {
          label: "App health reports",
          help: "Browsers report the app's own errors, failed requests and page speed to ESI, so faults are fixed sooner. Never page content, names, addresses or identifiers. Nothing leaves ESI.",
        },
        jitProvisioning: {
          label: "Create accounts at first sign-in",
          help: "On: anyone your identity provider signs in from a verified domain gets an account the first time. Off: only people you invite or a directory sync creates can sign in; anyone else sees the usual sign-in failure.",
        },
        deviceGrant: {
          label: "Sign-in with a device code",
          help: "Lets people sign in applications without a browser (over SSH, in containers) by approving a code on another device.",
        },
        mfa: {
          label: "Second factor",
          help: "Native accounts sign in with an authenticator app or a passkey too, and so do ESI passwords on SSO domains (their sign-in mode is set on the Sign-in page). Sign-ins through an identity provider rely on it.",
        },
        webSearch: {
          label: "Web search",
          help: "On by default. The assistant may search the public web.",
        },
        modelsAllowed: {
          label: "Efforts",
          help: "How much effort answers may take: Fast, Balanced or Deep. One effort locks it.",
        },
        defaultEffort: {
          label: "Default effort",
          help: "The effort new conversations start with. It must be one the project allows.",
        },
        advancedPicker: {
          label: "Let people pick another family",
          help: "People may run an effort on another model family of your labs, never a specific version.",
        },
        turnMaxUsd: {
          label: "Most one answer may spend",
          help: "Estimated AI cost, in USD, after which a single answer stops. It bounds each answer, not each person's monthly total.",
        },
        sharePressureThresholdPct: {
          label: "Budget pressure threshold",
          help: "Percentage of the monthly AI budget used before individual shares apply. The budget remaining at the first crossing is shared equally by people who used AI this month. Each new active person reduces everyone’s allowance. Admins receive one organisation-wide audit notice per month.",
        },
        allowedModels: {
          label: "Allowed models",
          help: "Only these models may serve this project's answers and background work.",
        },
        allowPersonal: {
          label: "Personal projects",
          help: "People may create projects of their own.",
        },
        shareOutsideEntity: {
          label: "Members from other entities",
          help: "Projects may add members from outside the owning entity and its sub-entities.",
        },
        maxUploadMb: {
          label: "Largest file",
          help: "The biggest file a project accepts.",
        },
        classificationScheme: {
          label: "Classification levels",
          help: "Levels from least to most sensitive, for the whole organisation.",
        },
        ocrMonthlyPages: {
          label: "Monthly scan budget",
          help: "Pages read by OCR each month across the organisation, at most the operator's budget.",
        },
        sourcesOnly: {
          label: "Answer from project sources only",
          help: "The assistant declines instead of using general knowledge.",
        },
        memoryRetention: {
          label: "Memory retention",
          help: "Days a memory is kept when no answer uses it and no one edits it. Applies to personal and project memories, for the whole organisation.",
        },
        tasks: {
          label: "Tasks",
          help: "People may create and manage project tasks. Turning tasks off keeps existing tasks readable and lets owners and managers delete them.",
        },
        memoryPersonal: {
          label: "Personal memory",
          help: "The assistant may suggest memories under answers and use the ones each person saved. Nothing is saved without them; each person sees only their own.",
        },
        memoryProject: {
          label: "Project memory",
          help: "Members may suggest facts for the whole project; owners and managers approve them before the assistant uses them in the project's conversations. Turning it off keeps stored memories manageable.",
        },
        generatedSources: {
          label: "Generated sources in answers",
          help: "Answers may draw on files the assistant wrote and someone added to knowledge, and on answers saved from them. Off: those files stay listed and downloadable, but the assistant no longer reads or cites them, drafts no automatic summaries of them, and neither suggests nor uses memories drawn from its own output.",
        },
        attachments: {
          label: "Conversation attachments",
          help: "People may attach working files to a conversation. Turning uploads off keeps existing readable files and permitted downloads available.",
        },
        artefacts: {
          label: "Artefacts",
          help: "The assistant may create and revise documents, code, diagrams and tables kept beside the conversation. Turning it off keeps existing artefacts readable.",
        },
        thinkingSummary: {
          label: "Thinking summaries",
          help: "Each step the assistant thinks through gets a one-line summary in its answer's steps, written by ESI and shown only to people who can read the answer. The model's own reasoning is never stored or shown. Off: the step shows its duration only.",
        },
        standalone: {
          label: "Conversations outside a project",
          help: "People may start conversations with no project and no project knowledge, private to them. Settings follow each person's current entity; usage stays with the entity where it started.",
        },
        residency: {
          label: "Where models run",
          help: "Global: global models, with EU fallback. Data is always stored in the EU.",
        },
        defaultClassification: {
          label: "Default classification",
          help: "Given to new files unless the uploader picks another.",
        },
        ocr: {
          label: "Read scanned files",
          help: "Scanned PDFs and images are read with a vision model.",
        },
        imageCaptions: {
          label: "Describe images",
          help: "Uploaded images get a short description that answers can cite.",
        },
        ocrMaxPages: {
          label: "Most pages read in one scan",
          help: "Longer scans are not read automatically.",
        },
        declassification: {
          label: "Owner and managers may declassify",
          help: "The owner and project managers may publish derived content to readers its sources would exclude.",
        },
        guidelines: {
          label: "Wiki writing guidelines",
          help: "How drafts are written: language, page types and structure.",
        },
        reviewAfterDays: {
          label: "Review wiki pages after",
          help: "A page reads “May be stale” once this many days pass since it was last confirmed as accurate, or since it was published. Off by default.",
        },
        placement: {
          label: "Where new pages start",
          help: "The Wiki folder each type of page starts in when the assistant, a file summary or an MCP client proposes it. A missing folder means the Wiki root; reviewers can still choose another folder.",
        },
        autoDraftSummaries: {
          label: "Draft a summary of each new file",
          help: "Each ready file gets a summary draft in the wiki review queue.",
        },
        wikiSuggestions: {
          label: "Wiki suggestions",
          help: "The assistant may suggest a wiki page under a cited answer. Nothing is drafted until the user chooses Draft it, and a person reviews it before it is published. Saving an answer to the wiki is not affected.",
        },
        wikiTrust: {
          label: "Who may publish wiki changes",
          help: "Who may approve and publish a change set: the person it was made for, another contributor, or a manager. A project can only make it stricter. Who can read each page does not change.",
        },
        webDomains: {
          label: "Web search domains",
          help: "Results only from the allowed domains, never from the blocked ones. A domain covers its subdomains.",
        },
        webMaxAge: {
          label: "Oldest web result",
          help: "Results published longer ago are left out, and so are results without a date.",
        },
        webProvider: {
          label: "Web search service",
          help: "The service that runs the assistant's web searches.",
        },
        webFetch: {
          label: "Reading web pages",
          help: "On by default. The assistant may read a public web page: one the user gave, a search result or a link of a page it read. An address it composes itself is shown for approval first.",
        },
        webFetchApproval: {
          label: "Approval before reading a page",
          help: "Addresses the assistant composed itself always need approval. Choose whether every page does too.",
        },
        webFetchDomains: {
          label: "Web page domains",
          help: "Pages only from the allowed domains, never from the blocked ones, redirects included. A domain covers its subdomains.",
        },
        egress: {
          label: "Outbound calls after reading classified content",
          help: "What happens to the assistant's calls to outside tools once a conversation has read files of each audience.",
        },
        webFetchProvider: {
          label: "Page reading service",
          help: "The service that reads web pages for the assistant. It has no access to your organisation's data.",
        },
        codeExecution: {
          label: "Code execution",
          help: "On by default. The assistant may run short programs for calculations and data analysis in an isolated sandbox in your data's Region, with no network and no access to your organisation's systems. Each answer gets its own sandbox, deleted when the answer ends.",
        },
        codeExecutionProvider: {
          label: "Code sandbox service",
          help: "The service that runs the assistant's programs. It has no network and no access to your organisation's data beyond the code it is sent.",
        },
        codeEgress: {
          label: "Code runs after reading classified content",
          help: "What happens to the assistant's code runs once a conversation has read files of each audience. Nothing leaves the sandbox, so this can be looser than for outbound calls.",
        },
      },
      wikiTrust: {
        requester: "The person it was made for, or any contributor",
        peer: "Someone other than its author",
        manager: "A manager or the owner",
        fewPublishers:
          "Fewer than two members are named in this project with a role that may publish (groups and rules not counted): with this level, nobody could publish their own changes.",
      },
      webFetchApproval: {
        provenance: "Only for addresses the assistant composed",
        always: "For every page",
      },
      residency: { eu: "EU only", global: "Global models, with EU fallback" },
      web: {
        include: "Only these domains",
        includeHint: "Empty: any domain.",
        exclude: "Never these domains",
        add: "Add",
        addTo: "Add to “{{list}}”",
        remove: "Remove {{domain}}",
        placeholder: "example.com",
        none: "None",
        anyDomain: "Any domain",
        only: "Only {{domains}}",
        never: "never {{domains}}",
        neverStart: "Never {{domains}}",
        andMore: "{{list}} and {{count}} more",
        invalid:
          "“{{domain}}” isn't a domain. Write a host name such as example.com, without https:// or a path.",
        duplicate: "{{domain}} is already in the list.",
        tooMany: "At most {{max}} domains per list.",
        inherited: "Inherited: {{value}}",
        widens:
          "This would allow more than the project inherits. Keep every inherited block, and allowed domains within the inherited ones.",
        noLimit: "No limit",
        days_one: "{{count}} day",
        days_other: "{{count}} days",
      },
      refused: {
        invalidDomain: "“{{domain}}” isn't a valid domain: write a host name such as example.com.",
        narrowOnly: "This project can only make this setting stricter than the value it inherits.",
        locked: "This setting is locked at a higher level.",
      },
      pages: "{{count}} pages",
      placement: {
        entry: "{{type}}: {{path}}",
        none: "Wiki root for every type",
        root: "Wiki root",
        missing: "{{path}} (not in this project)",
        hint: "Only existing folders are used: nothing is created.",
        pathPlaceholder: "Sources / Summaries",
        pathsHint:
          "Folder names from the Wiki root, separated by /. A project without that folder uses its Wiki root.",
      },
      guidelines: {
        language: "Language",
        languageHint: "Empty: the language of the sources.",
        sourcesLanguage: "the sources' language",
        pageTypes: "Page types",
        structure: "Structure",
        structureHint: "Sections, tone or anything drafts should follow.",
        summary: "Language: {{language}} · {{types}}",
        withStructure: "structure set",
      },
    },
    danger: {
      lead: "Deleting the project can't be undone.",
      delete: "Delete project",
      title: "Delete “{{name}}”?",
      body: "The project, its conversations, files and wiki become unreachable at once and are purged within 24 hours. This can't be undone.",
      typeName: "Type “{{name}}” to confirm",
      confirm: "Delete project",
      deleting: "Deleting…",
      deleted: "“{{name}}” was deleted.",
    },
  },
  settingsControls: {
    saving: "Saving…",
    saved: "Saved",
    on: "On",
    off: "Off",
    units: { hours: "hours", days: "days", mb: "MB", pages: "pages", usd: "USD", percent: "%" },
    range: "Enter a whole number from {{min}} to {{max}}.",
    rangeFrom: "Enter a whole number of at least {{min}}.",
    rangeHint: "{{min}} to {{max}} {{unit}}",
    rangeHintFrom: "At least {{min}} {{unit}}",
    segments: {
      optional: "Optional",
      required: "Required",
      euOnly: "EU only",
      global: "Global",
      fast: "Fast",
      balanced: "Balanced",
      deep: "Deep",
      composedOnly: "Composed only",
      everyPage: "Every page",
      allowed: "Allowed",
      asksFirst: "Asks first",
      blocked: "Blocked",
    },
    nullable: {
      limit: "Limit",
      ownBudget: "Own budget",
      off: "Off",
      noLimit: "No limit",
      operator: "Operator's budget",
    },
    lock: {
      lock: "Lock for levels below",
      unlock: "Unlock",
      lockName: "Lock for levels below: {{label}}",
      unlockName: "Unlock: {{label}}",
    },
    lockEveryTenant: {
      lock: "Lock for every tenant",
      unlock: "Unlock",
      lockName: "Lock for every tenant: {{label}}",
      unlockName: "Unlock for every tenant: {{label}}",
    },
    editors: {
      levels: "Edit levels",
      domains: "Edit domains",
      guidelines: "Edit guidelines",
      placement: "Edit placement",
      applications: "Edit applications",
      models: "Edit models",
    },
    editorName: "{{action}}: {{label}}",
    confirm: {
      group: "Confirm: {{label}}",
      turnOn: "Turn on",
      keepOff: "Keep off",
      allow: "Allow",
      keep: "Keep {{value}}",
      save: "Save",
      keepEditing: "Keep editing",
    },
    projectLoosen: {
      residency:
        "Allowing global models lets this project's answers be served outside the EU again. Data stays stored in the EU.",
      tasks: "Turning this on lets members create and manage tasks in this project again.",
      memoryPersonal:
        "Turning this on lets the assistant suggest and use personal memories in this project again.",
      memoryProject: "Turning this on lets members suggest facts for the whole project again.",
      attachments:
        "Turning this on lets people attach files to this project's conversations again.",
      artefacts:
        "Turning this on lets the assistant create documents, code and tables beside this project's conversations again.",
      thinkingSummary:
        "Turning this on shows a short summary of each step the assistant thought through in this project's answers again.",
      modelsAllowed: "Adding an effort lets this project's answers use it again.",
      advancedPicker:
        "Turning this on lets people pick another model family in this project again.",
      allowedModels: "Adding models lets them serve this project's answers again.",
      ocrMaxPages: "More pages let OCR read longer scans in this project.",
      declassification:
        "Turning this on lets people lower the classification of this project's files again.",
      generatedSources:
        "Turning this on lets answers in this project draw on files the assistant wrote again.",
      wikiSuggestions:
        "Turning this on lets the assistant suggest wiki pages under this project's answers again.",
      wikiTrust: "A lower level lets more people approve and publish this project's wiki changes.",
      mcpWriteTools:
        "Turning this on lets MCP clients create and change content in this project again, as the person.",
      mcpSelfPublish:
        "Turning this on lets MCP clients publish this project's wiki pages without another person's review.",
      mcpTools: "Turning this on gives MCP clients this group's tools in this project again.",
      webSearch:
        "Turning this on lets the assistant search the web in this project's conversations again.",
      webFetch:
        "Turning this on lets the assistant read public web pages in this project's conversations again.",
      webFetchApproval:
        "Only addresses the assistant composed will ask first: it reads other pages in this project without approval.",
      webMaxAge: "Older web results let the assistant cite outdated pages in this project.",
      webDomains: "These lists let web search reach domains this project kept it from.",
      webFetchDomains:
        "These lists let the assistant read pages on domains this project kept it from.",
      codeExecution:
        "Turning this on lets the assistant run code in a sandbox in this project's conversations again.",
    },
    inheritedFrom: {
      tenant: "Inherited from your organisation: {{value}}.",
      entity: "Inherited from {{entity}}: {{value}}.",
      entityUnknown: "Inherited from a parent entity: {{value}}.",
      instance: "Default: {{value}}.",
    },
    loosenGeneric: "This change widens what people, clients or the assistant may do with data.",
    // `tools.egress` per readers audience (D10): the value in words and its loosening.
    egress: {
      named: "Named readers",
      allow: "allowed",
      confirm: "asks first",
      block: "blocked",
      help: {
        project: "Files anyone who can read the project may read",
        entity: "Files limited to the owning entity",
        named: "Files only the people and groups named on them may read",
      },
      files: {
        project: "files anyone who can read the project may read",
        entity: "files limited to the owning entity",
        named: "files only named people may read",
      },
      loosen: {
        blockToConfirm:
          "After a conversation reads {{files}}, web search and web fetch will ask first instead of being blocked.",
        blockToAllow:
          "After a conversation reads {{files}}, web search and web fetch will run without asking instead of being blocked.",
        confirmToAllow:
          "After a conversation reads {{files}}, web search and web fetch will run without asking instead of asking first.",
      },
      to: { allow: "Allow", confirm: "Ask first" },
      keep: { block: "Keep blocked", confirm: "Keep asking first" },
      keepAll: "Keep this policy",
    },
    loosen: {
      webSearch:
        "Turning this on lets the assistant search the web from conversations at this level and below.",
      webFetch:
        "Turning this on lets the assistant read public web pages from conversations at this level and below.",
      mcpEnabled:
        "Turning this on lets people connect MCP clients, which then read what they may read in ESI.",
      mcpWriteTools: "Turning this on lets MCP clients create and change content, as the person.",
      mcpSelfPublish:
        "Turning this on lets MCP clients publish wiki pages without another person's review.",
      mcpTools: "Turning this on gives MCP clients this group's tools again.",
      declassification: "Turning this on lets people lower the classification of files.",
      deviceGrant:
        "Turning this on lets command-line tools and devices sign in with a code shown on another screen.",
      shareOutsideEntity: "Turning this on lets members from other entities be added to projects.",
      nativeClients:
        "Allowing any application lets every native and MCP client connect, not only the ones listed.",
      providers:
        "Turning a provider on lets the instance's connections, or tenants' own, call models through it.",
      labs: "Adding a lab lets its models answer here, once they pass the checks for each effort.",
      mfa: "Making the second factor optional lets native accounts sign in with a password alone.",
      residency:
        "Allowing global models lets answers be served outside the EU. Data stays stored in the EU.",
      wikiTrust: "A lower level lets more people approve and publish wiki changes.",
      webFetchApproval:
        "Only addresses the assistant composed will ask first: it reads other pages without approval.",
      mcpCeiling: "A higher ceiling lets MCP clients receive more sensitive files.",
      webMaxAge: "Older web results, or no limit, let the assistant cite outdated pages.",
      webDomains: "These lists let web search reach domains it could not reach before.",
      webFetchDomains:
        "These lists let the assistant read pages on domains it could not read before.",
      egress: "This lets outbound tools send more after a conversation has read classified files.",
      codeExecution:
        "Turning this on lets the assistant run code in a sandbox from conversations at this level and below.",
      codeEgress:
        "This lets the assistant run code with fewer approvals after a conversation has read classified files.",
    },
  },
  residencyNote: {
    eu: "Stored and processed in the EU",
    global: "Stored in the EU · models may run outside the EU",
    euLong: "Data is stored in the EU and models run in the EU.",
    globalLong:
      "Data stays stored in the EU. Prompts, conversation history and passages from files may be processed by models outside the EU.",
    label: "Data residency",
    // Where the agent's code runs (agent-turn D14), when code execution is on.
    codeEu: "Code the assistant runs stays in an isolated sandbox in the EU, with no network.",
    codeGlobal:
      "Code the assistant runs stays in an isolated sandbox in this deployment's Region, with no network.",
  },
  members: {
    you: "You",
    groupMembers_one: "{{count}} member",
    groupMembers_other: "{{count}} members",
    excluded_one:
      "{{count}} excluded by the entity rule: this member is outside the owning entity.",
    excluded_other:
      "{{count}} excluded by the entity rule: these members are outside the owning entity.",
    rulesHint: "Everyone whose directory attributes match the rule acts with its role.",
    audienceNote: {
      entity: "Members of the owning entity can also read this project, as readers.",
      entity_tree:
        "Members of the owning entity and its sub-entities can also read this project, as readers.",
    },
  },
  knowledgePanel: {
    title: "Knowledge",
    tree: "Knowledge",
    section: {
      files: "Sources",
      wiki: "Wiki",
      connected: "Connected",
      attachments: "This conversation",
      artefacts: "Artefacts",
    },
    type: { file: "File", wiki: "Wiki page", connected: "Connected item", artefact: "Artefact" },
    search: "Search files and pages",
    searchPages: "Search pages",
    items_one: "{{count}} item",
    items_other: "{{count}} items",
    matches_one: "{{count}} match",
    matches_other: "{{count}} matches",
    actionsFor: "Actions for {{name}}",
    actionsHint: "Actions: Shift+F10",
    openPage: "Open page",
    updated: "Updated {{date}}",
    pendingPage: "A pending change touches this page",
    pendingFolder: "holds pages with pending changes",
    breadcrumb: "Folder path",
    back: "Back to {{name}}",
    emptyFolder: "This folder is empty.",
    empty: {
      title: "No sources yet",
      body: "Wiki pages and sources of this project that you can read will be listed here.",
      upload: "Upload a file, or save an answer, to give this conversation its first source.",
    },
    noMatch: {
      title: "Nothing matches",
      body: "No file, page or folder name contains “{{query}}”.",
      clear: "Clear search",
    },
  },
  folders: {
    new: "New folder",
    newIn: "New folder in {{path}}",
    rename: "Rename",
    moveTo: "Move to…",
    uploadHere: "Upload here",
    field: "Folder",
    nameLabel: "Folder name",
    visibleNote: "Folder names are visible to everyone in this project.",
    nameProblem: {
      empty: "Enter a name.",
      too_long: "Use at most {{max}} characters.",
      invalid: "A name cannot contain / or \\, control characters, or be . or ..",
    },
    createTitle: "New folder",
    createIn: "In {{path}}",
    create: "Create folder",
    renameTitle: "Rename “{{name}}”",
    save: "Save name",
    moveTitle: "Move “{{name}}”",
    moveLead: "Folders organise knowledge only: moving changes nobody's access.",
    destination: "Destination: {{path}}",
    moveHere: "Move here",
    picker: {
      label: "Destination folder",
      search: "Search folders",
      root: "{{section}} (top level)",
      noMatch: "No folder name matches.",
      change: "Change",
      done: "Done",
      here: "Already here",
      inside: "Inside the folder being moved",
      tooDeep: "Too deep: folders nest at most {{max}} levels",
    },
    delete: {
      action: "Delete folder",
      title: "Delete “{{name}}”?",
      lead: "Only the folder goes. What it holds stays in the project.",
      moves: "Its contents move to “{{parent}}”: {{what}}.",
      movesBoth: "Its contents move to “{{parent}}”: {{items}} and {{folders}}.",
      empty: "It is empty. Nothing moves to “{{parent}}”.",
      files_one: "{{count}} file",
      files_other: "{{count}} files",
      pages_one: "{{count}} page",
      pages_other: "{{count}} pages",
      folders_one: "{{count}} folder",
      folders_other: "{{count}} folders",
      nothing: "Nothing is deleted. Items you cannot see also move, and keep who can read them.",
      confirm: "Delete folder",
      done: "Folder deleted. Its contents moved up.",
      doneRenamed:
        "Folder deleted. Its contents moved up; renamed to keep names unique: {{names}}.",
    },
    error: {
      nameTaken: "A folder here already has this name (names ignore case).",
      limit: "This section already holds the most folders a project can have.",
      tooDeep: "Folders nest at most {{max}} levels: choose a higher folder.",
      cycle: "A folder cannot go inside itself or one of its own folders.",
      wrongSection: "Wiki pages and sources have separate folders: choose one of this section.",
      frozen: "This project is read-only: folders cannot change.",
    },
  },
  artefactKnowledge: {
    cannotUpdate:
      "An earlier version is in knowledge. Only the person who added it, a manager or the owner can update it.",
    addEllipsis: "Add to knowledge…",
    addTitle: "Add to knowledge",
    updateTitle: "Update in knowledge",
    add: "Add to knowledge",
    update: "Update in knowledge",
    addNamed: "Add to knowledge: {{title}}, version {{version}}",
    updateNamed: "Update in knowledge: {{title}}, version {{version}}",
    inKnowledge: "In knowledge · version {{version}}",
    showInFiles: "Show in Sources",
    projectAudience_one: "Project members who can read its sources · {{count}} person now",
    projectAudience_other: "Project members who can read its sources · {{count}} people now",
    readersExplain:
      "Its sources decide who can read it: only people who can read all of them, now and later. Neither the folder nor anyone's role widens that.",
    generatedExplain:
      "Answers that cite it will say the assistant wrote it, and the project can keep such files out of answers.",
    withWeb: "drawn from web results",
    folderExplain: "The folder only places the file. It does not change who can read it.",
    updateExplain:
      "Version {{version}} replaces version {{current}} in knowledge. It stays in {{folder}}, with the same readers unless its sources narrow them.",
    alreadyAdded: "Version {{version}} of this artefact is already in the project's knowledge.",
    addSubmit: "Add to knowledge",
    updateSubmit: "Update in knowledge",
    adding: "Adding…",
    done: "{{title}} is in knowledge · version {{version}}.",
    refusal: {
      alreadyAdded:
        "This artefact is already in the project's knowledge. Update it from a newer version instead.",
      notNewer: "The knowledge already holds this version or a newer one.",
      storageLimit:
        "Your organisation's storage is full. Remove files, or ask an admin for more space.",
      frozen: "This project is frozen: nothing can be added to it.",
      forbidden: "You can't add files to this project.",
    },
  },
  generated: {
    mark: "Generated by agent",
    fromNamedPrefix: "From ",
    fromConversation: "From a conversation",
    fromDeleted: "From a deleted conversation",
    version: "version {{version}}",
    previewNote:
      "Written by the assistant in a conversation. Check important facts against original sources.",
    usesGenerated: "Uses generated sources",
  },
  files: {
    statusFilter: {
      failed: "Showing failed files only",
      processing: "Showing files still processing only",
      clear: "Show every file",
      mine: {
        failed: "Showing your failed uploads only",
        processing: "Showing your uploads still processing only",
      },
      none: {
        failed: "No file you can read has failed.",
        processing: "No file you can read is still processing.",
      },
    },
    title: "Sources",
    caption: "Project sources",
    upload: "Upload file",
    empty:
      "No files yet. Upload PDF, Word, PowerPoint, Excel, CSV, Markdown, HTML, text files or PNG and JPEG images; answers in this project cite them.",
    name: "Name",
    status: "Status",
    classification: "Classification",
    origin: "Origin",
    readers: "Readers",
    size: "Size",
    actions: "Actions",
    statusLabel: {
      uploading: "Upload incomplete",
      processing: "Processing",
      ready: "Ready",
      failed: "Failed",
    },
    noReason: "No reason was recorded.",
    paused: "Paused",
    pausedReason: "Monthly AI budget reached. It resumes on its own.",
    failure: {
      upload_mismatch:
        "The uploaded file does not match the declared size or type. Upload it again.",
      unknown: "Unknown failure reason.",
      ocr_page_cap: "Too many pages to read automatically.",
      ocr_page_cap_limit: "Too many pages to read automatically (limit {{limit}}).",
      ocr_budget_reached:
        "Monthly page budget for reading scans is used up. Retry next month or ask an admin.",
      ocr_failed: "The scan could not be read automatically.",
      no_text_layer: "No text found, and reading scans is off for this project.",
      unsupported_format: "This file type can't be read.",
      corrupt: "The file is damaged or protected and can't be read.",
      empty: "No text was found in this file.",
      too_large: "The file is too large to read.",
      missing_object: "The upload didn't reach storage. Upload it again.",
      model_rejected: "The file's content could not be indexed.",
      unavailable: "Processing is temporarily unavailable. Retry later.",
    },
    // `generated` (artefacts-to-knowledge) reads as internal until that change names it.
    originDerived: "Set from its sources: it can't be changed by hand.",
    originLabel: {
      internal: "Internal source",
      external: "External source",
      generated: "Generated by agent",
    },
    open: "Open",
    download: "Download",
    citeOnly: "Cited only, no download",
    retry: "Retry",
    retrying: "Retrying…",
    remove: "Remove",
    removing: "Removing…",
    actionFailed: "{{title}}: {{reason}}",
    restricted_one: "{{count}} more file in this project is restricted to other readers.",
    restricted_other: "{{count}} more files in this project are restricted to other readers.",
    actionsFor: "Actions for {{title}}",
    editAccess: "Edit access",
    delete: "Delete",
    deleteTitle: "Delete “{{title}}”?",
    deleteLead: "Deleting this file takes effect at once and can't be undone:",
    deleteEffects: {
      retrieval: "It can no longer be retrieved, cited or downloaded.",
      citations: "Answers that cited it will show “source deleted”.",
      wiki: "Wiki pages built from it get a review item.",
      purge: "Its content is purged within 24 hours.",
    },
    deleteConfirm: "Delete file",
    deleting: "Deleting…",
    deleted: "“{{title}}” was deleted.",
    cancel: "Cancel",
    access: {
      title: "Access to “{{title}}”",
      lead: "Who can read it, its classification and origin.",
      save: "Save",
      saving: "Saving…",
      uploaderIncluded: "{{name}} (uploaded it, always included)",
      noChange: "Nothing changed.",
      widenTitle: "More people will be able to read it",
      widenLead: "After this change, “{{title}}” can be read by:",
      gains: {
        project_one: "Everyone who can read this project ({{count}} member)",
        project_other: "Everyone who can read this project ({{count}} members)",
        projectUnknown: "Everyone who can read this project",
        entity: "Members of {{entity}}",
        entityTree: "Members of {{entity}} and its sub-entities",
        ownerFallback: "the owning entity",
        group: "The group {{name}}",
        rule: "People matching {{name}}",
        unnamed: "A reader you can't see by name",
      },
      downloadUnlocked: "Readers will be able to download the file.",
      floorWidened: "“{{level}}” allows sharing it more widely than before.",
      confirmWiden: "Give access",
      back: "Back",
      narrowedOut: "You no longer have access to “{{title}}”.",
      updated: "Access to “{{title}}” was updated.",
      refused: {
        derived: "A saved file's readers must be able to read every source it came from.",
        belowSource: "A saved file can't be classified below a source it came from.",
        declassified: "Declassified content can only be opened up again by a new declassification.",
        frozen: "This project is read-only now, so its files can't be changed.",
      },
    },
  },
  readers: {
    project: "Project audience",
    entity: "Owning entity",
    entityRef: "Entity {{ref}}",
    entityTree: "Owning entity and sub-entities",
    entityTreeRef: "Entity {{ref}} and sub-entities",
    onlyYou: "Only you",
    people_one: "{{count}} person",
    people_other: "{{count}} people",
    groups_one: "{{count}} group",
    groups_other: "{{count}} groups",
    rules_one: "{{count}} attribute rule",
    rules_other: "{{count}} attribute rules",
    none: "No one you can name",
  },
  audience: {
    restricted: "Restricted",
    label: "Who can read it",
    onlyYou: "Only you",
    youAndShared_one: "You and {{count}} person",
    youAndShared_other: "You and {{count}} people",
    youAndProject: "You and the project",
    youProjectAndShared_one: "You, the project and {{count}} person",
    youProjectAndShared_other: "You, the project and {{count}} people",
    projectSharedBy: "Shared with the project by {{name}}",
    ownerAndShared_one: "{{owner}} and {{count}} person",
    ownerAndShared_other: "{{owner}} and {{count}} people",
    sharedBy: "Shared with you by {{name}}",
    hiddenSources_one: "{{count}} source you can't see",
    hiddenSources_other: "{{count}} sources you can't see",
    webResults: "Includes web results",
    ranCode: "Ran code",
    generalAnswer: "General answer, no sources used",
  },
  models: {
    answer: {
      label: "Answered by",
      fallback: "Fallback model answered",
      policy: "Chosen effort or model not available",
      reasons: {
        older_version: "The newest version was unavailable",
        neighbour_family: "The usual model was unavailable",
        pin_unhonoured: "The version set by your organisation was unavailable",
        unavailable: "Fallback model answered",
        policy: "Chosen effort or model not available",
        provider_withdrawn: "The provider picked for this conversation is no longer offered",
      },
    },
    picker: {
      title: "Effort",
      trigger: "Effort: {{effort}}",
      triggerFamily: "Effort: {{effort}}, on {{family}}",
      triggerLocked: "Effort: {{effort}}, set by {{level}}",
      lockedBy: "Set by {{level}}",
      triggerOnly: "Effort: {{effort}}, the only one available here",
      onlyEffort: "The only effort available here",
      levels: {
        instance: "this instance",
        tenant: "your organisation",
        entity: "your entity",
        project: "this project",
      },
      efforts: {
        fast: { name: "Fast", hint: "Quick answers to simple questions" },
        balanced: { name: "Balanced", hint: "The right effort for most work" },
        deep: { name: "Deep", hint: "Reasons longer before answering" },
      },
      unavailableEffort: "{{effort}} is not available here. Answers use {{fallback}}.",
      unavailableFamily:
        "The model family you picked is not available here. Answers use {{effort}}'s family.",
      otherFamilies: "Other families for {{effort}}",
      effortFamily: "{{effort}}'s family",
      automatic: "Automatic",
      clearFamily: "Stop asking for it",
      useFallback: "Use {{effort}}",
      pinned: "Version set by your organisation:",
      pinUnhonoured:
        "The version your organisation set for {{effort}} is unavailable, so {{model}} answers.",
      through: "Through",
      throughLab: "{{lab}} through",
      providers: { esi: "ESI", tenantBedrock: "Your AWS Bedrock" },
      advanced: "Advanced",
      reasoning: {
        title: "Reasoning for this conversation",
        hint: "Applies to this conversation only.",
        preset: "{{effort}} preset ({{level}})",
        levels: { default: "Model default", low: "Low", medium: "Medium", high: "High" },
        line: "{{model}} · {{level}}",
        lineLevels: { low: "low reasoning", medium: "medium reasoning", high: "high reasoning" },
      },
      retention: { default: "Standard retention", aws_review: "AWS may review (30 days)" },
      processed: { eu: "Processed in the EU", global: "Processed globally" },
      providerNever: "Provider never sees data",
      providerReceives: "Provider receives data",
      cost: {
        lowest: "Lowest cost",
        moderate: "Moderate cost",
        higher: "Higher cost",
        highest: "Highest cost",
      },
      speed: { slower: "Slower", moderate: "Moderate speed", fastest: "Fastest" },
    },
    sections: {
      tabs: {
        label: "Models sections",
        labs: "Labs",
        efforts: "Efforts",
        pins: "Pins",
        health: "Health and history",
        access: "Models and consent",
      },
      purpose: {
        labs: "The labs whose models this level offers. The first is the primary lab: its families answer each effort unless you map another.",
        efforts:
          "Which efforts people may use, whether they may pick another family, and the family and reasoning level each effort runs on.",
        pins: "Keep a family on one version instead of its newest healthy one. A pin applies only while that version answers.",
        health:
          "What each family's versions answered in the latest probes, and when this level's models changed version.",
        access:
          "Each model's terms in plain words, the tenant's consent, and the exact versions this level allows.",
      },
      setHere: "Set at this level",
      inheritedFrom: "Inherited from {{source}}",
      lockedHere: "Locked for the levels below",
      labs: {
        offeredList: "Labs offered here",
        primary: "Primary lab",
        tested: "Tested by ESI · {{date}}, catalogue {{version}}",
        testedNoDate: "Tested by ESI",
        untested: "Not tested by ESI",
        notInEu: "Not available in the EU",
        offer: "Offer",
        drop: "Stop offering",
        moveUp: "Move {{lab}} up",
        moveDown: "Move {{lab}} down",
        warning: "Not tested by ESI: answer quality, tool use and citations may be less reliable",
        unacknowledged: "Confirm the warning about labs ESI has not tested before offering them.",
        dialog: {
          title_one: "Offer {{labs}}?",
          title_other: "Offer {{labs}}?",
          lead: "ESI has no recorded evaluation for this lab yet.",
          scope:
            "Its models become available to your organisation's answers where residency, allowed models and spend limits permit. Offering it is recorded in the audit log.",
          confirm: "I understand, and want to offer it to my organisation",
          submit_one: "Offer the lab",
          submit_other: "Offer the labs",
        },
      },
      efforts: {
        familyHeading: "Family per effort",
        family: "Family",
        reasoning: "Reasoning",
        reasoningLevels: { default: "Model default", low: "Low", medium: "Medium", high: "High" },
        notSupported: "not supported by {{model}}",
        lockFamilies: "Lock the families for levels below",
        lockReasoning: "Lock the reasoning levels (also for each conversation)",
        default: "Primary lab's family (default)",
        notUsable: "not usable for {{effort}}",
        notUsableTools: "Not usable for {{effort}}: no tool use. Its neighbour answers instead.",
        resolves: "Now {{model}}, through {{provider}}",
        noVersion: "No version available here",
        noneKept: "No version available here: last working version kept",
      },
      pins: {
        latest: "Latest (newest healthy version)",
        notAvailableOn: "not available on {{provider}}",
        unhonoured: "{{pin}} is unavailable, so {{model}} answers instead.",
      },
      health: {
        refresh: "Refresh models",
        refreshing: "Refreshing: results show here within a few minutes.",
        waitMinutes_one: "Refreshed recently: available again in {{count}} minute.",
        waitMinutes_other: "Refreshed recently: available again in {{count}} minutes.",
        interval: { hourly: "Checked every hour.", daily: "Checked every day." },
        notProbed: "Not probed yet",
        notListed: "Not listed by {{provider}}",
        probedAt: "checked {{date}}",
        inUse: "In use",
        status: {
          ok: "Answering",
          throttled: "Answering, throttled",
          denied: "Access denied",
          unavailable: "Unavailable",
          error: "Failed",
          unknown: "Unknown",
          retired: "Retired by the provider",
        },
        history: "Version history",
        noHistory: "No version change recorded at this level yet.",
        switch: "{{from}} → {{to}}",
        noVersion: "no version available",
        reasons: {
          new_version_healthy: "A newer version answered its checks",
          version_unhealthy: "The version stopped answering",
          pin_unhealthy: "The pinned version stopped answering",
          pin_restored: "The pinned version answers again",
          admin_pin: "An admin pinned a version",
          admin_unpin: "An admin removed a pin",
          provider_changed: "The provider changed",
          policy_changed: "The policy changed",
        },
      },
      olderOnly:
        "The allowed models here name only older versions ({{models}}): newer versions never serve answers. Pick the families or the current versions instead.",
      projectLink: "Models are set by your organisation and its entities.",
    },
    title: "Models",
    intro:
      "Each model's terms are stated in plain words. Your organisation chooses which models may serve its answers and background work; a model with stricter data terms is never used until a tenant admin accepts them.",
    instance: "This instance: {{geographies}} processing, {{mode}}.",
    retentionMode: {
      default: "standard Bedrock data retention",
      aws_review: "Bedrock data retention allowing AWS review",
    },
    geographies: { eu: "EU", global: "global" },
    level: "Policy for",
    organisation: "Whole organisation",
    catalogue: "Catalogue",
    implications: {
      retentionDefault: "No retention beyond Bedrock's standard abuse checks",
      retentionReview:
        "AWS may review flagged requests and keep them up to 30 days in the processing Region",
      geographiesEu: "Processed in the EU or, with global residency, globally",
      geographiesGlobal: "Processed globally only (not in the EU alone)",
      provider: "The model provider never receives your data",
      costSpeed: "Relative cost {{cost}} of 4, speed {{speed}} of 3",
    },
    unavailable: {
      retention: "Not offered: this instance's retention mode does not allow it",
      geography: "Not offered: its geography is not granted here",
      not_routed: "Not offered: this instance does not route it",
    },
    consent: {
      not_required: "No consent needed",
      none: "Needs a tenant admin's consent",
      accepted: "Allowed by {{name}} on {{date}}",
      acceptedNoName: "Allowed on {{date}}",
      outdated: "Its terms changed: needs consent again",
      revoked: "Consent revoked on {{date}}",
    },
    allow: "Review and allow",
    revoke: "Revoke",
    dialog: {
      title: "Allow {{model}}",
      lead: "Using this model for your organisation implies:",
      scope:
        "It becomes available to scopes whose residency is global and whose allowed models include it, from the next model call. Every acceptance is audited.",
      confirm: "I accept these terms for the whole organisation",
      submit: "Allow {{model}}",
      changed: "These terms changed while you were reading them. Review them again.",
    },
    revokeDialog: {
      title: "Revoke consent to {{model}}",
      lead: "From the next model call, no answer or background task uses it. Roles left without a model fail as unavailable until another model is allowed.",
      submit: "Revoke consent",
    },
    policy: "Policy",
    efforts: "Efforts",
    effortsHelp: "Fast, Balanced and Deep. A single effort locks it for everyone below.",
    advancedPicker: "Let people pick another family",
    advancedPickerHelp:
      "People may run an effort on another model family of your labs, never a specific version.",
    on: "On",
    off: "Off",
    lock: "Lock for levels below",
    save: "Save",
    saving: "Saving…",
    advanced: "Advanced: allowed models",
    advancedHelp:
      "Only the models ticked may serve this level's answers and background work. By default, every model residency and consent allow.",
    notUsable: "Not usable at this level",
    excludedAbove: "Not allowed by the level above",
    uncovered:
      "Without a usable model for: {{roles}}. Those calls fail as unavailable until a model is allowed.",
    blocked: "Saving would leave no usable model for: {{roles}}.",
    source: {
      instance: "Instance default",
      tenant: "Set for the organisation",
      entity: "Set for an entity",
      project: "Set for the project",
    },
    lockedAbove: "Locked above",
    roles: {
      chat: "Balanced answers",
      reasoning: "Deep answers and wiki drafts",
      fast: "quick tasks (titles, checks, summaries)",
      vision: "reading scans and images",
    },
    readOnly: "Entity admins can read consent; tenant admins accept and revoke it.",
  },
  upload: {
    title: "Upload files",
    fileHint:
      "PDF, Office, CSV, Markdown, HTML or text, up to {{mb}} MB; PNG or JPEG images up to {{imageMb}} MB.",
    unsupportedType:
      "This file type isn't supported. Use PDF, Word, PowerPoint, Excel, CSV, Markdown, HTML, plain text, PNG or JPEG.",
    emptyFile: "This file is empty.",
    tooLarge: "This file is larger than this project's {{mb}} MB limit.",
    imageTooLarge: "Images are limited to {{mb}} MB.",
    classification: "Classification",
    levelNamed: "Only named people can read it.",
    levelEntity: "Can't be shared with the whole project audience.",
    levelNoDownload: "Can't be downloaded; answers can still cite it.",
    levelAny: "Any reader choice below is allowed.",
    readers: "Who can read it",
    audience: {
      project: "Everyone who can read this project",
      entity: "Members of the owning entity",
      entity_tree: "Members of the owning entity and its sub-entities",
      named: "Only the people I choose",
      groups: "Groups or attribute rules I choose",
    },
    groupsHint:
      "Members of a group, or people whose directory attributes match a rule, who can also read this project. You stay a reader.",
    groupsLabel: "Groups",
    rulesLabel: "Attribute rules",
    noPrincipals: "Your organisation has no groups or attribute rules yet.",
    pickOne: "Choose at least one group or rule.",
    audienceNotAllowed: "Not allowed for {{level}}.",
    you: "You (always included)",
    noOtherMembers: "No other members yet.",
    membersUnavailable: "The member list couldn't be loaded, so only you can be chosen for now.",
    origin: "Origin",
    originLabel: { internal: "Internal", external: "External" },
    originHint: {
      internal: "Written inside your organisation.",
      external:
        "Supplied by a third party, such as a client or supplier. What is derived from it is marked external.",
    },
    storageUnavailable: "File storage isn't configured on this server, so files can't be uploaded.",
    putFailed: "The file didn't reach storage. Try again.",
    expired: "The upload link expired. Try again to start a new upload.",
    mismatch: "{{detail}} Try again.",
    frozen: "This project became read-only, so the upload can't be finished.",
    storageFull: "Your organisation's storage is full, so this file wasn't kept.",
    cancel: "Cancel",
    batch: {
      files: "Files",
      choose: "Choose files",
      dropHint: "or drop them here",
      dropping: "Drop the files to add them",
      list: "Files to upload",
      limit_one: "A batch holds at most {{max}} files, so 1 file wasn't added.",
      limit_other: "A batch holds at most {{max}} files, so {{count}} files weren't added.",
      folder: "A dropped folder isn't uploaded. Open it and choose the files inside instead.",
      duplicateOf: "Same content as {{name}}, so it's left out.",
      sameNameInProject: "A file of this name is already in the project.",
      sameNameInBatch: "Another file in this batch has this name.",
      accessMissing: "Choose who can read it before uploading.",
      sharedAccess: "These choices apply to every file unless you change a file's access.",
      status: {
        ready: "Ready",
        skipped: "Won't be uploaded",
        queued: "Queued",
        intent: "Preparing…",
        put: "Uploading… {{percent}}%",
        confirm: "Checking…",
        done: "Uploaded",
        failed: "Failed",
        cancelled: "Cancelled",
        not_sent: "Not sent",
      },
      progressOf: "Upload progress of {{name}}",
      removeFile: "Remove {{name}}",
      cancelFile: "Cancel {{name}}",
      retry: "Retry",
      retryFile: "Retry {{name}}",
      changeAccess: "Change access",
      changeAccessFile: "Change access for {{name}}",
      customAccess: "Custom access",
      accessTitle: "Access for {{name}}",
      accessLead: "Who can read this file, its classification and origin.",
      accessSave: "Save",
      accessReset: "Use the batch's access",
      submit_one: "Upload",
      submit_other: "Upload {{count}} files",
      retryAll_one: "Try again",
      retryAll_other: "Retry {{count}} files",
      cancelAll: "Cancel all",
      close: "Close",
      summary_one: "{{done}} of {{count}} file uploaded",
      summary_other: "{{done}} of {{count}} files uploaded",
      failedCount_one: "{{count}} failed",
      failedCount_other: "{{count}} failed",
      notSentCount_one: "{{count}} not sent",
      notSentCount_other: "{{count}} not sent",
      cancelledCount_one: "{{count}} cancelled",
      cancelledCount_other: "{{count}} cancelled",
      announce: {
        done: "{{name}} uploaded.",
        failed: "{{name}} failed: {{reason}}",
        cancelled: "{{name}} cancelled.",
        not_sent: "{{name}} not sent.",
      },
      closeTitle: "Stop uploading?",
      closeLead_one:
        "{{count}} file isn't uploaded yet. Stopping cancels it; files already uploaded stay in the project.",
      closeLead_other:
        "{{count}} files aren't uploaded yet. Stopping cancels them; files already uploaded stay in the project.",
      keepUploading: "Keep uploading",
      stopAndClose: "Stop and close",
    },
  },
  progress: {
    done: "Done",
    failed: "Failed",
    stepOf: "Step {{current}} of {{total}}: {{name}}",
  },
  citations: {
    page: "p. {{page}}",
    slide: "Slide {{slide}}",
    image: "Image",
    fromOcr: "from OCR",
    checkOriginal: "from OCR, check the original",
    caption: "described by AI",
    supportedByTranscription: "Supported by the transcription",
    start: "Opening section",
    sources: "Sources",
    sourcesCount_one: "{{count}} source",
    sourcesCount_other: "{{count}} sources",
    projectSources: "Project sources",
    verdicts: {
      unverified_one: "{{count}} unverified",
      unverified_other: "{{count}} unverified",
      unavailable_one: "{{count}} unavailable",
      unavailable_other: "{{count}} unavailable",
    },
    showAll: "Show all",
    collapse: "Collapse",
    more_one: "+{{count}}",
    more_other: "+{{count}}",
    moreLabel_one: "Show {{count}} more source",
    moreLabel_other: "Show {{count}} more sources",
    showing_one: "Showing {{count}} source",
    showing_other: "Showing {{count}} sources",
    showingAll_one: "Showing all {{count}} source",
    showingAll_other: "Showing all {{count}} sources",
    sourceChip: "Source {{handle}}: {{label}}",
    showInSources: "Show in sources",
    /** A cited source's classification level (the neutral mark's accessible name). */
    classification: "Classification: {{label}}",
    /** The compact chip's lock: a level above the scheme's lowest. */
    classificationLock: "Classified {{label}}",
    group_one: "{{count}} source",
    group_other: "{{count}} sources",
    groupLabel_one: "{{count}} cited source",
    groupLabel_other: "{{count}} cited sources",
    checking: "Checking citations…",
    open: "Open",
    openSource: "Open source",
    working: "Working…",
    unsourcedSpan: "not from project sources",
    stoppedUnavailable: "Stopped: the answer could not go on. Try again in a moment.",
    stoppedBySpend:
      "Stopped: your organisation's monthly AI budget is used up. An admin can raise it.",
    stoppedByTime:
      "Stopped: the answer reached its time limit for the external client. Ask again here to continue.",
    stoppedByTurnSpend:
      "Stopped: this answer reached the most one answer may spend on AI. Ask a narrower question, or ask an admin to raise the limit.",
    stoppedByUserShare:
      "Stopped: you have used your share of your organisation's monthly AI budget. You can ask again next month, or ask an admin to raise your share.",
    unsourcedBadge: "Not from sources",
    unverifiedSpan: "unverified",
    // One neutral notice for every hidden message: it never says why (a source, a page version
    // or a reader change) nor whether the viewer could ever read it (version-aware-labels D8).
    hiddenNotice: "Hidden: relies on sources you can't read",
    hiddenExplained:
      "The rest of the conversation stays visible. If you need this answer, ask a project manager.",
    speaker: { user: "You", assistant: "Answer" },
    state: {
      pending: "citation pending",
      checking: "Checking…",
      supported: "Supported",
      unsupported: "Unverified",
      unchecked: "Unchecked",
      deleted: "Source deleted",
      unavailable: "Source unavailable",
    },
    preview: {
      folder: "Folder:",
      loading: "Loading the passage…",
      versionChanged:
        "The source was updated since this answer. The cited passage is no longer shown.",
      noExcerpt: "No passage text to show.",
      unavailable: "Source unavailable",
      unavailableBody: "You can no longer read this source.",
      label: "Citation {{handle}}",
    },
    web: {
      badge: "web",
      span: "from the web",
      sources: "Web results",
      open: "Open page",
      noLink: "No link to open",
      noSnippet: "No snippet to show.",
      retrieved: "Retrieved on {{date}}",
    },
    help: {
      badge: "ESI help",
      chip: "ESI help: {{title}}",
      sources: "ESI help",
      open: "Open this screen",
      adminOnly: "An administrator does this. Ask one of your organisation's administrators.",
      noProject: "This screen is in a project: open it from the project concerned.",
      earlier: "Describes an earlier version of ESI.",
      gone: "This help page is no longer in ESI.",
      noExcerpt: "No passage to show.",
    },
  },
  conversation: {
    pick: "Conversation",
    switcher: "Switch conversation",
    newOption: "New conversation",
    untitled: "Untitled conversation",
    sharedBy: "shared by {{name}}",
    share: "Share",
    thread: "Messages",
    empty: "Ask about this project's files. Answers cite the passages they rely on.",
    ask: "Ask about this project…",
    send: "Send",
    thinking: "Searching the project's sources…",
    saveAsFile: "Save as file",
    frozen: "This project is read-only, so no messages can be sent.",
    sharedReadOnly: "Shared with you by {{name}}. You can read it; only {{name}} can continue it.",
    projectSharedReadOnly:
      "Shared with the project by {{name}}. You can read it; only {{name}} can continue it.",
    userShareRefused:
      "You have used your share of your organisation's monthly AI budget, so no new answer can start until next month. Your message is back in the box. An admin can raise your share.",
    budgetRefused:
      "Your organisation's monthly AI budget is used up, so no new answer can start. Your message is back in the box. An admin can raise the budget.",
    turnFailed:
      "The answer could not be completed. Your message is back in the box: send it again.",
    status: {
      submitted: "Searching the project's sources…",
      streaming: "Writing the answer…",
      done: "Answer ready.",
    },
    question: {
      title: "The assistant asks",
      forYou: "Question for you",
      youPicked: "You picked {{choice}}",
      picked: "Answered: {{choice}}",
      choices: "Choices",
      ownWords: "Answer in your own words",
    },
    timeline: {
      label: "Steps of this answer",
      progress: "Answer progress",
      details: "Show details",
      hideDetails: "Hide details",
      more: "+{{count}}",
      done: {
        readTasks_one: "Read the tasks ({{count}})",
        readTasks_other: "Read the tasks ({{count}})",
        proposedTask_zero: "Proposed no task",
        proposedTask_one: "Proposed a task",
        proposedTask_other: "Proposed {{count}} tasks",
        recalled_one: "Recalled a memory",
        recalled_other: "Recalled {{count}} memories",
        proposedMemory_one: "Proposed a memory",
        proposedMemory_other: "Proposed a memory",
        thinking: "Thought",
        search: "Searched sources",
        read_one: "Read {{count}} document",
        read_other: "Read {{count}} documents",
        readNone: "Read sources",
        web: "Searched the web",
        wiki_write: "Edited the wiki",
        artefact: "Wrote an artefact",
        code: "Ran code",
        task: "Worked on tasks",
        memory: "Used memory",
        question: "Asked you a question",
        people: "Looked for people who know",
        answer: "Wrote the answer",
        other: "Used a tool",
        help: "Searched ESI help",
        feedback: "Drafted a report",
      },
      running: {
        readTasks: "Reading the tasks…",
        proposedTask: "Proposing a task…",
        recalled: "Recalling memories…",
        proposedMemory: "Proposing a memory…",
        thinking: "Thinking…",
        search: "Searching sources…",
        read: "Reading…",
        web: "Searching the web…",
        wiki_write: "Editing the wiki…",
        artefact: "Writing an artefact…",
        code: "Running code…",
        task: "Working on tasks…",
        memory: "Using memory…",
        question: "Asking you a question…",
        people: "Looking for people who know…",
        answer: "Writing the answer…",
        other: "Working…",
        checking: "Checking the sources…",
        starting: "Working…",
      },
      thoughtFor: "Thought for {{duration}}",
      thoughtFallback: "Thought about the next step",
      underSecond: "< 1 s",
      seconds: "{{count}} s",
      minutes: "{{minutes}} min {{seconds}} s",
    },
    yours: "Yours",
    sharedWithYou: "Shared with you",
  },
  tools: {
    label: {
      web_search: "Web search",
      web_fetch: "Read a page",
      run_code: "Run code",
      other: "Outbound tool",
    },
    code: {
      program: "Program",
      programIn: "Program ({{language}})",
      output: "Output",
      errors: "Errors",
      showAll_one: "Show all ({{count}} more character)",
      showAll_other: "Show all ({{count}} more characters)",
      showLess: "Show less",
      exit: "Exit code {{code}}",
      filesMade_one: "{{count}} file",
      filesMade_other: "{{count}} files",
      filesIn: "Files given to the program",
      dropped: "Not kept",
      dropReason: {
        type: "this type of file is not allowed",
        size: "over the size limit",
        count: "over the number of files a run keeps",
        limit: "the conversation holds the most artefacts it can",
        writes: "the answer made the most artefact writes it can",
        artefacts_off: "artefacts are turned off here",
        failed: "it could not be stored",
      },
      declined: "You declined: the program did not run.",
      failed: "The program could not run: the sandbox is unavailable or stopped it.",
      blocked: "The program did not run.",
      downloadNamed: "Download {{name}}",
      approval: {
        title: "Run this program?",
        explained:
          "This answer read restricted content. The program runs in an isolated sandbox with no network, and only after you allow it.",
        files: "With these files",
        allow: "Run it",
      },
    },
    outcome: {
      running: "Running…",
      pending: "Waiting for confirmation",
      done: "Done",
      blocked: "Blocked",
      declined: "Declined",
      expired: "Expired",
      failed: "Failed",
    },
    arguments: {
      sent: "Sent outside ESI",
      proposed: "Would be sent outside ESI",
      notSent: "Not sent",
    },
    blockedBecause: "Nothing left ESI.",
    reason: {
      classification:
        "This answer used information whose classification does not allow sending it outside the organisation.",
      verbatim: "The request copied text from a passage read for this answer.",
      verbatim_confidential:
        "The request copied text from a confidential passage read for this answer.",
      handle: "The request named a source of this project.",
      residency: "This tool's provider is outside the data residency allowed here.",
      turn_limit: "This answer already made as many outbound requests as allowed.",
      model_budget: "Your organisation's monthly AI budget is used up.",
      user_budget: "You have used your share of your organisation's monthly AI budget.",
      no_confirmation_channel: "This request needs your confirmation, which only ESI can ask for.",
      url_provenance: "The assistant composed this address itself.",
      fetch_policy: "Your organisation requires approval for every page.",
      hidden_characters: "The request held characters you could not see.",
      file_unavailable: "A file the program was given is no longer available to you.",
      invalid_url: "The address is not a valid web address.",
      host_denied: "The address is not a public web page.",
      address_denied: "The address leads to a private or reserved network.",
      domain_denied: "Your organisation does not allow this site.",
      redirect_limit: "The page redirected too many times.",
      redirect_denied: "The page redirected to an address that is not allowed.",
      timeout: "The page took too long to answer.",
      too_large: "The page was too large.",
      unsupported_type: "The content type is not supported.",
      upstream: "The site did not answer.",
    },
    declined: "You declined this request: nothing left ESI.",
    expired: "This request was not confirmed in time: nothing left ESI. Ask again to continue.",
    alreadyDecided: "This request was already answered. Ask again to continue.",
    budget:
      "Your organisation's monthly AI budget is used up: nothing was sent. Reload to answer again once it is raised.",
    userBudget:
      "You have used your share of your organisation's monthly AI budget: nothing was sent. Reload to answer again once it is raised.",
    unavailable: "This request can no longer be answered. Ask again to continue.",
    failed: "The external service did not answer.",
    refused: "The request went beyond what this tool accepts: nothing left ESI.",
    results_one: "{{count}} result",
    results_other: "{{count}} results",
    resultsNote: "From outside the organisation: check before relying on it.",
    wiki: {
      written_one: "Wiki: {{count}} change written",
      written_other: "Wiki: {{count}} changes written",
      writing: "Writing to the wiki…",
    },
    help: {
      searched: "Searched ESI help",
      searching: "Searching ESI help…",
      pages_one: "{{count}} help page",
      pages_other: "{{count}} help pages",
    },
    web: {
      searched: "Searched the web",
      searching: "Searching the web…",
      showResults_one: "Show the result",
      showResults_other: "Show the {{count}} results",
      approval: "ESI will send this query to the web search service:",
      blocked: {
        classification:
          "Web search is unavailable in this conversation because of the sources it used.",
        residency: "Web search is unavailable under this project's data residency.",
        other: "This search was blocked: nothing was sent.",
      },
      declined: "You declined this search: nothing was sent.",
      expired: "This search was not confirmed in time: nothing was sent. Ask again to continue.",
      failed: "The web search service did not answer.",
      refused: "This search went beyond what web search accepts: nothing was sent.",
    },
    fetch: {
      read: "Read the page",
      reading: "Reading the page…",
      address: "Page address",
      truncated: "A long page: only its beginning was read.",
      notAllowed: "Not allowed",
      unsupported: "Unsupported content",
      approval: {
        title: "Allow ESI to read this page?",
        url_provenance: "The assistant composed this address itself: check it before allowing.",
        fetch_policy: "Your organisation requires approval for every page.",
        classification: "This conversation has read confidential sources.",
        other: "ESI will request exactly this address:",
      },
      blocked: {
        classification:
          "Reading pages is unavailable in this conversation because of the sources it used.",
        residency: "Reading pages is unavailable under this project's data residency.",
        other: "This page was not read: nothing was sent.",
      },
      declined: "You declined: the page was not read.",
      expired: "This page was not approved in time: it was not read. Ask again to continue.",
      failed: "The page could not be read.",
      refused: {
        domain_denied: "Your organisation does not allow reading pages from this site.",
        redirect_denied: "The page redirected to an address that is not allowed: nothing was read.",
        not_public: "This address is not a public web page: nothing was sent.",
        unsupported_type:
          "This content can't be read: only web pages, plain text, Markdown and JSON can.",
        other: "This address can't be read: nothing was sent.",
      },
    },
    approval: {
      title: "Allow this request to leave ESI?",
      explained: "{{tool}} will send exactly this to an external service:",
      allow: "Allow",
      decline: "Decline",
    },
  },
  projectShare: {
    withProject: "with the project",
    heading: "Share with the project",
    everyone: "Everyone in the project",
    shared: "Shared with the project",
    share: "Share",
    stop: "Stop sharing",
    working: "Saving…",
    nobodyYet: "No other members yet. People who join will be able to read it.",
    allWillRead_one: "The other member will be able to read it",
    allWillRead_other: "The {{count}} other members will be able to read it",
    allRead_one: "The other member can read it",
    allRead_other: "The {{count}} other members can read it",
    someExcluded_one: "{{count}} of {{total}} members can't read some of its sources",
    someExcluded_other: "{{count}} of {{total}} members can't read some of its sources",
    guests:
      "Reaches members, including those who join later, who can read every source. Guests aren't included: name them below.",
    frozen: "This project is read-only, so its shares can't change.",
    adminStop: "Stop sharing with the project",
    adminStopTitle: "Stop sharing this conversation with the project?",
    adminStopBody:
      "Everyone who reads it through the project loses access, including you unless you're named on it. People named on it keep it.",
    adminStopConfirm: "Stop sharing",
  },
  share: {
    refusedPerson: "{{name}}: {{reason}}",
    title: "Share this conversation",
    explain:
      "People you share with can read the conversation, not continue it. Each person is checked against the sources it used; anyone who can't read them all is refused.",
    sharedWith: "Shared with",
    nobody: "No one named yet.",
    stop: "Stop sharing",
    add: "Share with",
    noCandidates: "Everyone else who can read this project already has it.",
    noMatch: "No one who can read this project matches.",
    find: "Find people",
    findHint: "Only the first 200 people are listed: type a name or e-mail address.",
    onlyYou: "No one else can read this project yet.",
    addPeople: "Add people to the project",
    addPeopleHint: "A project manager can add people to the project.",
    membersUnavailable: "The member list couldn't be loaded.",
    sharedNow: "Shared with {{names}}.",
    refused_one: "{{count}} person was refused:",
    refused_other: "{{count}} people were refused:",
    sharing: "Sharing…",
    submit: "Share",
    done: "Done",
    explainStandalone:
      "People you share with can read the conversation, not continue it. Name them by email address; anyone who can't receive it is refused.",
    someone: "Someone",
    emails: "Email addresses",
    emailsPlaceholder: "name@example.com",
    emailsHint: "Separate addresses with commas or new lines (20 at most).",
    cannotReceive: "{{email}} can't receive this conversation.",
  },
  save: {
    title: "Save answer as a file",
    readers_one: "{{count}} person will be able to read it",
    readers_other: "{{count}} people will be able to read it",
    readersExplain:
      "Only people who can read every source of this answer. If a source is narrowed later, so is the file.",
    webExplain: "Part of this answer comes from public web pages: the file is marked as external.",
    classification: "Classification",
    origin: "Origin",
    fileTitle: "File name",
    fileTitlePlaceholder: "Leave empty for a name from the answer",
    suggesting: "Suggesting a name…",
    saved: "Saved as “{{title}}”. It appears in Sources once processed.",
    saving: "Saving…",
    submit: "Save file",
    cancel: "Cancel",
    done: "Done",
    target: "Project",
    targetHint: "The file is saved in this project, for its readers.",
    noTarget: "None of your projects accepts saved answers from you.",
    residencyNarrower:
      "This conversation was answered with models outside the EU, so it can't go into this EU-only project.",
  },
  residency: {
    title: "Data residency",
    intro:
      "Where models may process this organisation's prompts and sources. EU keeps every model call in the EU; Global also allows global models, with EU models as fallbacks. Files and conversations stay stored in the EU either way.",
    ceilingEuOnly: "This instance allows EU models only: residency is EU everywhere.",
    value: { eu: "EU only", global: "Global" },
    source: {
      instance: "Instance",
      tenant: "Organisation",
      entity: "Entity",
      project: "Project",
    },
    locked: "Locked",
    lockedAbove: "Locked above",
    organisation: "Organisation",
    entities: "Organisation and entities",
    projects: "Projects",
    name: "Name",
    residency: "Residency",
    origin: "Comes from",
    lock: "Lock for levels below",
    save: "Save",
    saving: "Saving…",
    personalProject: "Personal project",
    globalCount_zero: "No project uses global models.",
    globalCount_one: "{{count}} project may use global models.",
    globalCount_other: "{{count}} projects may use global models.",
    choose: "Residency of {{name}}",
  },
  signin: {
    title: "Sign-in",
    lead: "How people of your organisation sign in: their identity provider, and the domains that send them there.",
    adminsOnly: "Only tenant admins can manage sign-in.",
    providers: "Identity providers",
    providersCaption: "Identity providers",
    noProviders:
      "No identity provider yet. People sign in with ESI accounts until you add one and bind a domain to it.",
    addGoogle: "Add Google Workspace",
    protocol: { oidc: "OpenID Connect", saml: "SAML" },
    preset: { google_workspace: "Google Workspace" },
    status: { active: "Active", disabled: "Disabled" },
    googleRule:
      "Admits only accounts of its bound domains whose Google Workspace matches. Personal Google accounts are refused.",
    redirectUri: "Redirect URI",
    redirectUriHelp: "Paste it as an authorised redirect URI of the identity provider's client.",
    copy: "Copy",
    copied: "Copied",
    google: {
      title: "Add Google Workspace",
      description:
        "Uses an OAuth client in your own Google Cloud project. Google's issuer and scopes are set by ESI.",
      steps:
        "In Google Cloud: create an OAuth client of type Web application, set the consent screen to Internal, and add the redirect URI below.",
      restrict:
        "Optional: in the Google Admin console (Security, API controls, App access control), allow this client only for the group or org unit that should use ESI, so nobody else can even reach it.",
      displayName: "Name shown at sign-in",
      clientId: "Client ID",
      clientSecret: "Client secret",
      secretHelp: "Sent to the sign-in service once. ESI does not keep it.",
      submit: "Add Google Workspace",
      adding: "Adding…",
      added: "{{name}} was added. Bind a verified domain to it so its people can sign in.",
      defaultName: "Google Workspace",
    },
    domains: "Domains",
    domainsCaption: "Domains",
    noDomains: "No domain yet. Claim your organisation's e-mail domain to route its sign-ins.",
    claim: "Claim a domain",
    claimTitle: "Claim a domain",
    claimDescription:
      "Choose where its sign-ins go. You prove ownership with a DNS TXT record before it routes anyone.",
    domain: "Domain",
    routesTo: "Sign-ins go to",
    nativeAccounts: "ESI accounts",
    routesToLine: "Sign-ins go to {{name}}",
    nativeLine: "Sign-ins use ESI accounts",
    bindingFixed:
      "The destination is fixed once the domain is verified. Choose it now: moving a verified domain is not available yet.",
    claimSubmit: "Claim",
    claiming: "Claiming…",
    verified: "Verified",
    pending: "Waiting for DNS verification",
    txtName: "TXT record name",
    txtValue: "TXT record value",
    verify: "Verify",
    verifying: "Checking…",
    verifyFor: "Verify {{domain}}",
    verifiedNotice: "{{domain}} is verified.",
    // sso-and-esi-credentials (#419, D1, D5, D7, D8): who may also use ESI sign-in on a domain.
    mode: {
      labels: {
        sso_or_esi: "SSO or ESI password",
        sso_admins_esi: "SSO only",
      },
      help: {
        sso_or_esi:
          "Everyone signs in with {{name}}, and may also set up an ESI password or passkey. The default.",
        sso_admins_esi:
          "Everyone signs in with {{name}}. Tenant admins keep an ESI password or passkey to get in if {{name}} is down; each use is audited and emailed to the other admins.",
      },
      chipPrefix: "Sign-in mode:",
      change: "Change",
      changeFor: "Change sign-in for {{domain}}",
      title: "Sign-in for {{domain}}",
      lead: "People on this domain can always sign in with {{name}}. Choose who may also use an ESI password or passkey.",
      choose: "Sign-in mode for {{domain}}",
      current: "(current)",
      mfaOptional:
        "ESI sign-in follows your organisation's two-step verification setting, currently optional: a password alone can sign in.",
      mfaRequired:
        "ESI sign-in follows your organisation's two-step verification setting, currently required: an authenticator app or a passkey too.",
      mfaUnknown: "ESI sign-in follows your organisation's two-step verification setting.",
      mfaLink: "Change it in Security",
      people_one: "{{count}} person loses ESI sign-in",
      people_other: "{{count}} people lose ESI sign-in",
      impact: "{{people}}: {{sessions}} and {{grants}} end. Their SSO keeps working.",
      refused: {
        last_sign_in_method: {
          title: "No admin could sign in after this change.",
          body: "No tenant admin can sign in yet: none has signed in with {{name}} or holds an ESI password or passkey. Have an admin sign in with {{name}} first.",
        },
      },
      save: "Save",
      saving: "Saving…",
      saved: "{{domain}} now uses “{{mode}}”.",
      savedSessions_one: "{{count}} ESI sign-in session ended.",
      savedSessions_other: "{{count}} ESI sign-in sessions ended.",
    },
    removal: {
      actionsFor: "Actions for {{name}}",
      removeIdpItem: "Remove provider…",
      releaseDomainItem: "Release domain…",
      cancelClaimItem: "Cancel claim",
      idpTitle: "Remove {{name}}?",
      idpLead: "Nobody will be able to sign in through {{name}}. This can't be undone.",
      domainTitle: "Release {{domain}}?",
      domainLead:
        "{{domain}} stops routing sign-ins, and another organisation can verify it. This can't be undone.",
      claimTitle: "Cancel the claim on {{domain}}?",
      claimLead: "It is not verified yet, so it routes nobody. Its DNS token stops working.",
      impact: "What this ends",
      nobodyLoses: "Nobody loses sign-in.",
      people_one: "{{count}} person loses sign-in.",
      people_other: "{{count}} people lose sign-in.",
      createdAtFirstSignIn_one: "{{count}} of them was created at their first sign-in.",
      createdAtFirstSignIn_other: "{{count}} of them were created at their first sign-in.",
      accountsStayIdp:
        "Their accounts and content stay; they can sign in again once a new provider is set up for their domain.",
      accountsStayDomain:
        "Their accounts and content stay; they can sign in again once the domain is verified and bound to a provider again.",
      signInContinues_one:
        "{{count}} person is signed out but can still sign in through {{name}}, which may vouch for addresses outside its verified domains.",
      signInContinues_other:
        "{{count}} people are signed out but can still sign in through {{name}}, which may vouch for addresses outside its verified domains.",
      sessions_one: "{{count}} active session",
      sessions_other: "{{count}} active sessions",
      grants_one: "{{count}} connected app",
      grants_other: "{{count}} connected apps",
      sessionsEnd: "{{sessions}} and {{grants}} end now.",
      domains_one:
        "The domain {{domains}} is released. Claim and verify it again to use it with another provider.",
      domains_other:
        "The domains {{domains}} are released. Claim and verify them again to use them with another provider.",
      source:
        "The directory source {{name}} is removed, with {{groups}} and {{memberships}}. Access granted through those groups ends.",
      groups_one: "{{count}} group",
      groups_other: "{{count}} groups",
      memberships_one: "{{count}} membership",
      memberships_other: "{{count}} memberships",
      signsOutActorIdp: "Your account is linked to {{name}}: you will be signed out too.",
      signsOutActorDomain: "You are among the people signed out: your session ends too.",
      refusedIdpTitle: "You can't remove {{name}}.",
      refusedIdp:
        "No organisation admin would keep a way to sign in afterwards. To replace it, bind your domain to the new provider and have an admin sign in through it, then remove {{name}}.",
      refusedDomainTitle: "You can't release {{domain}}.",
      refusedDomain:
        "No organisation admin would keep a way to sign in afterwards. Have an admin sign in through another provider, or with an ESI account, first.",
      typeToConfirm: "Type {{name}} to confirm",
      recentSignIn:
        "This needs a sign-in within the last 5 minutes: you may be asked to sign in again.",
      removeIdp: "Remove provider",
      releaseDomain: "Release domain",
      removing: "Removing…",
      releasing: "Releasing…",
      cancelClaim: "Cancel claim",
      cancelling: "Cancelling…",
      keep: "Keep claim",
      close: "Close",
      cleanupTitle: "Finish removing {{name}}",
      cleanupLeft: "{{name}} is removed, but its clean-up at the sign-in service did not finish.",
      finishCleanup: "Finish clean-up",
      claimVerified:
        "{{domain}} was verified in the meantime. Close this and choose Release domain to release it.",
      retryAccount: "Try again",
      cleanupStillPending:
        "The clean-up of {{name}} did not finish this time either. Try again later; nobody can sign in through it meanwhile.",
      cleanupPending:
        "{{name}} is removed: nobody can sign in through it. Its clean-up at the sign-in service did not finish. Retry to finish it; you may be asked to sign in again.",
      retry: "Retry clean-up",
      retrying: "Retrying…",
      cleanupDone: "{{name}} is removed and its clean-up is finished.",
      idpRemoved: "{{name}} is removed.",
      domainReleased: "{{domain}} is released.",
      claimCancelled: "The claim on {{domain}} is cancelled.",
      keepTitle: "Keep a domain instead of releasing it",
      keepNone: "Keep no domain",
      keepDomain: "Keep {{domain}} and move its people to ESI sign-in",
      keepCounts: "{{kept}}; {{links}}.",
      keepWithCredential_one: "{{count}} person keeps their ESI sign-in",
      keepWithCredential_other: "{{count}} people keep their ESI sign-in",
      keepLinks_one: "{{count}} gets a link to set a password, valid 72 hours",
      keepLinks_other: "{{count}} get a link to set a password, valid 72 hours",
      keepMfaOptional:
        "People on a kept domain follow your organisation's two-step verification setting, currently optional: a password alone can sign in.",
      keepMfaRequired:
        "People on a kept domain follow your organisation's two-step verification setting, currently required: an authenticator app or a passkey too.",
      linksSent_one: "{{count}} set-password link sent.",
      linksSent_other: "{{count}} set-password links sent.",
      linksFailed_one:
        "{{count}} could not be sent: that person can ask for a new one on ESI's “Get a new invitation link” page.",
      linksFailed_other:
        "{{count}} could not be sent: those people can ask for a new one on ESI's “Get a new invitation link” page.",
    },
  },
  directory: {
    google: {
      add: "Add Google Workspace source",
      needsIdp:
        "To sync Google Workspace groups, first add the Google Workspace identity provider in Sign-in.",
      openSignin: "Open Sign-in",
      title: "Add a Google Workspace source",
      description:
        "Reads users, groups and memberships with a service account of your Google Cloud project. Nothing in Google is changed.",
      name: "Name",
      defaultName: "Google Workspace",
      identityProvider: "Identity provider",
      key: "Service account key (JSON)",
      keyHelp:
        "Written once and kept encrypted outside the database. ESI shows only the account and the key ID afterwards.",
      keyChosen: "{{email}} · key {{keyId}}",
      keyInvalid: "This file is not a Google service account key (JSON).",
      scope: "Who gets an ESI account",
      scopeHelp:
        "Groups and memberships sync for everyone. Accounts are created only for this group's members (nested included) or this org unit's users.",
      scopeGroup: "Members of a group",
      scopeOrgUnit: "Users of an org unit",
      groupEmail: "Group e-mail",
      orgUnitPath: "Org unit path",
      privileges: "Privileges to grant",
      privilegesHelp:
        "In the Google Admin console, assign the service account a custom admin role with only these read privileges. No domain-wide delegation.",
      privilege: {
        users_read: "Users: Read",
        groups_read: "Groups: Read",
        org_units_read: "Organizational units: Read",
      },
      residency:
        "Workspace data stays where Google keeps it; ESI stores the synced groups and accounts in its own region.",
      submit: "Add source",
      adding: "Adding…",
      added: "{{name}} was added, disabled. Test it, then enable it.",
      account: "{{email}} · key {{keyId}}",
      scopeLine: {
        group: "Accounts for members of {{value}}",
        orgUnit: "Accounts for users of {{value}}",
      },
      skipped: "Left out at the last run: {{list}}",
      skip: {
        outside_scope: "{{count}} outside the scope",
        external_member: "{{count}} outside the Workspace",
        customer_member: "{{count}} “all users” member",
      },
      test: "Test",
      testing: "Testing…",
      testFor: "Test {{name}}",
      enable: "Enable",
      enabling: "Enabling…",
      replaceKey: "Replace key",
      replacing: "Replacing…",
      keyReplaced: "The key of {{name}} was replaced.",
      testPassed: "{{name}}: the test passed. The service account can read what the source needs.",
      testMissing:
        "{{name}}: missing privilege: {{list}}. Grant it in the Google Admin console, then test again.",
      testFailed: "{{name}}: the test failed. {{reason}}",
      testError: {
        no_adapter: "This source cannot be tested.",
        credential_missing: "The source has no usable key.",
        credential_refused:
          "The key could not be read: it is missing, or belongs to another organisation.",
        token_refused: "Google refused the service account's key.",
        scope_not_found: "The group or org unit was not found.",
        throttled: "Google limited the calls. Try again in a minute.",
        unreachable: "Google could not be reached.",
      },
    },
    unknownReason: "Unknown reason",
    title: "Directory",
    intro:
      "Where your identity provider, SCIM and HR feeds keep groups, attributes and memberships in line with ESI. Access follows these changes from the next request.",
    adminsOnly: "Only admins can manage the directory. Ask a tenant admin if you need a change.",
    sources: "Sources",
    caption: "Directory sources",
    noSources:
      "No source yet. Add a SCIM source for your identity provider, or a push source for an HR feed.",
    name: "Name",
    kind: "Kind",
    kindLabel: { idp: "Sign-in groups", scim: "SCIM", push: "Push API", pull: "Pull" },
    status: "Status",
    statusLabel: { active: "Active", disabled: "Disabled" },
    lastSync: "Last sync",
    lastFull: "Last full reconciliation",
    identityProvider: "Identity provider",
    identityProviderFor: "Identity provider of {{name}}",
    noIdentityProvider: "None",
    identityProviderHelp:
      "The identity provider this source speaks for. A first sign-in matches the IdP subjects it records only through that provider; with none, it records no subject.",
    unboundHint: "Names no identity provider: the IdP subjects it sends are ignored.",
    lapsed_one: "{{count}} person must sign in again for their groups",
    lapsed_other: "{{count}} people must sign in again for their groups",
    lapsedHint:
      "Their groups from this identity provider no longer grant access: the provider has not confirmed them within the tenant's validity period.",
    never: "Never",
    stale: "Stale",
    staleHint: "No completed sync within its interval.",
    errors: "Recent failures",
    noErrors: "None",
    errorLine: "{{reason}} ({{item}})",
    moreErrors: "and {{count}} more",
    rotate: "Rotate token",
    rotating: "Rotating…",
    add: "Add source",
    adding: "Adding…",
    newName: "Source name",
    newKind: "Kind",
    newKindLabel: { scim: "SCIM (Entra ID, Okta…)", push: "Push API (HR or ERP feed)" },
    tokenTitle: "Token for {{name}}",
    tokenOnce:
      "Copy it now: you won't see it again. Rotating the token later refuses this one from then on.",
    scimUrl: "SCIM base URL",
    token: "Token",
    copy: "Copy",
    copied: "Copied",
    dismiss: "I've stored it",
    unmapped: "Unmapped org codes",
    unmappedIntro:
      "People whose organisation code no mapping covers. They keep their other memberships and get no entity from this code until it is mapped.",
    unmappedCaption: "Unmapped organisation codes",
    noUnmapped: "Every organisation code is mapped.",
    person: "Person",
    code: "Code",
    source: "Source",
    since: "Since",
    mapTo: "Map to",
    map: "Map",
    mapping: "Mapping…",
    chooseEntity: "Entity for {{code}}",
    mapped_one: "{{code}} mapped. {{count}} person left the queue.",
    mapped_other: "{{code}} mapped. {{count}} people left the queue.",
  },
  wiki: {
    docs: {
      noSources: "No source documents recorded.",
      empty: {
        title: "No pages yet",
        body: "Published pages you can read will be listed here.",
      },
      pages: "Pages",
      details: "Page details",
      about: "About this page",
      status: "Status",
      version: "Version",
      trust: "Trust",
      ready: "Ready",
      history: "History",
      allHistory: "View full history",
      contents: "On this page",
      resize: "Resize the Pages panel",
      resizeValue: "{{width}} pixels wide",
      resizeHint: "Drag or use the arrow keys to resize. Double-click to reset.",
      toolbar: "Wiki toolbar",
      trustValues: {
        generated: "Generated",
        internal: "Internal",
        external: "External sources",
        mixed: "Mixed sources",
      },
      writtenBy: "Written by",
      written: {
        agent: "The agent",
        agentRole: "The agent ({{role}})",
        roles: { chat: "chat", reasoning: "reasoning", fast: "fast", vision: "vision" },
        answer: "A saved answer",
        person: "A person",
        mcp: "An MCP client",
        mcpClient: "An MCP client ({{client}})",
        ingestion: "Automatic summary",
        revert: "A revert",
      },
    },
    stewardship: {
      title: "Ownership and review",
      owner: "Owner: {{name}}",
      noOwner: "No owner",
      someone: "A former member",
      verified: "Verified by {{name}} on {{date}}",
      notVerified: "Not verified",
      verify: "Verify this version",
      withdraw: "Withdraw verification",
      changeOwner: "Change owner",
      membersFailed: "The project's members could not be loaded. Try again.",
      stale: {
        label: "May be stale",
        sourceChanged: "A source changed on {{date}}",
        lastVerified_one: "Last verified {{count}} day ago",
        lastVerified_other: "Last verified {{count}} days ago",
        published_one: "Published {{count}} day ago, never verified since",
        published_other: "Published {{count}} days ago, never verified since",
      },
      refused: {
        notFound: "This person can't own the page: an owner is a project member who can read it.",
        forbidden: "Your role doesn't allow this change.",
        frozen: "This project is frozen: its wiki can't change.",
        newer: "A newer version was published. Open it to verify it.",
      },
      ownerDialog: {
        title: "Owner of {{title}}",
        body: "The owner answers for the page. It changes no version and gives no access.",
        field: "Owner",
        nobody: "No owner",
        cancel: "Cancel",
        save: "Save owner",
        saving: "Saving…",
      },
    },
    okfChecks: {
      warnings: "Before publishing",
      errors: {
        title_empty: "The page needs a title.",
        title_too_long: "The title is longer than 200 characters.",
        title_link_syntax: "The title can't contain [[ or ]].",
        title_line_break: "The title must fit on one line.",
        front_matter:
          "The page can't start with a front-matter block (---). Remove it or add a line above it.",
      },
      warning: {
        unresolved_link: "[[{{target}}]] does not open a page you can read",
        heading_repeats_title: "The first heading repeats the title",
        heading_level_skipped: "A heading level is skipped at “{{target}}”",
        summary_without_source: "This summary has no source",
      },
    },
    folderIndex: {
      contents: "Contents of {{folder}}",
      empty: {
        title: "No pages in this folder",
        body: "Published pages you can read in this folder will be listed here.",
      },
    },
    breadcrumb: {
      label: "Where this page is",
      wiki: "Wiki",
      folder: "Pages in “{{folder}}”",
      empty: "No pages you can read here",
    },
    neighbours: {
      label: "Pages in this folder",
      previous: "Previous",
      next: "Next",
    },
    history: {
      passage: {
        title: "History of this passage",
        action: "History",
        where: "In “{{section}}” · {{page}}",
        steps: "How this passage came to be",
        introduced: "Introduced in",
        writtenBy: "written by {{name}}",
        proposedBy: "Proposed by {{name}}",
        approvedBy: "approved by {{name}}",
        publishedBy: "published by {{name}}",
        discussion_one: "{{count}} discussion on this passage in the review",
        discussion_other: "{{count}} discussions on this passage in the review",
        noThreads: "Nobody discussed this passage in the review.",
        open: "Still open",
        onText: "On the text then:",
        none: "No earlier history of this passage is available to you.",
        member: "a member",
      },
      review: {
        toggle: "What changed since my last review",
        against: "Changes against the text you reviewed",
        revised: "Revised since your review",
        unchanged: "Unchanged since your review",
        new: "New since your review",
        decision: {
          changes_requested: "you requested changes {{at}}",
          approved: "you approved it {{at}}",
          rejected: "you rejected it {{at}}",
        },
      },
      read: {
        line: "This page changed since you last read it (version {{version}})",
        show: "Show changes",
        hide: "Hide changes",
        dismiss: "Dismiss",
        unavailable: "The changes could not be loaded.",
      },
      current: "Current",
      changes: "Changes",
      changesOf: "Changes in version {{version}}",
      select: "Select version {{version}} to compare",
      compare: "Compare",
      compareHint: "Select two versions to compare them.",
      restore: "Restore this version",
      restoreVersion: "Restore version {{version}}",
      actions: "More actions for version {{version}}",
      compareWithCurrent: "Compare with current",
    },
    compare: {
      title: "Compare versions",
      range: "Version {{from}} to version {{to}}",
      titleChange: "Title: {{from}} → {{to}}",
      typeChange: "Type: {{from}} → {{to}}",
      noChanges: "These two versions have the same content.",
      tooLarge: "These versions differ too much to compare line by line. Both are shown in full.",
      notFound: "These versions can't be compared with your access, or they no longer exist.",
      backToPage: "Back to the page",
      openVersion: "Open version {{version}}",
    },
    restore: {
      pageTitle: "Restore version {{version}} of {{title}}?",
      pageBody:
        "This creates a change set that restores version {{version}}. The page doesn't change until the change set is reviewed and published.",
      setTitle: "Revert {{title}}?",
      setBody_one:
        "This creates a change set that restores the page it changed. Nothing changes until the change set is reviewed and published.",
      setBody_other:
        "This creates a change set that restores the {{count}} pages it changed. Nothing changes until the change set is reviewed and published.",
      confirm: "Create change set",
      working: "Creating…",
      cancel: "Cancel",
      revert: "Revert this change set",
      refused: {
        version: "This version can't be restored with your access.",
        changeSet: "This change set can't be reverted with your access.",
        current: "This is already the current version of the page.",
        frozen: "This project is frozen. Nothing can be changed until it is unfrozen.",
        notPublished:
          "This change set is no longer published, or the page changed. Reload and try again.",
        forbidden: "Your role can't propose wiki changes in this project.",
      },
    },
    // The conversation's Wiki changes card and wiki side panel (wiki-collaboration D8, D8a).
    reading: {
      views: "Show the page",
      view: { marked: "Marked", clean: "Clean", source: "Source" },
      tooLarge: "This page is too large to mark: it opens as its line changes.",
      inserted: "inserted",
      removed: "removed",
    },
    sizes: {
      created_one: "{{count}} new page",
      created_other: "{{count}} new pages",
      edits_one: "{{count}} edit",
      edits_other: "{{count}} edits",
      renames_one: "{{count}} rename",
      renames_other: "{{count}} renames",
      moves_one: "{{count}} move",
      moves_other: "{{count}} moves",
      deletions_one: "{{count}} deletion",
      deletions_other: "{{count}} deletions",
      folders_one: "{{count}} folder",
      folders_other: "{{count}} folders",
      lines: "+{{added}} −{{removed}}",
      newLines_one: "{{count}} line",
      newLines_other: "{{count}} lines",
      none: "No changes",
    },
    author: {
      title: "Wiki draft",
      titleOnlyYou: "Wiki draft · only you",
      kind: {
        created: "New page",
        edits: "Edit",
        renames: "Rename",
        moves: "Move",
        deletions: "Deletion",
        folders: "Folder",
      },
      structure: "Structure",
      more_one: "and {{count}} more change",
      more_other: "and {{count}} more changes",
      decides: {
        you: "You may publish in this project",
        requester: "Someone who may publish decides",
        peer: "Someone other than the author decides",
        manager: "A manager or the owner decides",
        own_edits: "Someone else must decide: you edited a page",
        waiting: "Waiting for your answer",
      },
      preview: "Preview",
      publish: "Publish",
      submit: "Submit for review",
      submitInstead: "Submit for review instead",
      approvePublish: "Approve and publish",
      viewChanges: "View changes",
      updatedBelow: "Wiki draft updated below",
      revised: "Revised: the changed pages need a new review",
      settled: {
        open: "Submitted for review",
        changes_requested: "Changes requested",
        published: "Published",
        discarded: "Discarded",
      },
      previewTitle: "Preview · your draft",
      openFull: "Open full preview",
      onlyYou: "Only you see this draft. Select a passage to instruct the agent.",
      readOnly: "Submitted: you see it as reviewers will. Select a passage to instruct the agent.",
      changedPages: "Changed pages",
      publishSheet: {
        title_one: "Publish {{count}} change to the wiki?",
        title_other: "Publish {{count}} changes to the wiki?",
        explain: "What will be published, and who will read each page.",
        sameReaders: "Same readers",
        unchanged: "content unchanged",
        footnote:
          "Readers follow each page's sources. This project lets you publish your own changes; it is recorded in Activity.",
        stale: "{{pages}} changed meanwhile. Nothing was published: open the preview to update it.",
        declassifyHint: "A page could be published to wider readers than its sources allow.",
        declassify: "Declassify in the full publish dialog",
      },
      instruct: {
        action: "Instruct the agent",
        hint: "Goes to the agent in this conversation, not to people.",
        notSent: "not sent yet",
        label: "Instruction for the agent",
        add: "Add instruction",
        cancel: "Cancel",
        tooLong: "Up to 1,000 characters.",
        full: "Up to 20 instructions: send these first.",
      },
      tray: {
        title_one: "{{count}} instruction for the agent",
        title_other: "{{count}} instructions for the agent",
        onlyYou: "Only you see them",
        remove: "Remove the instruction on “{{quote}}”",
        clear: "Clear",
        send: "Send to the agent",
        tooLong: "Too long for one message: remove an instruction or shorten one.",
      },
      sent: {
        title_one: "Revise the wiki draft · {{count}} instruction",
        title_other: "Revise the wiki draft · {{count}} instructions",
        on: "On {{where}}",
      },
    },
    card: {
      title: "Wiki changes",
      untitled: "Untitled change set",
      pages_one: "{{count}} page",
      pages_other: "{{count}} pages",
      folders_one: "{{count}} folder",
      folders_other: "{{count}} folders",
      stale_one: "{{count}} stale",
      stale_other: "{{count}} stale",
      workingNote: "In no one else's review queue until you submit it",
      review: "Review",
      submit: "Submit",
      discard: "Discard",
      discardTitle: "Discard these wiki changes?",
      discardBody: "The change set is closed. No page changes.",
      settled: {
        open: "Submitted for review",
        changes_requested: "Sent back for changes",
        published: "Published",
        discarded: "Discarded",
      },
      openReview: "Open the review",
      unavailable: "This change set is no longer available to you.",
    },
    panel: {
      label: "Wiki: {{title}}",
      back: "Back",
      close: "Close the wiki panel",
      openInWiki: "Open full page",
      knowledge: "Knowledge",
      onThisPage: "On this page",
      discussion_one: "{{count}} open discussion · on the full page",
      discussion_other: "{{count}} open discussions · on the full page",
      discussionNone: "Discussion · on the full page",
      openReview: "Open the review",
      notFound: "This page doesn't exist or you can't read it.",
      linkedFrom: "Linked from",
      history: "Versions",
      versionLine: "Version {{version}} · {{date}}",
      showCurrent: "Show the current version",
      items: "Items",
    },
    changes: {
      notFound: "This change or page is unavailable to you.",
      discardBody: "This change moves to Closed. No page is changed.",
      discardComment: "Comment (optional, shown in Closed)",
      discardCommentDropped:
        "Discarded. Your comment was not kept: this server does not take comments on a discard yet.",
      discardedBy: "Discarded by {{name}} · {{at}}",
      discardedUnused: "Discarded unused · {{at}}",
      publishedPages: "Published pages",
      versionOf: "{{title}} · version {{version}}",
      edit: "Edit page",
      save: "Save changes",
      sources: "Sources",
      regenerate: "Regenerate",

      review: "Review",
      activity: "Activity",
      hiddenTitle: "Change with hidden pages",
      pages_one: "{{count}} page",
      pages_other: "{{count}} pages",
      hidden:
        "Some pages are hidden from you. Someone who can read them must review this change before publication.",
      hiddenActivity: "Some change details are hidden from you.",
      blockedStale: "Review these conflicting pages before publishing: {{pages}}",
      approveAll: "Approve all",
      approve: "Approve",
      reject: "Reject",
      reopen: "Reopen",
      sendBack: "Send back",
      comment: "What needs to change?",
      changedPages: "Changed pages",
      allPages: "All pages",
      approvedBy: "Approved by {{name}}",
      rejectedBy: "Rejected by {{name}}",
      publishedBy: "Published by {{name}} · {{at}}",
      rebased: "Rebased from version {{version}}. Review and approve again.",
      clearsApproval: "Saving clears the previous approval.",
      reconcile: "Reconcile this proposal with the current page before saving.",
      noSources: "No source documents. Readers are checked before publishing.",
      openPublished: "Open published version",
      source: "Show Markdown",
      rendered: "Read page",
      root: "Wiki root",
      moveFrom: "Move from {{folder}}.",
      moveTo: "Move to {{folder}}. The content and version stay the same.",
      deleting: "This page will be deleted. Its earlier versions are retained.",
      proposedTitle: "Proposed title: {{title}}",
      before: "Current version",
      after: "Proposed version",
      unsaved: "Discard unsaved edits?",
      keepEditing: "Keep editing",
      discardEdits: "Discard edits",
      publishExplain:
        "Review who can read each page before publishing. Nothing changes until you confirm.",
      includePending: "Include pending pages",
      approveFirst: "Approve a page or choose Include pending pages.",
      audienceUnchanged: "This operation preserves the page audience.",
      noActivity: "Published and discarded changes will appear here.",
      older: "Show older",
      // wiki-collaboration: the working change set, submission, folder items, the check.
      working:
        "Working: the agent may still change this set. Nothing reaches other reviewers until you submit it.",
      workingOther: "Working: its author has not submitted it for review yet.",
      submit: "Submit for review",
      submitted: "Submitted for review.",
      submitExplain: "Its pages are checked against their sources when you submit.",
      publishNeedsRole: "Someone who may publish in this project reviews it once you submit.",
      mcpPublisher: {
        notYou:
          "Your MCP client proposed this change, so someone else who may publish in this project reads and publishes it.",
        others:
          "Proposed through an MCP client: someone other than {{name}} who may publish in this project publishes it.",
        anyone:
          "Proposed through an MCP client. This project lets its author publish it too; that is recorded in the activity log.",
      },
      notChecked: "Not checked",
      notCheckedWorking: "Not checked until submitted",
      notCheckedReason: {
        spend_limit: "Not checked: the spending limit was reached",
        check_failed: "Not checked: the check failed",
        timed_out: "Not checked: the check timed out",
      },
      folderItem: "Folder",
      folder: {
        folder_create: "Creates the folder {{path}}. Existing folders with this name are reused.",
        folder_rename: "Renames {{path}} to {{name}}.",
        folder_move: "Moves {{path}} into {{target}}.",
        folder_delete:
          "Deletes {{path}}. Its pages and subfolders move up to its parent; no page is deleted.",
      },
      folderMissing: "This folder no longer exists. Reject this item to publish the rest.",
      inFolder: "In {{path}}",
      // wiki-collaboration slice B: change requests, trust levels.
      changesRequested: "Changes requested",
      approveBlocked: "Resolve the change request on this page before approving it.",
      publishBlocked: "Resolve the open change requests before publishing.",
      skipped_one: "{{count}} page skipped: changes requested",
      skipped_other: "{{count}} pages skipped: changes requested",
      skippedOwn_one: "{{count}} page skipped: you edited it",
      skippedOwn_other: "{{count}} pages skipped: you edited them",
      trustOwnEdits:
        "You edited a page of this change set: another contributor approves it and publishes the set.",
      trustRefused: {
        requester: "You may not approve or publish wiki changes in this project.",
        peer: "This project needs another contributor to approve and publish these changes.",
        manager: "This project needs a manager to approve and publish wiki changes.",
      },
      state: {
        working: "Working",
        open: "Open",
        changes_requested: "Changes requested",
        published: "Published",
        discarded: "Discarded",
      },
      itemState: {
        pending: "Pending",
        approved: "Approved",
        rejected: "Rejected",
        stale: "Stale",
        published: "Published",
      },
      op: {
        create: "New page",
        edit: "Edit",
        rename: "Rename",
        move: "Move",
        delete: "Delete",
        folder_create: "New folder",
        folder_rename: "Rename folder",
        folder_move: "Move folder",
        folder_delete: "Delete folder",
      },
      origin: {
        person: "Person",
        agent: "Agent",
        answer: "Saved answer",
        mcp: "Connected client",
        ingestion: "Source summary",
        revert: "Revert",
      },
      stale: {
        overlap: "The page changed in the same place. Edit or regenerate this proposal.",
        too_large:
          "The change is too large to reconcile automatically. Review it against the current page.",
        deleted: "The page was deleted. This proposal cannot be published.",
        missing: "The page is unavailable. This proposal cannot be published.",
      },
    },
    threads: {
      item: "Discussion on this page",
      set: "Discussion on the whole change set",
      none: "No comments yet.",
      kind: { comment: "Comment", question: "Question", change_request: "Changes requested" },
      kindLabel: "Kind",
      kindOption: { comment: "Comment", question: "Question", change_request: "Request changes" },
      forAgent: "Ask the agent to revise",
      forAgentHint:
        "Its author gets a Revise card in their conversation; nothing runs until they send it.",
      body: "Comment",
      bodyItem: "Comment on this page",
      bodySet: "Comment on the change set",
      post: "Post",
      reply: "Reply",
      replyLabel: "Your reply",
      resolve: "Resolve",
      reopen: "Reopen",
      resolvedBy: "Resolved by {{name}}",
      resolvedWithNote: "Resolved by {{name}} · {{note}}",
      note: {
        label: "Decision note (optional)",
        placeholder: "Accepted / rejected because …, affects …",
        hint: "One line, kept with the thread for everyone who can read it.",
        confirm: "Resolve",
        replyConfirm: "Reply & resolve",
        tooLong: "Keep it to {{max}} characters.",
      },
      deleted: "Comment deleted",
      deleteComment: "Delete comment",
      deleteBody:
        "Its text is removed for everyone. A note that it was deleted stays in the thread.",
      agentRevised: "Revised by the agent",
      askedAgent: "Asked the agent to revise",
      invalid: "A comment needs 1 to 2,000 characters.",
      readOnly: "This change set is closed: its discussion is read-only.",
      someone: "Someone",
    },
    pending: {
      toggle: "Pending changes",
      shortcut: "Shift+P",
      banner_one: "{{count}} pending change touches this page",
      banner_other: "{{count}} pending changes touch this page",
      view: "View",
      viewSet: "View “{{title}}”",
      section: "A pending change touches this section",
      viewUnnamed: "View the pending change",
      unnamed: "A change set",
    },
    pageThreads: {
      title: "Discussion",
      open_one: "{{count}} open",
      open_other: "{{count}} open",
      none: "No discussion on this page yet.",
      comment: "Comment on the page",
      body: "Comment on the page",
      bodyHeading: "Comment on this section",
      onPage: "On the page",
      onHeading: "On “{{heading}}”",
      earlier: "On an earlier version",
      detached: "Detached · on",
      onVersion: "version {{version}}",
      visibleTo_one: "Visible to the {{count}} person who can read this version",
      visibleTo_other: "Visible to the {{count}} people who can read this version",
      visibleToReaders: "Visible to the people who can read this version",
      whoResolves: "Its author, the page’s owner or someone who may publish resolves it.",
      paneLabel: "Page discussion",
      onPassage: "On a passage",
      ask: "Ask the agent",
      asked: "Asked the agent · Open the conversation",
      commentOn: "Comment on “{{heading}}”",
      headingThreads_one: "{{count}} open discussion on “{{heading}}”",
      headingThreads_other: "{{count}} open discussions on “{{heading}}”",
      dialogTitle: "Discussion on “{{heading}}”",
      dialogDescription: "Members who can read this page see it. Plain text only.",
    },
    revision: {
      title: "Revision requested",
      by: "{{name}} asks the agent to revise “{{item}}”",
      revise: "Revise",
      sending: "Sending…",
      settled: "Revision of “{{item}}” sent to the agent · requested by {{name}}",
      openReview: "Open the review",
      someone: "A reviewer",
      passage: "On the passage",
    },
    // Resolve with the agent (wiki-resolve-with-agent D4, D6, D7, D10).
    resolve: {
      trigger: "Resolve with the agent",
      triggerLabel_one: "Resolve with the agent: {{count}} comment",
      triggerLabel_other: "Resolve with the agent: {{count}} comments",
      triggerOf: "{{count}} of {{of}}",
      triggerLabelOf: "Resolve with the agent: {{count}} of {{of}} comments, the oldest",
      triggerHint:
        "The agent revises this draft and drafts a reply to each comment. You send the replies.",
      running: "The agent is resolving the comments",
      sent_one: "Resolve {{count}} comment on “{{title}}” sent to the agent",
      sent_other: "Resolve {{count}} comments on “{{title}}” sent to the agent",
      sentUntitled_one: "Resolve {{count}} comment sent to the agent",
      sentUntitled_other: "Resolve {{count}} comments sent to the agent",
      revision: "Agent revision {{revision}}",
      revisionPending: "Agent revision",
      progress: "Resolution progress",
      step: {
        read_one: "Read {{count}} comment",
        read_other: "Read {{count}} comments",
        revise: "Revising pages",
        draft: "Drafting replies",
        ready: "Ready to review",
      },
      live: {
        pages_one: "{{count}} page revised",
        pages_other: "{{count}} pages revised",
        replies_one: "{{count}} reply drafted",
        replies_other: "{{count}} replies drafted",
      },
      nothingSent: "Nothing reaches the reviewers until you send the replies.",
      ready: "Replies ready: {{counts}}",
      notSent_one: "{{count}} reply drafted, not sent",
      notSent_other: "{{count}} replies drafted, not sent",
      counts: {
        changed_one: "{{count}} changed",
        changed_other: "{{count}} changed",
        question_one: "{{count}} question",
        question_other: "{{count}} questions",
        noChange_one: "{{count}} no change",
        noChange_other: "{{count}} no change",
      },
      none: "no reply drafted",
      review: "Review replies",
      sendAll: "Send all",
      settledSent: "replies sent",
      settledUndone: "undone",
      failed: "The agent could not finish this revision. Nothing was sent to the reviewers.",
      unanswered_one: "{{count}} comment not answered by the agent: reply in the review",
      unanswered_other: "{{count}} comments not answered by the agent: reply in the review",
      panel: {
        hint: "Replies are drafts only you can see. Edit, send or remove each one.",
        back: "Back to the preview",
        theme_one: "{{theme}} · {{count}} comment",
        theme_other: "{{theme}} · {{count}} comments",
        from: "{{name}} · {{kind}}",
        on: "on “{{quote}}”",
        choices: "Choices: {{list}}",
        send: "Send",
        edit: "Edit",
        editLabel: "The reply to {{name}}",
        choiceLabel: "Choice {{n}}",
        save: "Save",
        remove: "Remove",
        sent: "Sent",
        removed: "Removed",
        drafts_one: "{{count}} draft · each reviewer gets one notification",
        drafts_other: "{{count}} drafts · each reviewer gets one notification",
        noDrafts: "No draft left to send.",
        unanswered: "Not answered by the agent",
        otherPages: "Other pages this revision changed",
        unansweredItem: "{{page}}: reply by hand in the review",
        theSet: "The change set",
        undoPage: "Undo this page's changes",
        undone: "Changes undone",
        undoTitle: "Undo the agent's changes to “{{title}}”?",
        undoBody:
          "The page goes back to its text before this revision and its replies are removed. The other pages keep the revision.",
        undoConfirm: "Undo the changes",
        unavailable: "This revision is no longer available.",
      },
      reply: {
        label: "The agent's reply",
        forName: "The agent, for {{name}}",
        forSomeone: "The agent, for the author",
        draft: "Draft · only you see it",
        outcome: {
          changed: "Changed",
          question: "Question for {{name}}",
          questionYou: "Question for you",
          no_change: "No change",
        },
        seeChange: "See the change",
        choices: "Answers you can pick",
        answer: "Answer…",
        waiting: "Waiting for {{name}}",
        waitingYou: "Waiting for your answer · {{name}} runs the agent again with it",
        hidden: "The agent replied using sources you can't read.",
        hiddenAsk: "The agent replied using sources you can't read: ask {{name}}.",
        someone: "the reviewer",
      },
      bar: {
        sent: "Agent revision {{revision}} · {{counts}}",
        drafted: "Agent revision {{revision}} · replies not sent yet",
        menu: "More review actions",
        resolveAnswered: "Resolve answered ({{count}})",
        resolveAnsweredHint: "Changed and No change replies. Questions stay open.",
        resolveTitle_one: "Resolve {{count}} answered thread",
        resolveTitle_other: "Resolve {{count}} answered threads",
        resolveBody:
          "Each thread is resolved in your name with the same note. Threads with a question stay open.",
        resolved_one: "{{count}} thread resolved",
        resolved_other: "{{count}} threads resolved",
      },
    },
    title: "Wiki",
    openWiki: "Open the wiki",
    backToProject: "Back to the project",
    tabs: { pages: "Pages", review: "Review queue", declassifications: "Declassifications" },
    newPage: "New page",
    noPages: "No pages you can read yet. Save an answer to the wiki, or write a page.",
    empty: {
      title: "The project's knowledge, written down",
      body: "The wiki holds what this project knows: reviewed pages that cite their sources. Write the first page, or start a conversation and save a good answer to the wiki.",
      bodyRead:
        "The wiki holds what this project knows: reviewed pages that cite their sources. Its pages will be listed here once they are published.",
      startConversation: "Start a conversation",
      sources: "Browse the sources",
      waiting_one: "{{count}} draft is waiting for review",
      waiting_other: "{{count}} drafts are waiting for review",
    },
    overview: {
      title: "Wiki overview",
      pages_one: "{{count}} page",
      pages_other: "{{count}} pages",
      recent: "Recently updated",
      updated: "Updated {{date}}",
      review: "Waiting for review",
      waiting_one: "{{count}} change is waiting for review",
      waiting_other: "{{count}} changes are waiting for review",
      nothingWaiting: "Nothing for you to review",
      status: { uploading: "Uploading", processing: "Processing", failed: "Failed" },
    },
    needsChecking: {
      title: "Need checking",
      filter: "Pages that need checking",
      clear: "Show all pages",
      none: "No page needs checking.",
      reason: {
        source_changed: "A source changed",
        review_due: "Review due",
        unverified: "Changed since it was verified",
      },
    },
    pages: "Pages",
    log: "Recent publications",
    version: "Version {{version}}",
    versionBy: "Version {{version}} by {{author}}, {{at}}",
    versionAt: "Version {{version}}, {{at}}",
    newerVersion: "A newer version of this page is available.",
    openCurrent: "Open the current version",
    processing: "Being indexed: it can be cited once ready.",
    declassified: "Contains declassified content",
    declassifiedExplain:
      "A project manager or the owner published this version to readers who cannot read all of its sources.",
    backlinks: "Linked from",
    noBacklinks: "No page you can read links here.",
    provenance: "Sources (owner and managers)",
    unreadableSource: "A source you cannot read",
    sourceDeclassified: "declassified",
    edit: "Propose a change",
    delete: "Delete page",
    editor: {
      title: "Title",
      pageType: "Page type",
      markdown: "Content (Markdown)",
      preview: "Preview",
      saving: "Saving…",
      cancel: "Cancel",
    },
    pageEditor: {
      new: "New page",
      newHint:
        "This proposes a new page. It goes to review: nothing appears under Pages until it is published.",
      change: "Propose a change to “{{title}}”",
      changeHint:
        "Your change is made against version {{version}}. It goes to review: the page stays as it is until it is published.",
      why: "Why (optional)",
      whyHint: "Shown to reviewers with the proposal.",
      previewEmpty: "The preview appears as you write.",
      save: "Save for review",
      publish: "Publish",
      noAccess: "You can read this wiki but not propose pages in it.",
      frozen: "This project is frozen: its wiki cannot change.",
    },
    pageTypes: { summary: "Summary", concept: "Concept", entity: "Entity", other: "Page" },
    draft: {
      provenance: {
        someone: "someone",
        untitled: "an untitled conversation",
      },
      sourceDeleted: "Source deleted",
      changesAgainst: "Changes against version {{version}}",
      added: "added",
      removed: "removed",
      unchanged_one: "{{count}} unchanged line",
      unchanged_other: "{{count}} unchanged lines",
      tooLarge:
        "This page is too large to compare line by line. The draft is shown as it would be published.",
      noChanges: "No change against the current version.",
      publish: "Publish",
    },
    inbox: {
      title: "Changes",
      views: "Change views",
      view: {
        toDecide: "To decide",
        mine: "Mine",
        following: "Following",
        closed: "Closed",
      },
      countSr_one: "{{count}} waiting",
      countSr_other: "{{count}} waiting",
      loading: "Loading changes…",
      alerts: "Source alerts",
      someone: "Someone",
      who: {
        agent: "Agent for {{name}}",
        answer: "Saved answer · {{name}}",
        mcp: "{{name}} through a connected client",
        revert: "Revert by {{name}}",
        ingestion: "Source summary",
      },
      when: {
        submitted: "submitted {{ago}}",
        started: "started {{ago}}",
        sentBack: "changes requested {{ago}}",
        sentBackBy: "changes requested by {{name}} {{ago}}",
      },
      state: {
        working: "Draft · not submitted",
        submitted: "Submitted",
      },
      why: {
        requester: "You may publish in this project",
        peer: "Someone other than the author must decide",
        manager: "A manager or the owner decides",
        own_edits: "Someone else must decide: you edited a page",
        waiting: "Waiting for your answer",
        publisher: "Someone who may publish decides",
        author: "Waiting for its author",
      },
      risk: {
        changeRequests_one: "{{count}} change request open",
        changeRequests_other: "{{count}} change requests open",
        stale_one: "{{count}} page changed since the proposal",
        stale_other: "{{count}} pages changed since the proposal",
        hidden: "Some pages are hidden from you",
      },
      search: "Search closed changes",
      closed: {
        published: "Published by {{name}} · {{at}}",
        discarded: "Discarded by {{name}} · {{at}}",
        swept: "Discarded unused · {{at}}",
        hidden_one: "+{{count}} change you can’t see",
        hidden_other: "+{{count}} changes you can’t see",
        comment: "Comment:",
      },
      empty: {
        toDecide: {
          title: "Nothing to decide",
          body: "Change sets you may approve and publish appear here once someone submits them.",
        },
        mine: {
          title: "No changes of yours",
          body: "Drafts you or your agent start, and the ones you submitted, appear here until they are decided.",
        },
        following: {
          title: "Nothing to follow",
          body: "Open change sets someone else decides, or that you commented on, appear here.",
        },
        closed: {
          title: "No closed changes yet",
          body: "Published and discarded change sets appear here, newest first.",
          search: "No closed change matches “{{query}}”.",
          recent: "No recent closed change you can see.",
        },
      },
    },
    review: {
      waitingChangeRequests_one: "{{count}} change request for you",
      waitingChangeRequests_other: "{{count}} change requests for you",
      waitingQuestions_one: "{{count}} question for you",
      waitingQuestions_other: "{{count}} questions for you",
      contradictions_one: "{{count}} possible contradiction",
      contradictions_other: "{{count}} possible contradictions",
      stale: "The page changed since this proposal: open it to edit or regenerate it.",
      review: "Review",
      discard: "Discard",
      resolve: "Mark as reviewed",
      back: "Changes",
      summaryMenu: "More actions on this change set",
      requestedBy: "Changes requested by {{name}}",
      requestBody:
        "The change set goes back to its author with your comment. It cannot be published until the request is resolved.",
      requestLabel: "What should change?",
      structureNote:
        "Pages moved and folders created or changed. Content and versions are unchanged.",
      outline: {
        content: "Content",
        structure: "Structure",
        removals: "Removals",
        tree: "Folders and moves",
        flags_one: "{{count}} possible contradiction",
        flags_other: "{{count}} possible contradictions",
        menu: "Decide on “{{title}}”",
        open_one: "{{count}} change",
        open_other: "{{count}} changes",
      },
      margin: {
        flag: "Possible contradiction",
        flagHint: "Approving this page acknowledges it.",
      },
      passage: {
        label: "Comment on the selected passage",
        action: { comment: "Comment", question: "Question", change_request: "Request a change" },
        comment: "Comment on this passage",
        question: "Ask about this passage",
        change_request: "Request a change to this passage",
        askAgent: "and ask the agent to revise",
        visible: "Visible to the people who can read this page in the review.",
      },
      onPage: "On the page:",
      line: {
        contradiction: "Possible contradiction",
        request: "Changes requested",
        source: "with {{title}}",
        who_one: "{{name}} · {{count}} reply",
        who_other: "{{name}} · {{count}} replies",
        open: "Open",
        opened: "Opened",
      },
      marker: {
        flag: "Possible contradiction: open it",
        thread_one: "{{kind}} by {{name}}, {{count}} comment",
        thread_other: "{{kind}} by {{name}}, {{count}} comments",
      },
      stepper: {
        label: "Open items on this page",
        previous: "Previous open item",
        next: "Next open item",
        of: "{{index}} of {{count}} open",
        open_one: "{{count}} open",
        open_other: "{{count}} open",
      },
      dock: { bottom: "Dock to bottom", side: "Dock to side" },
      pane: {
        label: "Review item",
        close: "Close the item",
        on: "On",
        onPage: "On the page",
        jump: "Jump to the passage",
        replies_one: "{{count}} reply",
        replies_other: "{{count}} replies",
        whoResolves: "Its author, or someone who may decide on this change set, resolves it.",
        replyPlaceholder: "Reply…",
        visible: "Everyone who can see this change set",
        replyResolve: "Reply & resolve",
        pageSays: "The page says",
        sourceSays: "The source says",
        sourceSaysNamed: "The source says · {{title}}",
        openSource: "Open the source",
        acknowledges:
          "Approving the page acknowledges this contradiction, and the audit records it.",
        commentOn: "Comment on it",
        askAgent: "Ask the agent to fix it",
        commentLabel: "Comment on this passage",
        agentLabel: "What should the agent change?",
      },
      sheet: { expand: "Show the item", collapse: "Hide the item" },
      strip: {
        views: "Review views",
        changes: "Changes",
        discussion: "Discussion",
        inline: "Show issues inline",
        viewMenu: "View options",
        page: "Change {{index}} of {{count}}: {{title}}",
        position: "{{index}} of {{count}}",
        new: "New",
        removed: "Removed",
      },
      discussion: {
        onPages: "On the pages",
        requests_one: "{{count}} change request",
        requests_other: "{{count}} change requests",
        questions_one: "{{count}} question",
        questions_other: "{{count}} questions",
        comments_one: "{{count}} comment",
        comments_other: "{{count}} comments",
      },
      bar: {
        label: "Decision",
        request: "Request changes",
        approvePublish: "Approve and publish",
        approve: "Approve",
        stale_one: "{{count}} page changed since this proposal",
        stale_other: "{{count}} pages changed since this proposal",
        setRequests_one: "{{count}} change request on the whole change set (Discussion)",
        setRequests_other: "{{count}} change requests on the whole change set (Discussion)",
        acknowledges_one: "Approving acknowledges {{count}} possible contradiction",
        acknowledges_other: "Approving acknowledges {{count}} possible contradictions",
      },
      kind: {
        source_changed: "Source changed, page may be stale",
        contradiction: "Possible contradiction with a source",
        redeclassify: "Re-review: a declassified source changed",
      },
    },
    publish: {
      title: "Publish to the wiki",
      titleOf: "Publish “{{title}}”",
      newPage: "Creates a new page.",
      newVersion: "Publishes a new version of “{{title}}”.",
      readers_one: "{{count}} person will be able to read it",
      readers_other: "{{count}} people will be able to read it",
      readersExplain:
        "Only people who can read every source of this page. If a source is narrowed later, so is the page.",
      classification: "Classification",
      widen: "Publish with wider readers (declassify)",
      widenExplain:
        "The page would reach the whole project ({{count}} people). These sources limit who can read it:",
      cannotRead_one: "{{count}} intended reader cannot read this source",
      cannotRead_other: "{{count}} intended readers cannot read this source",
      reason: "Reason (recorded in the audit log)",
      irreversible:
        "Anyone who reads the page before you withdraw the declassification keeps what they read. Withdrawing only stops further reads.",
      unavailable: {
        not_admin: "Only a project manager or the owner can publish with wider readers.",
        forbidden_by_setting: "Declassification is turned off for this project.",
        break_glass: "Break-glass access cannot declassify content.",
        cannot_read_sources: "You cannot declassify content whose sources you cannot all read.",
      },
      submit: "Publish",
      submitWiden: "Declassify and publish",
      publishing: "Publishing…",
      cancel: "Cancel",
      published: "Published.",
      done: "Done",
    },
    declassifications: {
      empty: "No declassification in this project.",
      withdraw: "Withdraw",
      withdrawn: "Withdrawn {{at}}",
      unreadable: "Content you cannot read",
      row: "by {{who}}, {{at}}",
      someone: "someone",
    },
    export: "Export as OKF",
    exporting: "Preparing the export…",
    exportReady: "Download the export",
    exportFailed: "The export failed.",
    saveAsFileInstead: "Save as a file instead",
    saveReviewNote:
      "Creates a draft in the review queue. Nothing is published until someone reviews it.",
    saveToWiki: "Save to wiki",
    savedToWiki: "Draft added to the wiki review queue.",
    reviewDraft: "Review the draft",
    draftTitle: "Draft title",
    draftTitlePlaceholder: "Name this draft",
    saveWeb: {
      title: "Save this answer to the wiki?",
      body: "Part of this answer comes from public web pages. The draft is marked as external: check it before publishing.",
      confirm: "Save to wiki",
      cancel: "Cancel",
    },
    reviewCount_one: "{{count}} item to review",
    reviewCount_other: "{{count}} items to review",
  },
  recommendations: {
    why: "Why?",
    whyLabel: "Why? (optional)",
    reasons: { not_useful: "Not useful", wrong: "Wrong", duplicate: "Duplicate" },
    reasonNoted: "Reason noted.",
    itemRejected: "Page rejected.",
  },
  wikiSuggestion: {
    suggested: "Suggested for the wiki",
    proposed_one: "Proposed wiki page",
    proposed_other: "Proposed wiki pages",
    changeTo: "Change to {{page}}",
    reviewNote: "Goes to review only if you draft it",
    sources_one: "{{count}} source",
    sources_other: "{{count}} sources",
    external: "Includes content from web pages; the draft is marked external",
    showPage: "Show proposed page",
    hidePage: "Hide proposed page",
    draft: "Draft it",
    drafting: "Drafting…",
    dismiss: "Dismiss",
    dismissed: "Suggestion dismissed.",
    drafted: "Draft added to the review queue.",
    gone: "This suggestion is no longer available.",
    disabled: "Wiki suggestions are turned off for this project.",
    frozen: "This project is frozen: no draft can be added.",
    role: "You can no longer draft wiki pages in this project.",
    changed: "The wiki changed while drafting. Try again.",
    loadingPage: "Loading the proposed page…",
  },
  wideBlock: {
    expand: "Expand",
    expandNamed: "Expand {{name}}",
    expandTable: "Expand table",
    expandCode: "Expand code",
    table: "Table",
    code: "Code",
    close: "Close",
    description: "Shown full screen. Escape closes it.",
    zoom: "Zoom",
    zoomIn: "Zoom in",
    zoomOut: "Zoom out",
    level: "Zoom level",
    fit: "Fit",
    actualSize: "Actual size",
    size: "Drawing size",
    panArea: "{{name}}: drag or use the arrow keys to move, + and − to zoom",
    tooSmall: "Too small to read: Actual size or Expand",
    diagram: "Diagram",
    expandDiagram: "Expand diagram",
    diagramTooLarge: "This diagram is too large to draw here. Its source is shown instead.",
    diagramTooMany:
      "Only the first {{count}} diagrams of a page are drawn. Its source is shown instead.",
  },
  charts: {
    showTable: "Show as table",
    showChart: "Show as chart",
    total: "Total",
    share: "Share",
  },
  reports: {
    title: "Reports",
    hint: "Usage, answer quality, cost and capacity, health and security.",
    lead: "Counts only: no message, document or person is ever shown here.",
    tabs: {
      usage: "Usage",
      quality: "Answer quality",
      cost: "Cost and capacity",
      health: "Health and security",
    },
    filters: {
      period: "Period",
      last7: "Last 7 days",
      last30: "Last 30 days",
      last90: "Last 90 days",
      last13Months: "Last 13 months",
      custom: "Custom",
      from: "From",
      to: "To",
      entity: "Entity",
      allEntities: "Whole scope",
      apply: "Apply",
    },
    updated: "Updated {{at}}",
    stale:
      "These figures were last updated {{at}}. They are refreshed every hour; newer activity is not counted yet.",
    neverComputed: "These figures have not been computed yet. They are refreshed every hour.",
    synthetic: "Includes synthetic demo data",
    syntheticBody:
      "Some figures of this period were generated for a demonstration. They are added to real activity and are not real use.",
    entityReset: "That entity is outside your scope. Showing your whole scope.",
    empty: "No activity in this period.",
    day: "Day",
    entity: "Entity",
    project: "Project",
    personalProject: "Personal projects",
    standaloneLine: "Standalone conversations",
    unattributedNote: "Entity and project breakdowns leave out activity that belongs to neither.",
    usage: {
      questions: "Questions",
      conversations: "Conversations started",
      activeUsers: "Active users",
      activeUsersIn: "Active users in {{month}}",
      dailyTitle: "Questions and active users per day",
      dailySummary: "{{questions}} questions over {{days}} days, peak on {{peak}}.",
      dailySummaryEmpty: "No questions over {{days}} days.",
      entitiesTitle: "Questions per entity",
      entitiesSummary: "{{top}} asked the most questions ({{count}}).",
      topProjects: "Top projects by questions",
      topProjectsSummary: "{{top}} leads with {{count}} questions.",
    },
    recommendations: {
      title: "Recommendations",
      lead: "How people decided what the assistant suggested. Counts only: no content, no names.",
      kind: "Recommendation",
      kinds: {
        memory_proposal: "Memory proposals",
        wiki_suggestion: "Wiki suggestions",
        wiki_change_item: "Agent-written wiki pages",
        wiki_change_set: "Agent change sets",
        feedback_draft: "Feedback drafts",
        wiki_thread_reply: "Agent replies on review threads",
      },
      origins: { requested: "asked for", noticed: "unprompted" },
      dismissal: "Dismissal",
      shown: "Shown",
      accepted: "Accepted",
      edited: "Edited",
      dismissed: "Dismissed",
      ignored: "Ignored",
      acceptance: "Acceptance",
      noRate: "—",
      noRateLabel: "Fewer than 10 outcomes",
      version: "{{role}} · {{model}} · prompt {{version}}",
      unknownModel: "Unknown model",
      unknownVersion: "unknown",
      unknownRole: "Unknown role",
      reasonsLine: "Reasons: {{reasons}}",
      noReasons: "No reason given",
      reasonLabel: {
        not_useful: "Not useful",
        wrong: "Wrong",
        duplicate: "Duplicate",
        expired: "Expired",
        conversation_deleted: "Conversation deleted",
        set_closed: "Left at publish",
      },
      note: "Acceptance counts accepted and edited outcomes, shown from 10 outcomes. Personal memory figures of a project or entity with fewer than five active users count in the organisation's total only.",
      empty: "No recommendation was decided in this period.",
      export: "Export JSON",
      exporting: "Exporting…",
      exportFailed: "The export could not be downloaded. Try again.",
    },
    quality: {
      supported: "Supported",
      unverified: "Unverified",
      unsupported: "Unsupported",
      citations: "Citations",
      status: "Citation status",
      sharesTitle: "Citations by status",
      sharesSummary: "{{supported}} of {{total}} citations were supported by their source.",
      sharesEmpty: "No citation in this period.",
      answers: "Answers",
      withoutSource: "Answers with no source",
      withoutSourceHint: "{{share}} of answers",
      ocrFlagged: "Citations of doubtful scans",
      ocrFlaggedHint: "Passages the transcription guard flagged",
      projectsTitle: "Answer quality per project",
    },
    cost: {
      total: "Estimated model cost",
      unknown_one: "{{count}} call of unknown cost, not included",
      unknown_other: "{{count}} calls of unknown cost, not included",
      byRoleTitle: "Estimated cost per day, by model role",
      byRoleSummary: "{{total}} over the period; {{top}} cost the most.",
      roles: "Model roles",
      role: "Role",
      inputTokens: "Input tokens",
      outputTokens: "Output tokens",
      calls: "Calls",
      cost: "Estimated cost",
      roleLabel: {
        chat: "Chat",
        reasoning: "Reasoning",
        fast: "Fast",
        vision: "Vision (scans and images)",
        embedding: "Search indexing",
      },
      capacity: "Capacity",
      ocrPages: "Scanned pages read this month",
      storage: "Stored documents",
      seats: "Active users this month",
      noLimit: "No limit set",
      modelSpend: "Estimated AI spend this month",
      spendWarning: "AI spend is at 80 % or more of this month's limit.",
      spendExceeded:
        "AI spend is past this month's limit. Questions keep working: the limit only warns.",
      spendReachedHard:
        "AI spend reached this month's limit: new questions are refused until next month or until the limit is raised.",
      projectsTitle: "Estimated cost per project",
      storedInScope: "Stored documents in scope: {{size}}",
      toolsTitle: "Outbound tools (apart from model cost)",
      tool: "Tool",
      toolLabel: { webSearch: "Web search", codeExecution: "Code execution" },
      originsTitle: "Estimated AI cost by origin",
      origin: "Origin",
      originLabel: { api: "ESI app", worker: "Background processing", mcp: "MCP", other: "Other" },
    },
    health: {
      ingestion: "Documents",
      ingestionFailures: "Failed documents",
      failuresTitle: "Failed documents by reason",
      failuresSummary: "{{count}} documents failed; most often: {{top}}.",
      reason: "Reason",
      otherReason: "Other reason",
      uploading: "Upload incomplete",
      processing: "Waiting to be processed",
      models: "Model calls",
      modelFailures: "Failed",
      modelUnavailable: "No model available",
      modelFallbacks: "Served by a fallback model",
      access: "Access",
      notFound: "Refused content requests",
      downloadDenied: "Refused downloads",
      flowDenied: "Flow-check denials",
      toolCallsBlocked: "Tool calls blocked",
      toolCallsDeclined: "Tool calls declined",
      governance: "Exceptional access",
      breakGlass: "Break-glass accesses",
      declassificationsCreated: "Declassifications",
      declassificationsWithdrawn: "Declassifications withdrawn",
      seeEvents: "See the events",
      projectsTitle: "Health and security per project",
    },
  },
  governance: {
    memories: {
      title: "Project memories",
      lead: "Facts the project's team agreed on. You see the text only of memories whose sources you can read.",
      empty: "This project has no agreed memory.",
      hidden: "Hidden: relies on sources you can't read",
      sources_one: "{{count}} source",
      sources_other: "{{count}} sources",
      unverified: "Unverified",
      lastUsed: "Last used {{date}}",
      neverUsed: "Not used yet",
    },
    pending: {
      title: "Pending owner approval",
      sent: "Sent to the project's owner for approval. Nothing changes until the owner or a project manager approves.",
      detail:
        "{{change}}. The owner or a project manager decides; the request expires on {{date}}.",
      group_one: "It also waits on {{count}} other project.",
      group_other: "It also waits on {{count}} other projects.",
      reasonLabel: "Reason for the owner (optional)",
      reasonSave: "Send reason",
      reasonSaved: "Reason sent to the owner.",
      breakGlass: "Use break-glass access instead",
      breakGlassHelp:
        "Break-glass access lets you read the project now, as a Reader. It is audited, and the project's owner and managers see it with your reason.",
      breakGlassTitle: "Break-glass access to {{name}}",
      breakGlassReason: "Reason (required)",
      breakGlassConfirm: "Open as Reader",
      inList: "Pending owner approval",
    },
    mine: {
      title: "Your access requests",
      lead: "Changes waiting for a project's approval, and their outcome over the last 30 days.",
      personal: "A personal project",
      requested: "Requested {{date}}",
      decided: "Decided {{date}}",
      status: {
        pending: "Pending",
        approved: "Approved",
        rejected: "Rejected",
        expired: "Expired",
        cancelled: "Cancelled",
      },
    },
    change: {
      visibility: "Visibility: {{visibility}}",
      details: "Name and description",
      memberAdd: "Add {{name}} as {{role}}",
      groupAdd: "Grant {{role}} to the group {{name}}",
      groupRole: "Change the group {{name}} to {{role}}",
      ruleGrant: "Grant {{role}} to the rule {{name}}",
      setting: "Change the setting {{key}}",
      transfer: "Transfer to {{name}}",
      entityJoin: "Join the entity {{name}}",
      entityMove: "Access as {{role}}, opened by moving the entity {{name}}",
      directorySync: "Access as {{role}}, opened by directory sync",
      shareOutside: "Allow members from outside the owning entity ({{scope}})",
      shareOutsideReset: "Restore the inherited outside-entity rule ({{scope}})",
      settingReset: "Reset the setting {{key}} ({{scope}})",
      wholeTenant: "whole organisation",
      unknown: "a removed item",
    },
    title: "Projects",
    hint: "Every project you govern, with its last 30 days.",
    lead: "Metadata and counts only. Governing a project never makes you a reader of it.",
    name: "Project",
    owner: "Owner",
    personal: "Personal project",
    personalOwner: "A person",
    members: "Members",
    documents: "Documents",
    // A non-breaking hyphen: a wrapping header breaks between the words, never inside "Flow-check".
    flowDenials: "Flow\u2011check denials",
    questions: "Questions",
    activeUsers: "Active users",
    cost: "Estimated cost",
    last30: "Last 30 days",
    frozen: "Frozen",
    empty: "No project to govern.",
    under: "Owned under",
    lastActivity: "Last activity",
    open: "Open {{name}}",
    back: "Projects",
    loadFailed: "This project could not be loaded.",
    personalDetail: "A personal project's members and settings stay with its owner.",
    metadataOnly:
      "You govern this project: you manage who holds which role and how it is configured. Its files, wiki and conversations stay closed to you unless you are a member or use audited break-glass access.",
    facts: "Overview",
    ownerEntity: "Owning entity",
    visibility: "Visibility",
    files: "Files",
    filesValue_one: "{{count}} file · {{size}}",
    filesValue_other: "{{count}} files · {{size}}",
    tasks: "Tasks",
    tasksValue: "{{total}} total · {{open}} open · {{overdue}} overdue",
    rolesValue: "{{members}} · {{groups}} · {{rules}}",
    rolesMembers_one: "{{n}} person",
    rolesMembers_other: "{{n}} people",
    rolesGroups_one: "{{n}} group",
    rolesGroups_other: "{{n}} groups",
    rolesRules_one: "{{n}} rule",
    rolesRules_other: "{{n}} rules",
    classifications: "Classifications",
    noClassifications: "No file yet",
    spend: "Spend (last 30 days)",
    created: "Created",
    breakGlassActive:
      "End your break-glass access to this project before managing it from the backoffice.",
    selfRefused:
      "Governance never gives you a role in a project, directly, through a group or by its visibility. Use break-glass access to read it.",
    governed: {
      title: "Projects you govern",
      lead: "You administer the entities that own these projects but are not a reader of them. Manage them from the backoffice.",
      entityUnknown: "An entity you administer",
      link: "Manage {{name}} in the backoffice",
    },
  },
  branding: {
    title: "Branding",
    hint: "Your organisation's name, colours and logo in ESI.",
    lead: "How ESI shows your organisation to its members: the name, an accent colour and a logo per theme. Browser tabs and the sign-in page stay ESI.",
    readOnly: "Only organisation admins can change branding.",
    readOnlySupport: "Read-only under support access: logo images are not loaded.",
    default: "Default",
    remove: "Remove",
    themeInline: { light: "light theme", dark: "dark theme" },
    theme: { light: "Light theme", dark: "Dark theme" },
    scope: {
      heading: "Whole organisation",
      purpose: "Set once for the organisation: every entity and member sees the same branding.",
    },
    name: {
      heading: "Name",
      label: "Display name",
      help: "Names your logo for screen readers and shows in the account menu.",
      helpFallback:
        "Names your logo for screen readers and shows in the account menu. Without one, “{{name}}” is used.",
      invalid: "Use 1 to 60 visible characters, without hidden or control characters.",
    },
    accent: {
      heading: "Accent colour",
      label: { light: "Light theme", dark: "Dark theme" },
      help: "Fills primary buttons, and colours links and focus when it reads clearly.",
      helpDarkInherited: "Uses the light theme's colour until you set one for the dark theme.",
      sameAsLight: "Same as light theme",
      picker: "Pick the accent colour of the {{theme}}",
      hex: "Accent colour of the {{theme}}, as a hex value",
      format: "Use a six-digit hex colour such as #3b5bdb.",
      invalid: "Use a six-digit hex colour such as #3b5bdb.",
      outcome: {
        applied: "Fills primary buttons and colours links and focus in the {{theme}}.",
        "fills-only":
          "Fills primary buttons only: it reads below 4.5:1 on the {{theme}} background, so links and focus keep the default colour.",
        default: "The default accent applies in the {{theme}}.",
      },
    },
    logo: {
      heading: "Logo",
      label: { light: "Light theme", dark: "Dark theme" },
      help: {
        light:
          "Replaces the ESI mark in the sidebar and phone bar. PNG or WebP, 32 to 2,048 pixels a side, up to 1 MB; shown 24 pixels tall.",
        dark: "Shown in the dark theme. Without one, the light logo shows in both themes.",
      },
      /** Account emails inline only a PNG light logo of 200 KiB or less (#302). */
      emails:
        "Emails don’t show a WebP logo or one over 200 KB: they show your organisation’s name instead.",
      on: { light: "On light", dark: "On dark" },
      none: "None: the ESI mark shows",
      darkNeedsLight: "Add a light logo first: the dark one shows only alongside it",
      darkUnused:
        "This dark logo is not shown: the dark theme uses a tenant logo only alongside a light one. Upload a light logo to use it.",
      usesLight: "Uses the light logo",
      upload: "Upload",
      replace: "Replace",
      uploading: "Uploading…",
      unnamed: "Organisation logo",
      refused: {
        type: "Use a PNG or WebP image. SVG, JPEG and GIF are not accepted.",
        size: "The file is larger than 1 MB. Export a smaller image.",
        signature:
          "This file is not the image type it claims to be. Export it again as PNG or WebP.",
        dimensions: "Each side must be between 32 and 2,048 pixels.",
        pixels: "The image holds more than 4 million pixels. Export a smaller one.",
        animated: "Animated images are not accepted. Use a still image.",
        malformed: "The image could not be read. Export it again as PNG or WebP.",
      },
    },
  },
  memoryAdmin: {
    title: "Memory",
    lead: "How long memories are kept, whether people and projects may use them, and how much they are used. Admins never see what anyone saved.",
    policies: "Policies",
    figures: "Usage",
    figuresLead:
      "People are counted in their default entity; project figures in the entity that owns the project. Entities with fewer than 5 people show no figures, so no one's use can be singled out.",
    entity: "Entity",
    metrics: {
      personal_memories: "Personal memories",
      users_with_memories: "People using memory",
      proposals_accepted: "Suggestions saved",
      proposals_dismissed: "Suggestions dismissed",
      project_memories: "Project memories",
      pending_suggestions: "Awaiting review",
    },
    tooFew: "Too few people to show figures",
    total: "Shown entities",
    activity: "Memory events",
    activityLead:
      "Policy changes, erasures, exports and expiries, latest first. Never the memories themselves.",
    activityEmpty: "No memory event yet.",
    activityTenantAdmins: "Memory events are shown to tenant admins.",
  },
  audit: {
    title: "Audit events",
    lead: "Who did what and when, for one kind of action. Reasons and details stay in the audit log.",
    when: "When",
    action: "Action",
    target: "Target",
    actor: "By",
    empty: "No event of this kind.",
    loadMore: "Load more",
    back: "Reports",
    actionLabel: {
      idp_created: "Identity provider added",
      idp_removed: "Identity provider removed",
      idp_cleanup_completed: "Identity provider clean-up finished",
      domain_claimed: "Domain claimed",
      domain_verified: "Domain verified",
      domain_released: "Domain released",
      feedback_sent: "Feedback sent to the ESI team",
      tenant_budget_pressure: "Monthly AI budget under pressure",
      memory_expired: "Unused memories expired",
      project_memories_expired: "Unused project memories expired",
      memory_exported: "Memories exported",
      memory_erased: "Memories erased",
      memory_project_suggested: "Project memory suggested",
      memory_project_approved: "Project memory approved",
      memory_project_rejected: "Project memory rejected",
      memory_project_edited: "Project memory edited",
      memory_project_deleted: "Project memory deleted",
      memory_project_withdrawn: "Project memory suggestion withdrawn",
      memory_project_expired: "Project memory suggestion expired",
      setting_changed: "Setting changed",
      setting_reset: "Setting reset",
      tenant_jit_changed: "Account creation at first sign-in changed",
      auth_jit_refused: "Sign-in refused: no account",
      branding_logo_uploaded: "Logo uploaded",
      branding_logo_removed: "Logo removed",
      project_break_glass: "Break-glass access",
      declassification_created: "Declassification",
      declassification_withdrawn: "Declassification withdrawn",
    },
    actorType: { user: "A person", operator: "An operator", system: "ESI" },
  },
  standalone: {
    ask: "Ask anything…",
    noKnowledge: "Answers here use no project knowledge",
    disabled:
      "Conversations outside a project are turned off for your organisation: this one can be read, not continued.",
  },
  conversationHeader: {
    noProject: "No project",
    viaClient: "via {{host}}",
    viaCli: "via the ESI command-line tool",
    viaClientHint: "Started from an external client: {{id}}",
    move: "Move to project",
    more: "Conversation actions",
    rename: "Rename",
    renameLabel: "Conversation title",
    task: "Task: {{title}}",
    taskUnavailable: "Unavailable task",
    taskUnavailableHint: "This conversation works on a task you cannot open now.",
  },
  conversationActions: {
    archive: "Archive",
    unarchive: "Unarchive",
    delete: "Delete",
  },
  conversationView: {
    notFoundTitle: "Conversation not found",
    notFound: "It doesn't exist, or you can't open it.",
    backHome: "Back home",
  },
  conversationList: {
    lead: "Every conversation you can open, in your projects and outside any project.",
    search: "Search conversations",
    filter: {
      label: "Show",
      all: "All",
      mine: "Mine",
      shared: "Shared with me",
      project: "Shared with the project",
      archived: "Archived",
    },
    projectShared: "Shared with the project",
    projectSharedAnd_one: "Shared with the project and {{count}} person",
    projectSharedAnd_other: "Shared with the project and {{count}} people",
    projectSharedBy: "Shared with the project by {{name}}",
    sharedWith_one: "Shared with {{count}} person",
    sharedWith_other: "Shared with {{count}} people",
    sharedBy: "Shared by {{name}}",
    archived: "Archived",
    levelSources: "{{level}} sources",
    loadMore: "Load more",
    actionsFor: "Actions for {{title}}",
    empty: {
      noneTitle: "No conversations yet",
      none: "Ask a question from New chat, or in a project.",
      inProject: "Ask a question about this project to start a conversation.",
      inProjectReadOnly: "No conversation was started in this project.",
      shared: "Nobody has shared a conversation with you yet.",
      projectTitle: "Nothing shared with the project",
      project: "Conversations shared with the whole project appear here.",
      noMatchTitle: "No conversation matches",
      noMatch: "Search covers titles and your own questions.",
      archivedTitle: "Nothing archived",
      archived: "Archived conversations leave your lists and stay searchable.",
    },
  },
  search: {
    title: "Search conversations",
    label: "Search",
    placeholder: "Search titles and your questions…",
    recent: "Recent",
    results: "Conversations",
    none: "No conversation matches.",
    noRecent: "No conversations yet.",
    hint: "Search covers titles and your own questions, never answers.",
    count_one: "{{count}} conversation found",
    count_other: "{{count}} conversations found",
  },
  move: {
    title: "Move to project",
    explain:
      "The conversation joins the project for good. Here is what changes before you confirm.",
    project: "Project",
    pick: "Pick a project",
    noProject: "You have no project to move it into.",
    effects: "What changes",
    into: "It becomes a conversation of {{project}} and opens there.",
    noLapsing: "Everyone it is shared with can read the project: no share lapses.",
    lapsing_one: "{{count}} share lapses: {{names}} can't read this project.",
    lapsing_other: "{{count}} shares lapse: {{names}} can't read this project.",
    breakGlass: "The project's admins can reach it under audited emergency access.",
    attachments_one: "{{count}} attachment stays with the conversation.",
    attachments_other: "{{count}} attachments stay with the conversation.",
    attachmentsPrivate_one: "{{count}} of them stays private to you and the people you named.",
    attachmentsPrivate_other: "{{count}} of them stay private to you and the people you named.",
    attachmentsAllPrivate: "All of them stay private to you and the people you named.",
    sourcesOnly: "The project answers from its sources only: later answers follow that.",
    oneWay: "It can't be moved back out of the project.",
    residency:
      "This conversation was answered with models outside the EU, so it can't move into this EU-only project.",
    changed: "Something changed since this preview (a share, say). Review it again.",
    notStandalone: "Only a conversation outside any project can be moved.",
    turnInProgress: "An answer is still being written. Try again once it's done.",
    frozen: "This project is read-only and takes no conversation.",
    confirm: "Move to {{project}}",
    confirmShort: "Move",
    moving: "Moving…",
  },
  deleteConversation: {
    title: "Delete this conversation?",
    gone: "It is gone for everyone at once, and leaves your lists and search.",
    lapsing_one: "Its share with {{count}} person lapses.",
    lapsing_other: "Its shares with {{count}} people lapse.",
    derived: "Files and wiki drafts saved from it stay, with their own readers.",
    turnInProgress: "An answer is still being written. Try again once it's done.",
    confirm: "Delete",
    deleting: "Deleting…",
  },
  artefacts: {
    kind: {
      markdown: "Document",
      code: "Code",
      mermaid: "Diagram",
      svg: "Drawing",
      csv: "Table",
      file: "File",
    },
    file: { downloadToOpen: "Download the file to open it." },

    version: "Version {{version}}",
    open: "Open",
    openNamed: "Open {{title}}",
    shown: "Open beside the conversation",
    writing: "Writing…",
    writingNamed: "Writing {{title}}…",
    untitled: "Artefact",
    failed: "Could not be written",
    unavailable: "Not available to you",
    panel: {
      label: "Artefact: {{title}}",
      back: "Knowledge",
      backLabel: "Back to the knowledge panel",
      close: "Close artefact",
      download: "Download",
      downloadNamed: "Download version {{version}} of {{title}}",
      loading: "Loading the artefact…",
      notFound: "This artefact isn't available to you, or no longer exists.",
      failed: "The artefact could not be loaded.",
      retry: "Try again",
      resize: "Resize the artefact panel",
    },
    versions: {
      label: "Versions",
      position: "Version {{version}} of {{count}}",
      previous: "Previous version ({{version}})",
      next: "Next version ({{version}})",
      latest: "Latest",
      latestNamed: "Show the latest version ({{version}})",
      newer: "A newer version is available.",
      showNewer: "Show version {{version}}",
    },
    code: {
      copy: "Copy",
      copied: "Copied",
      copyLabel: "Copy the code",
      plain: "Plain text",
      large: "Highlighting is off for large files.",
    },
    table: {
      caption: "{{title}}",
      partial: "Showing {{shown}} of {{total}} rows; download for all.",
      empty: "This table has no rows.",
    },
    diagram: {
      drawing: "Drawing the diagram…",
      failed: "This diagram could not be drawn. Its source is shown instead.",
      source: "Source",
      diagram: "Diagram",
      view: "View",
    },
    section: {
      title: "Artefacts",
      updated: "Updated {{date}}",
      empty: "No artefacts in this conversation yet.",
    },
  },
  projectOverview: {
    loading: "Loading",
    loadError: "Could not load this section.",
    open: "Open",
    by: "by {{name}}",
    pages_one: "{{count}} page",
    pages_other: "{{count}} pages",
    ask: "Ask about this project",
    recent: {
      title: "Recent conversations",
      empty: "No conversations yet.",
    },
    needsYou: {
      title: "Needs you",
      count_one: "{{count}} item",
      count_other: "{{count}} items",
      empty: "Nothing needs you right now.",
      more_one: "{{count}} more",
      more_other: "{{count}} more",
      showReview: "Show the review queue",
      showTasks: "Show your tasks",
      showMemories: "Show the memories to review",
      showAccessRequests: "Show the access requests",
      countAtLeast_one: "at least {{count}} item",
      countAtLeast_other: "at least {{count}} items",
      moreAtLeast_one: "{{count}} more and others",
      moreAtLeast_other: "{{count}} more and others",
      review: "Review {{title}}",
      revise: "Revise {{title}}",
      sentBack: "Changes requested",
      item: {
        source_changed: "Check {{title}}: a source changed",
        contradiction: "Check {{title}}: a contradiction",
        redeclassify: "Review the declassification of {{title}}",
      },
      itemUntitled: {
        source_changed: "Check a page: a source changed",
        contradiction: "Check a page: a contradiction",
        redeclassify: "Review a declassification",
      },
      hiddenTask: "A task you cannot read",
      due: "Due {{date}}",
      memories_one: "{{count}} memory to review",
      memories_other: "{{count}} memories to review",
      accessRequests_one: "{{count}} access request to decide",
      accessRequests_other: "{{count}} access requests to decide",
    },
    whatChanged: {
      title: "What changed",
      filter: "Show",
      all: "All",
      readable: "Only what you can read is listed.",
      empty: "Nothing has changed yet.",
      emptyFilter: "Nothing of this kind has changed lately.",
      published: "Published",
      publishedMore_one: "{{title}} and {{count}} more page",
      publishedMore_other: "{{title}} and {{count}} more pages",
      sourceAdded: "Source added",
      shared: "Shared with the project",
    },
    wiki: {
      pages: "Pages",
      waiting: "Waiting for review",
      lastPublished: "Last published {{when}}",
      neverPublished: "Nothing published yet",
      needsChecking: "Need checking",
      waitingCount_one: "{{count}} waiting",
      waitingCount_other: "{{count}} waiting",
      needsCheckingCount_one: "{{count}} needs checking",
      needsCheckingCount_other: "{{count}} need checking",
    },
    sources: {
      files: "Files",
      processing: "Processing",
      failed: "Failed",
      failedOwn: "Your failed uploads",
      restricted_one: "{{count}} more file is restricted to other readers.",
      restricted_other: "{{count}} more files are restricted to other readers.",
      filesCount_one: "{{count}} file",
      filesCount_other: "{{count}} files",
      processingCount_one: "{{count}} processing",
      processingCount_other: "{{count}} processing",
      failedCount_one: "{{count}} failed",
      failedCount_other: "{{count}} failed",
    },
    usage: {
      title: "Usage",
      questions: "Questions asked",
      active: "Active members",
      sourced: "Answers with sources",
      topCited: "Most cited",
      citations_one: "{{count}} citation",
      citations_other: "{{count}} citations",
      questionsCount_one: "{{count}} question",
      questionsCount_other: "{{count}} questions",
      activeCount_one: "{{count}} active member",
      activeCount_other: "{{count}} active members",
      updated: "Last 30 days · updated {{when}}",
      notComputed: "Last 30 days · figures appear within the hour",
      notComputedShort: "Not computed yet",
    },
    people: {
      title: "People",
      unnamed: "Unnamed",
      owner: "Owner",
      managers_one: "Manager",
      managers_other: "Managers",
      in: "In the project",
      members_one: "{{count}} member",
      members_other: "{{count}} members",
      groups_one: "{{count}} group",
      groups_other: "{{count}} groups",
      yourRole: "Your role: {{role}}",
    },
    start: {
      title: "This project has no content yet",
      lead: "Add sources, write a first wiki page or invite people, then ask a first question above.",
      leadReader: "Nothing has been added yet. You can still ask a first question above.",
      leadFrozen: "Nothing has been added yet, and the project is read-only.",
      addSources: "Add sources",
      newPage: "New page",
      invite: "Invite people",
    },
  },
  projectHome: {
    tabsLabel: "Project",
    tabs: {
      home: "Overview",
      conversations: "Conversations",
      settings: "Settings",
      memory: "Memory",
    },
    newConversation: "New conversation",
    askShort: "Ask about this project…",
    askSend: "Ask",
  },
};

/** The shape every catalogue must have: `en`'s keys, with string leaves. */
type Shape<T> = { [K in keyof T]: T[K] extends string ? string : Shape<T[K]> };
export type Catalogue = Shape<typeof en>;
